/**
 * S5.5-F — die Seite „Rechnungsdetail" (`/invoicing/:id`).
 *
 * Eine Kundenrechnung (oder Korrektur) als Ganzes: Kopf, Dokument, Hauptbuch und
 * Zahlung, Bezüge, Verlauf — in der Reihenfolge des „Detail Page Pattern"
 * (`pattern-library.md`): Kopf als Statusstreifen, darunter Hauptspalte
 * (Dokument, Hauptbuch) und Seitenspalte (Bezüge), der Verlauf als letzter
 * Abschnitt. Sie liest und schreibt genau diese Routen (S5.5, Backend; die
 * Zahl „drei" stand hier bis BA3b und war seit #7 ueberholt):
 *
 *   GET  /api/invoicing/admin/invoices/<id>                         → das Detail
 *   POST /api/invoicing/admin/invoices/<id>/dokument-link?art=…     → signierter Link (120 s)
 *   POST /api/invoicing/admin/invoices/<id>/neuversand  {an}        → Neuversand
 *   POST /api/invoicing/admin/invoices/<id>/erneut-senden
 *        {nachricht?, betreff?, an?}                                → erneut senden
 *   POST /api/invoicing/admin/invoices/<id>/igl-freigabe {…}        → IgL-Handfreigabe (#7)
 *   GET  /api/invoicing/admin/invoices/<id>/export-proof            → Ausfuhrnachweise (BA7)
 *   POST /api/invoicing/admin/invoices/<id>/export-proof  (multipart) → Nachweis hochladen (BA7)
 *   POST /api/invoicing/admin/invoices/<id>/export-proof/<p>/link   → 120-s-Link (BA7)
 *   POST /api/invoicing/admin/invoices/<id>/export-proof/<p>/entscheidung
 *        {entscheidung, grund?}                                     → freigeben/ablehnen (BA3b)
 *
 * 🔴 **Zwei Versandwege, zwei Knöpfe, zwei Bedeutungen** (Owner-Auftrag
 *    2026-10-16). „Send again …" steht im Warnstreifen einer **abgelehnten** Mail
 *    (`undeliverable`) und repariert die Adresse — das ist `NeuversandMaske`.
 *    „Send invoice again …" steht im Seitenkopf einer **zugestellten** Rechnung
 *    (`mailed`) und schickt dieselbe Mail noch einmal, an dieselbe Adresse,
 *    optional mit einer Nachricht — das ist `ErneutSendenMaske`. Der Owner hat
 *    den zweiten Weg im Testlauf 2 vermisst („Rechnung erneut an Kunden - geht
 *    noch nicht"); die zwei dürfen sich nicht vermischen, sonst verliert der
 *    `undeliverable`-Riegel seine Bedeutung.
 *
 *    🔴 **Drei Felder, jedes optional, jedes mit einer sichtbaren Vorgabe**
 *    (Owner-Entscheid 2026-10-16): Empfänger (vorbelegt mit der Bestelladresse),
 *    Betreff (Platzhalter = der Betreff, der ohne Eingabe hinausgeht) und
 *    Nachricht. Leer heisst überall „die Vorgabe gilt" — das ist der Normalfall,
 *    kein Fehler. Die Vorgaben und die zwei Obergrenzen kommen als
 *    `mail.betreff_vorgabe`, `mail.betreff_max_zeichen` und
 *    `mail.nachricht_max_zeichen` vom Server; die Anzeige führt **keine** zweite
 *    Zahl und **keinen** zweiten Text (Regel 5: eine Quelle).
 *
 *    🔴 **Der Standard-Text hängt an der SPRACHE der Rechnung.** Steht er für sie
 *    noch aus (`mail.standard_nachricht_bereit === false`), sagt die
 *    Maske das vor dem Absenden und sperrt den Versand — **auch mit
 *    geschriebenem Text**: die Einleitung ginge sonst mit dem Platzhalter
 *    hinaus. Fail-closed, wie das Backend.
 *
 * 🔴 **Keine Aktion ändert eine AUSGESTELLTE Rechnung.** Der Neuversand schickt
 *    die vorhandene Rechnungsmail über `mailing.versende` (G13) erneut — die
 *    Seite erfindet keinen Kundentext, sie wählt nur die Adresse. Die
 *    **IgL-Handfreigabe** (#7) stößt die Ausstellung eines **gesperrten**
 *    Auftrags (`intent`) an und schreibt vorher ein unlöschbares Ereignis;
 *    eine bereits ausgestellte Rechnung berührt sie nie. Blieb die Ausstellung
 *    nach der Freigabe offen (`nicht_versucht`/`fehler`), bietet die Seite
 *    „Ausstellung erneut versuchen" — derselbe Weg, **kein** zweites Ereignis.
 *    „Korrektur anlegen" (S5.6) gibt es noch nicht und wird nicht angeboten.
 *
 * 🔴 **Die PDF-Vorschau braucht einen Wurzelpfad, sonst sieht sie keinen 404.**
 *    Das Backend liefert seit dem 2026-11-03 den WURZELPFAD `/dokument/<token>`
 *    (vorher eine absolute URL über `FILES_PUBLIC_BASE_URL`).
 *    `PdfInlineFrame` probt aber nur Wurzelpfade — eine absolute URL mountet
 *    ungeprüft, und ein abgelaufener Link bliebe ein stummer leerer Rahmen.
 *    `relativerPfad` macht aus einer URL GLEICHEN Ursprungs deshalb den Pfad; er
 *    bleibt stehen, weil er beide Formen annimmt.
 *    Die Seite probt denselben Pfad selbst (`usePdfInlineProbe`) und mountet den
 *    Rahmen ERST danach: bei 404 „Link abgelaufen oder ungültig — Vorschau neu
 *    laden", bei jedem anderen Fehler „Vorschau nicht verfügbar" — so bietet der
 *    Rahmen sein „Open file" (Token-URL im neuen Tab) nur noch an, wenn sich der
 *    Link zwischen den zwei Proben ändert. Zwei HEAD je Link, nacheinander —
 *    `PdfInlineFrame` meldet seinen Zustand nicht nach außen, und ein geteiltes
 *    Bauteil wird hier nicht umgebaut. Das Limit (30/min je Rechnung) trägt das.
 *
 * 🔴 **Ein Link FREMDEN Ursprungs wird weder eingebettet noch geöffnet** (Gate
 *    S5.5-F, P4): `PdfInlineFrame` probt ihn nicht, und sein „Download PDF"
 *    navigierte die ganze Seite (`download` gilt nur gleichen Ursprungs) — der
 *    Token stünde in Adressleiste und Verlauf. Statt dessen der Hinweis
 *    „Vorschau nur über die Hauptadresse verfügbar" (lokal: Flask 5001 hinter
 *    CRA 3000 — dort gibt es die Vorschau nur über den Flask-Port).
 *
 * 🔴 **Ein Link lebt 120 s, der Rahmen länger.** Ein geladener Rahmen merkt
 *    den Ablauf nicht — aber sein „Download PDF" und jedes Neuladen gingen ins
 *    Leere. Nach `gueltig_sekunden` steht deshalb ein Hinweis über dem Rahmen.
 *
 * 🔴 **Mobil (≤ 600 px) kein Rahmen** (Register `PdfInlineFrame`: „Don't use for
 *    MOBILE"): ein Knopf holt einen `anhang`-Link, probt ihn per HEAD und öffnet
 *    ihn nur bei 200 UND `attachment` (`pruefeAnhang`); sonst steht eine Meldung
 *    auf der Seite. Erst auf Druck — ein Link, der beim Laden geholt würde, wäre
 *    beim Tippen vielleicht schon tot.
 *
 * 🔴 **Das Ergebnis des Neuversands lebt in der Seite, nicht in der Maske** (Gate
 *    S5.5-F, P1). Hat der Server den Versuch erreicht (`zustand`) oder ist der
 *    Ausgang unbekannt (keine Antwort, 5xx ohne `zustand`), schließt die Maske,
 *    das Detail lädt neu, und der Satz bleibt stehen — auch wenn der neue Status
 *    die Maske gar nicht mehr anbietet und auch wenn das Neuladen scheitert.
 *    „sind neu geladen" steht erst da, wenn das Laden fertig ist. Der Riegel
 *    (`neuversand_modus`) kommt mit dem Detail: bei `off` gibt es keinen Knopf.
 *
 * 🔴 **Zustände (Regel 22 §3):** erstes Laden = Skeleton, nie Spinner; Neuladen
 *    lässt den Stand stehen (`LinearProgress`); 404 = „Rechnung nicht gefunden"
 *    mit Weg zurück; Fehler = Alert mit „Erneut versuchen". Fehlende Werte stehen
 *    als „—", nie als 0. Die Zahlungszuordnung ist „nicht anwendbar" — der Satz
 *    der API steht da, keine Zahl.
 *
 * 🔴 **Kontrast:** jede `Pill` steht auf `elevation={0}` (Kopf, Abschnitte) —
 *    auf elevation 1 fiele `info` im Dunkelmodus unter 4,5:1 (`beschriftungen.js`,
 *    `STATUS_TON`). Gepinnt in `__tests__/InvoiceDetail.test.jsx`.
 */
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import PageContainer from '../../components/common/PageContainer'
import CopyButton from '../../components/common/CopyButton'
import ViewHeader from '../../components/ViewHeader'
import CollapsibleSection from '../../components/common/CollapsibleSection'
import FileDropzone from '../../components/common/FileDropzone'
import Fachbegriff from '../../components/common/Fachbegriff'
import PdfInlineFrame, {
  PDF_ERROR,
  PDF_MISSING,
  PDF_PROBING,
  PROBE_TIMEOUT_MS,
  isSameOriginPath,
  usePdfInlineProbe,
} from '../../components/common/PdfInlineFrame'
import { ActivityTimeline, EVENT_TYPES } from '../../components/common/ActivityTimeline'
import Pill from '../../components/common/Pill'
import RefreshButton from '../../components/common/RefreshButton'
import { api } from '../../api/client'
import { BUTTON_RADIUS, FS, PANEL_RADIUS, PANEL_SX, SP } from '../../theme/tokens'
import KorrekturDialog, { ZULAESSIGE_ARTEN } from './KorrekturDialog'
import { formatBerlinDate, formatBerlinDateTime } from '../../utils/time'
import {
  ANSCHRIFT_FEHLER_TEXT,
  ANSCHRIFT_UNBEKANNT,
  ART_LABEL,
  AUSSTELLEN_FEHLER_TEXT,
  AUSSTELLEN_UEBERSPRUNGEN_TEXT,
  BB_LABEL,
  KANAL_LABEL,
  RECHTSTRAEGER_LABEL,
  STATUS_LABEL,
  STATUS_TON,
  STEUERFALL_LABEL,
  STEUERFALL_TOOLTIP,
  vorgangLabel,
} from './beschriftungen'
import DisabledReason from '../../components/common/DisabledReason';
import useFokusNachLaden from '../../components/common/hooks/useFokusNachLaden'

// ── Beschriftungen (nur diese Seite) ───────────────────────────────────────
// Gleichstand mit der Quelle in `__tests__/InvoiceDetail.test.jsx`.

const STRICH = '—'

/**
 * Antwort des Neuversands — der `zustand` des Mailers (`mailing.py`,
 * „Die Ergebniszustände") plus `fehler` der Route (502, `admin_detail.py`).
 */
/** 🔴 Die Stati, aus denen „erneut senden" arbeitet — die Frontend-Haelfte von
 * `mailing.ERNEUT_SENDBARE_STATI` (Grill B1, 2026-10-16). Beide Seiten tragen
 * dieselbe Menge: `paid` ist **zugestellt** (`lagen.py`) und der haeufigste
 * Anlass fuer eine zweite Rechnungsmail. */
export const ERNEUT_SENDBARE_STATI = ['mailed', 'paid']

// ── F11 (2026-11-04) — Rechnungsanschrift und „Ausstellen jetzt" ───────────
//
// 🔴 **Die drei Ereignisse tragen keinen Zustand** (`anschrift.py`,
// `EREIGNIS_TYPEN_OHNE_ZUSTAND`). Der IgL-Pfad unten (`iglSperre`,
// `iglWiederholung`) liest „das jüngste Ereignis des eigenen Belegs" — nach
// einer Anschriften-Überschreibung wäre das nicht mehr die Sperre, und der
// Knopf „Release IgL …" bliebe weg, obwohl das Backend `igl_freigabe_moeglich`
// weiterhin `true` liefert (es filtert dieselbe Menge, `admin_detail`).
// Dieselbe Liste steht deshalb hier — als **Kopie der Semantik**, nicht als
// zweite Wahrheit über die Namen: die Namen kommen aus dem Vertrag (B6).
const NICHT_ZUSTANDS_TYPEN = [
  'anschrift_ueberschrieben', 'anschrift_dokument', 'neu_ausstellen_angefordert',
]

/** Der Deckel der Kundenmitteilung — dieselbe Zahl wie `anschrift.MAX_DOKUMENT_BYTES`. */
export const ANSCHRIFT_MAX_BYTES = 2 * 1024 * 1024

/** Die erlaubten Endungen (Plan B1b.2); die **Endung** entscheidet den MIME. */
export const ANSCHRIFT_ENDUNGEN = ['.eml', '.pdf', '.png', '.jpg', '.jpeg']

/**
 * Der Sofortbefund der gewählten Datei — was der Browser schon weiß, bevor
 * etwas hinausgeht (Hausform `dateiBefund` oben, eigene Grenzen).
 *
 * 🔴 **Eigene Funktion, nicht `dateiBefund`**: jene Grenze ist 20 MB und der
 * Ausfuhr-Nachweis; hier sind es 2 MB und vier Endungen. Zwei Flächen, zwei
 * Grenzen — eine geteilte Zahl wäre an einer der beiden falsch.
 */
export function anschriftDateiBefund(datei) {
  if (!datei) return ANSCHRIFT_FEHLER_TEXT.dokument_fehlt
  const groesse = Number(datei.size)
  if (!isFinite(groesse) || groesse <= 0) return ANSCHRIFT_FEHLER_TEXT.dokument_fehlt
  if (groesse > ANSCHRIFT_MAX_BYTES) return ANSCHRIFT_FEHLER_TEXT.dokument_zu_gross
  const name = String(datei.name || '').toLowerCase()
  const endung = name.includes('.') ? name.slice(name.lastIndexOf('.')) : ''
  if (!ANSCHRIFT_ENDUNGEN.includes(endung)) return ANSCHRIFT_FEHLER_TEXT.dokument_typ_nicht_erlaubt
  return null
}

/**
 * Die B2B-Registrierung aus dem jüngsten Ereignis, das eine traegt
 * (`sperre`/`issued`, Plan A.4). Sie steht im Dialog als **Hinweis**, nicht als
 * Vorbelegung: die Firmenregel des Servers erlaubt nur den registrierten Namen,
 * und ein stilles Übernehmen einer abweichenden Registrierungsanschrift wäre
 * eine Änderung, die niemand angeklickt hat.
 */
function registrierungAus(verlauf) {
  for (let i = verlauf.length - 1; i >= 0; i -= 1) {
    const r = verlauf[i]?.nutzlast?.registrierung
    if (r && (r.company_name || r.street || r.postal_code || r.city)) return r
  }
  return null
}

export const NEUVERSAND_ZUSTAND_LABEL = {
  versandt: 'Mailed',
  undeliverable: 'Rejected — the address is undeliverable',
  vorlaeufig_gescheitert: 'Provisionally failed',
  ohne_empfaenger: 'No recipient',
  unterdrueckt: 'Suppressed',
  unterdrueckt_nullbetrag: 'Suppressed — zero amount',
  nicht_im_status: 'Not in status Undeliverable — no resend',
  unbekannt: 'Outcome unknown',
  konfigurationsfehler: 'Configuration error in mail delivery',
  pdf_abweichung: 'PDF differs from the recorded hash — not sent',
  laeuft_schon: 'Another delivery of this invoice is currently running',
  bereits_versandt: 'Already mailed — no second delivery',
  // Seit N10-T2 werden Korrekturen gemailt; nur andere Belegarten haben keinen Mailweg.
  kein_rechnungsdokument: 'No invoice or correction document — there is no mail delivery for this',
  // N10-T2 (R15 B-W2): eine Korrektur geht nur mit freigegebenem Korrekturtext hinaus.
  gesperrt: 'Blocked — the correction text is not released yet, nothing was sent',
  // 2026-10-16 („Rechnung erneut senden"): die zwei neuen Ausgänge des Mailers.
  // Sie stehen hier, weil diese Karte die GESCHLOSSENE Menge der Mailer-Ausgänge
  // abbildet — eine Probe liest sie aus `mailing.py` gegen.
  standard_nachricht_fehlt: 'The standard message is not released yet for this language — nothing was sent',
  nachricht_zu_lang: 'The message is too long',
  // 2026-10-16 (A2): der Betreff ist ein einzeiliger Kopf. Beide Ausgänge stehen
  // hier, weil diese Karte die GESCHLOSSENE Menge der Mailer-Ausgänge abbildet.
  betreff_zu_lang: 'The subject is too long',
  betreff_ungueltig: 'The subject must be a single line — line breaks are not allowed',
  fehler: 'Error during delivery',
}

/** `invoice_refund_decision.art` — CHECK aus `335_invoicing_refund_decision.sql` (Plan S4.1). */
export const REFUND_ART_LABEL = {
  kein_geld: 'No money refunded',
  ohne_rechnung: 'Without Forge invoice',
  vor_ausstellung: 'Before issuance',
  ausstehend: 'Waiting for issuance',
  korrektur_positionen: 'Correction by items',
  korrektur_betrag: 'Correction by amount',
  steuerkorrektur: 'Tax correction',
  zahlungsstoerung: 'Payment disruption',
  korrektur_aus: 'Correction switched off',
  mensch: 'Needs a human',
}

/** `invoice_refund_decision.zustand` — CHECK aus Migration 335. */
export const REFUND_ZUSTAND_LABEL = {
  offen: 'Open',
  ausstehend: 'Pending',
  erledigt: 'Resolved',
  ueberholt: 'Superseded',
}
const REFUND_ZUSTAND_TON = { offen: 'warn', ausstehend: 'info', erledigt: 'ok', ueberholt: 'plain' }

// ── BA7 — der Ausfuhrnachweis in Forge ─────────────────────────────────────

/**
 * Die Arten aus `export_proof.ARTEN` — die geschlossene Menge. Ein unbekannter
 * Wert faellt auf „Unknown", nie auf einen geratenen Satz.
 *
 * 🔴 **Das Zustandswort ist das Verb der Handlung** (R16 C1 zu BA3b,
 * Orchestrator-Entscheid 2026-10-27): der Knopf heisst „Approve", also heisst
 * der Zustand „Approved" — nicht „Checked". „checked" bleibt allein dem Haken
 * des Hochladenden („Invoice number checked"); auf der Vier-Augen-Flaeche
 * bezeichnete das Wort sonst zwei Personen. Die Order-View (BA8) traegt
 * dieselben Woerter in ihrer `en.json`; die Jest-Probe „C1 P2a" haelt beide
 * Seiten gleich.
 */
export const AUSFUHR_ART_LABEL = {
  erwartet: 'Expected',
  eingegangen: 'Received',
  abgelaufen: 'Expired',
  geprueft: 'Approved',
  abgelehnt: 'Rejected',
}

/**
 * Das dritte Segment des Abschnittskopfs: liegt ein Nachweis vor?
 *
 * 🔴 **Nach einer Ablehnung nicht „Proof on file"** (R16 C2 zu BA3b,
 * Orchestrator-Entscheid 2026-10-27). `nachweis_vorhanden` ist bei `abgelehnt`
 * IMMER wahr — die Ablehnungszeile selbst steht in `NACHWEIS_ARTEN` des Servers
 * —, „Proof on file" las sich aber wie „erfuellt". Die Regel haengt deshalb an
 * der ART, nicht an `nachweis_vorhanden`. „approved" heisst dabei: der
 * AKTUELLE Zustand ist freigegeben — dieselbe Lesart wie die Geldsperre ab BA5
 * (#3893; `_selbstausfuhr_gesperrt`). Die Order-View traegt
 * dieselben Segmente (`ausfuhr.nachweis_*`); „C2 P2b" haelt sie gleich.
 */
export const AUSFUHR_NACHWEIS_LABEL = {
  ja: 'Proof on file',
  nein: 'Proof missing',
  abgelehnt: 'No approved proof',
}
const AUSFUHR_ART_TON = {
  erwartet: 'warn',
  eingegangen: 'info',
  abgelaufen: 'err',
  geprueft: 'ok',
  abgelehnt: 'err',
}

/**
 * Die **offenen** Arten — der Vorgang laeuft noch. `geprueft`/`abgelehnt` sind
 * entschieden (BA3), `abgelaufen` ist zu; das Alterswort im Kopf sagt „open"
 * nur hier (R15-W-1). Bewusst **nicht** `NACHWEIS_ARTEN` des Servers: das ist
 * „ein Nachweis liegt vor", eine andere Frage.
 */
const OFFENE_ARTEN = ['erwartet', 'eingegangen']

/**
 * Die Groessengrenze eines Nachweises — **eine** Zahl fuer Server und Anzeige.
 *
 * 🔴 Der Server entscheidet (`export_proof_upload.MAX_BYTES`, 20 MiB); diese
 * Konstante ist die Anzeigehaelfte derselben Grenze und **kein** zweiter Wert
 * (R16 W3). Die Backend-Probe
 * `test_export_proof_upload.py::test_die_anzeigegrenze_ist_die_servergrenze`
 * liest sie aus DIESER Datei und haelt sie gegen `MAX_BYTES` — wer eine der
 * beiden Stellen aendert, macht sie rot, statt dass die Anzeige still eine
 * alte Zahl behauptet.
 */
export const AUSFUHR_MAX_BYTES = 20 * 1024 * 1024

/**
 * Die Saetze der Transport- und Dateifehler. 🔴 **Der Zuordnungssatz kommt vom
 * Server** (`export_proof_upload.pruefe_zuordnung`); diese Karte traegt nur,
 * was der Server als Code schickt, und erfindet keinen zweiten Wortlaut fuer
 * einen Zustand, den er selbst beschreibt.
 */
export const AUSFUHR_FEHLER_TEXT = {
  kein_ausfuhrvorgang: 'This invoice has no open export case.',
  datei_zu_gross: 'The file is too large.',
  rumpf_zu_gross: 'The file is too large.',
  typ_nicht_erlaubt: 'Only PDF, JPEG, PNG or HEIC.',
  mime_passt_nicht: 'Only PDF, JPEG, PNG or HEIC.',
  // 🔴 **Zwei Codes, zwei Saetze** (R16 B1, Nachbesserung 3): vorher trugen
  // beide „Please choose a file." — der Nutzer las also „Bitte waehlen Sie eine
  // Datei." direkt ueber „leer.pdf · 0 B", obwohl er sehr wohl eine gewaehlt
  // hatte. Der Server schickt `datei_leer`, wenn `pruefe_datei` eine leere
  // Datei sieht (`export_proof_upload.FEHLER_LEER`); das Mapping unten loest
  // den Code ueber diese Karte auf, also zeigt die Server-Antwort denselben
  // neuen Satz wie die Sofortpruefung.
  datei_leer: 'The file is empty.',
  datei_fehlt: 'Please choose a file.',
  // 🔴 **Client-only, kein Servercode** (R16 W-3, Nachbesserung 4): der Satz
  // fuer den Doppelwurf stand als Literal im JSX und hing damit an keiner
  // Karte — der einzige Dateifehler, den keine Vollstaendigkeitspruefung
  // erfasste. Wortlaut byte-gleich uebernommen.
  mehrere_dateien: 'Drop one file at a time.',
  forbidden: 'Your account is not allowed to upload proofs.',
  // 🔴 Rueckfall fuer den 422 OHNE `fehler[]` (R15-Befund W8): der Zuordnungs-
  // fehler traegt seine Saetze je Feld; fehlt die Liste, stand hier vorher der
  // rohe Code „zuordnung" im Alert.
  zuordnung: 'Check the marked points on the scan.',
  // 🔴 Rueckfall der Dateipruefung (R15 Q1): `export_proof_routes.py` antwortet
  // mit `datei_fehler`, wenn `pruefe_datei` einen Fehler liefert, den
  // `_DATEI_FEHLER` nicht kennt. Gespeichert ist dann NICHTS — der Satz sagt
  // das, statt mit „may or may not" einen Ausgang offen zu lassen, der feststeht.
  datei_fehler: 'The file could not be accepted — nothing was stored.',
}

/**
 * Ist die gewaehlte Datei ueberhaupt hochladbar? → `null` oder ein Satz.
 *
 * 🔴 **Dieselbe Regel wie im Kundenkonto** (`ausfuhrregeln.dateiPruefen`):
 * keine Datei, leer, zu gross. Der Typ bleibt Sache des `accept`-Attributs und
 * des Servers (Magic Bytes) — eine dritte Typentscheidung im Client waere eine
 * zweite Wahrheit. Die Grenze kommt aus `AUSFUHR_MAX_BYTES`, nicht aus einer
 * zweiten Zahl.
 */
export function dateiBefund(datei) {
  if (!datei) return AUSFUHR_FEHLER_TEXT.datei_fehlt
  const groesse = Number(datei.size)
  // 🔴 **Leer ist nicht „keine Datei"** (R16 B1, Nachbesserung 3): eine
  // gewaehlte Datei mit 0 Byte bekam vorher den Satz „Please choose a file." —
  // eine Aufforderung, obwohl die zweite Zeile daneben `leer.pdf · 0 B` nannte.
  // Sie bekommt den eigenen Satz. `datei_fehlt` bleibt der Fall OHNE Datei.
  // Eine nicht lesbare Groesse (kaputtes `File`-Objekt) ist kein Leerzustand
  // und behaelt deshalb den alten Satz; erreichbar ist der Zweig nicht
  // (`File.size` ist immer eine Zahl), er haelt ihn fail-closed.
  if (!isFinite(groesse)) return AUSFUHR_FEHLER_TEXT.datei_fehlt
  if (groesse <= 0) return AUSFUHR_FEHLER_TEXT.datei_leer
  if (groesse > AUSFUHR_MAX_BYTES) return AUSFUHR_FEHLER_TEXT.datei_zu_gross
  return null
}

/**
 * Eine Groesse lesbar machen: `'0 B'`, `'1 KB'`, `'1.5 KB'`, `'20 MB'`.
 *
 * 🔴 **Englisch, mit Punkt** — diese Flaeche ist die Forge-Oberflaeche und
 * traegt englische Texte (`AUSFUHR_FEHLER_TEXT`). Die Kundenkonto-Fassung
 * (`ausfuhrregeln.formatiereGroesse`) schreibt das Komma; zwei Sprachen, zwei
 * Schreibweisen, **eine** Rechnung je Flaeche. Ohne `toLocaleString`: das folgt
 * der Browser-Locale, und dieselbe Zahl saehe auf zwei Rechnern verschieden aus.
 */
export function formatiereGroesse(bytes) {
  const zahl = Number(bytes)
  if (!isFinite(zahl) || zahl <= 0) return '0 B'
  if (zahl < 1024) return Math.round(zahl) + ' B'
  const einheiten = ['KB', 'MB', 'GB']
  let wert = zahl / 1024
  let stufe = 0
  while (wert >= 1024 && stufe < einheiten.length - 1) {
    wert = wert / 1024
    stufe += 1
  }
  const gerundet = Math.floor(wert * 10) / 10
  return String(gerundet) + ' ' + einheiten[stufe]
}

/**
 * 🔴 **Kein roher Servercode im Alert** (R15-Befund W7). `fehlertext` gibt
 * `response.data.error` unveraendert zurueck — auf dieser Flaeche stand dann
 * „not found" oder „boom". Hier steht, was der Leser wissen muss: ob der
 * Server geantwortet hat, und mit welchem Status. `fehlertext` selbst bleibt
 * unveraendert, es traegt andere Flaechen dieser Seite.
 */
const ausfuhrFehlertext = (e) => {
  const status = e?.response?.status
  return status
    ? `The server answered with status ${status}.`
    : 'The server did not answer.'
}

// ── BA3b — Freigeben/Ablehnen eines eingegangenen Nachweises ───────────────

/**
 * Die Grenze des Ablehnungsgrundes — die Anzeigehaelfte von `MAX_GRUND`
 * (`export_proof_pruefung_routes.py`). Der Server kuerzt still auf diese Zahl;
 * die Eingabe nimmt deshalb keine laengere an. Die Jest-Probe „BA3b T7" liest
 * `MAX_GRUND` aus der Python-Quelle und haelt diese Konstante dagegen — keine
 * zweite Wahrheit.
 */
export const ENTSCHEIDUNG_GRUND_MAX = 2000

/**
 * Die Herkunft einer Nachweiszeile (`export_proof.QUELLEN`). Die Maske nennt
 * sie, damit der Pruefer sieht, WER die drei Haken gesetzt hat — die
 * Vier-Augen-Seite (Gesamtplan §5.1). Ein Kunde, der selbst hochlaedt (BA9),
 * hat nichts geprueft; ein Mitarbeiter schon. Die Probe „BA3b T11b" haelt die
 * Schluessel gegen die Quelle.
 */
export const NACHWEIS_QUELLE_LABEL = {
  erkennung: 'automatic detection',
  upload: 'customer upload',
  shopify: 'Shopify',
  manuell: 'staff upload',
}

/**
 * Die Woerter der zwei Wege. 🔴 **Kein Feld fuer „laeuft"** (K-F1,
 * `pattern-library.md` §Loading state): beim Senden bleibt die Beschriftung
 * „Approve"/„Reject", den Lauf zeigt der Knopf mit `loading` und `aria-busy`.
 *
 * 🔴 **Ablehnung: der Kunde erfaehrt nichts automatisch** (Owner-Entscheid
 * „Ablehnungsmail manuell"). BA9 sperrt den Kunden-Upload nach `abgelehnt`;
 * ein Ersatznachweis kommt nur ueber den Adminweg. Der Satz verspricht deshalb
 * weder eine Mail noch einen Weg, den der Kunde selbst gehen koennte.
 */
const ENTSCHEIDUNG_TEXT = {
  geprueft: {
    titel: 'Approve export proof',
    knopf: 'Approve',
    satz: 'Approving this proof records that the stamped invoice proves the export. The decision is '
      + 'logged under your account and cannot be changed afterwards. Nothing is refunded '
      + 'and no email is sent.',
    ergebnis: 'The export proof has been approved. Nothing was refunded.',
  },
  abgelehnt: {
    titel: 'Reject export proof',
    knopf: 'Reject',
    satz: 'Rejecting this proof records that it does not prove the export. The decision is logged under '
      + 'your account and cannot be changed afterwards. Nothing is refunded and no email is '
      + 'sent — if the customer needs to know, contact them yourself. A replacement proof '
      + 'can only be uploaded by staff, with “Upload proof”.',
    ergebnis: 'The export proof has been rejected. Nothing was refunded and the customer was not notified.',
  },
}

/**
 * 🔴 **Unbekannt heisst unbekannt** (BA7 R16 W2). Die Route schreibt Zeile und
 * Ereignis in einer Transaktion; ein 5xx oder ein Abbruch kann NACH dem Commit
 * kommen. Der Satz behauptet deshalb nichts ueber den Ausgang.
 */
const ENTSCHEIDUNG_UNBEKANNT_SATZ = 'The decision may or may not have been saved — the list is being reloaded.'

/**
 * Welche Zeile ist entscheidbar? Die mit der **groessten `id`** ueber ALLE
 * Zeilen der Rechnung — und nur, wenn sie `eingegangen` ist. Sonst `null`.
 *
 * Das ist die Regel von `entscheide` (`export_proof_pruefung.py`: `FOR UPDATE`
 * auf die Zeile, `art == eingegangen`, dann `ORDER BY id DESC LIMIT 1`). Die
 * `id` gilt, nicht die Position: `nachweis_liste` liefert `ORDER BY p.id`, und
 * eine Anzeige, die „die letzte Zeile" naehme, haenge an dieser Reihenfolge.
 * Die Proben „BA3b T8"/„T8b" verankern beide Haelften in der Python-Quelle;
 * die eigentliche Schranke bleibt der Server (409 `nicht_juengste`).
 */
function entscheidbareId(nachweise) {
  let juengste = null
  for (const zeile of nachweise || []) {
    if (Number.isFinite(zeile?.id) && (juengste === null || zeile.id > juengste.id)) juengste = zeile
  }
  return juengste && juengste.art === 'eingegangen' ? juengste.id : null
}

/**
 * Die definierten Antworten der Route, je Code ein Satz. 🔴 **Genau die Codes
 * der Quelle** (`export_proof_pruefung_routes.py` + `export_proof_pruefung.py`)
 * — die Probe „BA3b T6" liest sie dort und verlangt Gleichheit. Jede dieser
 * Antworten kommt VOR dem Schreiben (`_halt` rollt zurueck), der Ausgang steht
 * also fest.
 *
 * 🔴 **`nicht_juengste` sagt „this attempt changed nothing"**, nicht „Nothing
 * was saved" (Grill W1): ein frueherer Versuch kann gespeichert sein — genau
 * das ist die Lage, wenn derselbe Bediener nach einem unbekannten Ausgang oder
 * ein zweiter Bediener entschieden hat.
 */
export const ENTSCHEIDUNG_FEHLER_TEXT = {
  forbidden: 'Your account is not allowed to decide on export proofs. Nothing was saved.',
  kein_schreibkonto: 'Your account is not allowed to decide on export proofs. Nothing was saved.',
  rumpf_fehlt: 'The request was incomplete. Nothing was saved.',
  unbekannte_entscheidung: 'Unknown decision. Nothing was saved.',
  nicht_gefunden: 'This proof does not belong to this invoice. Nothing was saved — the list is being reloaded.',
  nicht_eingegangen: 'This entry is not a received proof and cannot be decided. Nothing was saved — the list is being reloaded.',
  nicht_juengste: 'This proof has already been decided, or a newer proof has arrived — this attempt changed nothing. The list is being reloaded.',
}

/** Die Codes, die einen BEWEGTEN Stand melden: Liste und Detail laden neu, die Maske schliesst ab. */
const ENTSCHEIDUNG_NEU_LADEN = ['nicht_gefunden', 'nicht_eingegangen', 'nicht_juengste']

/**
 * 401 — das globale Tor bzw. `require_auth` antworten VOR jedem Schreibzugriff
 * (Grill W2). Der Ausgang steht fest; ohne eigenen Zweig fiele er auf „may or
 * may not" und die Seite laedt unnoetig neu. `api/client.js` hat den
 * Token-Refresh dann schon versucht.
 */
export const ENTSCHEIDUNG_SITZUNG_SATZ = 'Your session has expired — please sign in again. Nothing was saved.'

/** 429 — der Limiter (30/min je Rechnung) steht VOR `require_auth` und vor dem Schreiben. */
export const ENTSCHEIDUNG_GRENZE_SATZ = 'Too many decisions for this invoice — please wait a minute. Nothing was saved.'

/**
 * Deutet eine gescheiterte Entscheidung: `{ satz, neuLaden, beendet, unbekannt }`.
 * `beendet` = die Maske bietet keinen zweiten Versuch mehr, nur noch „Close".
 *
 * Reihenfolge: 401 und 429 zuerst (ihr Rumpf traegt keinen Code der Route —
 * `unauthorized` bzw. eine HTML-Seite des Limiters), dann die Karte, dann der
 * unbekannte Zweig. Ein 4xx mit einem Code, den die Route nicht kennt, ist
 * ebenfalls unbekannt: woher er kommt, weiss dieser Client nicht.
 */
function deuteEntscheidungsFehler(e) {
  const status = e?.response?.status
  if (status === 401) return { satz: ENTSCHEIDUNG_SITZUNG_SATZ, neuLaden: false, beendet: false, unbekannt: false }
  if (status === 429) return { satz: ENTSCHEIDUNG_GRENZE_SATZ, neuLaden: false, beendet: false, unbekannt: false }
  const code = e?.response?.data?.error
  const satz = status && status < 500 ? ENTSCHEIDUNG_FEHLER_TEXT[code] : null
  if (satz) {
    const bewegt = ENTSCHEIDUNG_NEU_LADEN.includes(code)
    return { satz, neuLaden: bewegt, beendet: bewegt, unbekannt: false }
  }
  return { satz: ENTSCHEIDUNG_UNBEKANNT_SATZ, neuLaden: true, beendet: true, unbekannt: true }
}

// ── BA12 — die Ausfuhr-Hinweismail von Hand ────────────────────────────────

/**
 * Je Code ein Satz fuer den Mitarbeiter (englisch, Forge-Text — KEIN Kundentext).
 *
 * 🔴 **Genau die Codes des Servers.** Die erste Gruppe ist
 * `ausfuhr_hinweis.KNOPF_GRUENDE` (sie sperren den Knopf und kommen als
 * `hinweis.knopf_grund` bzw. als `grund` eines 409 `gesperrt`), die zweite die
 * Ausgaenge der Route (`zustand`) plus `an_ungueltig`. Die Probe
 * `backend/tests/invoicing/test_ausfuhr_hinweis_gruende.py` liest beide Seiten
 * und verlangt Gleichheit — ein neuer Code ohne Satz wird dort rot.
 *
 * Kein Schluessel fuer `versandt` (Erfolgsbanner), `gesperrt` (traegt seinen
 * `grund`) und `unbekannt` (`AUSFUHR_HINWEIS_UNBEKANNT_SATZ`). Keine
 * Apostrophe in den Saetzen: die Probe liest `'…'`-Literale.
 */
export const AUSFUHR_HINWEIS_GRUND_TEXT = {
  // Knopfgruende — warum der Knopf gesperrt ist
  modus_off: 'Sending export instructions is switched off.',
  text_nicht_freigegeben: 'The email or its example images are not approved or not uploaded yet.',
  kein_ausfuhrvorgang: 'This invoice has no export case.',
  nachweis_liegt_vor: 'A proof has been received — the customer needs no instructions anymore.',
  vorgang_entschieden: 'The export case has been decided — instructions are no longer needed.',
  storniert: 'The invoice has been fully corrected or cancelled.',
  ohne_bestellung: 'This invoice has no shop order, so the customer has no upload page.',
  kunde_geloescht: 'The customer link was deleted or suppressed (Art. 17 GDPR).',
  firmenkunde: 'Business customer — the self-export instructions are for private customers only.',
  betrag_hoechstens_50: 'The invoice total is 50 € or less — the export VAT refund rule (§ 6 (3a) UStG) does not apply.',
  // Ausgaenge der Route (409/502) und die Adresspruefung (400)
  laeuft_schon: 'Another send for this invoice is running or happened moments ago — nothing was sent. '
    + 'Check the order timeline before trying again.',
  braucht_bestaetigung: 'The send was not confirmed — nothing was sent.',
  adresse_noetig: 'There is no customer address on file — enter one. Nothing was sent.',
  an_ungueltig: 'Enter a valid email address. Nothing was sent.',
  baufehler: 'The email could not be built — nothing was sent. Please report this.',
  fehler: 'Sending failed — the mail server reported an error. The attempt is logged in the order timeline.',
}

/**
 * 🔴 **Unbekannt heisst unbekannt** (Review Focus 5): ein Netzfehler, ein 5xx
 * ohne bekannten Ausgang oder `unbekannt` kann NACH dem Senden kommen. Der Satz
 * behauptet nichts ueber den Ausgang, und die Seite zeigt keinen Erfolg.
 */
export const AUSFUHR_HINWEIS_UNBEKANNT_SATZ = 'The email may or may not have been sent — the order timeline is being reloaded. '
  + 'Check it before trying again.'

/**
 * R16 Q2 (28.10.): der GET gelang, trug aber keine Lage (`hinweis: null`, BA12
 * abgeschirmt, B1). „Unbekannt ist nicht kein" (Regel 22 §3, wie `useNachweise`)
 * — eine gedaempfte Zeile statt Schweigen. Scheitert der GET selbst, steht dort
 * schon sein Fehlersatz mit „Try again"; die Zeile kommt dann nicht dazu.
 */
const AUSFUHR_HINWEIS_STAND_UNBEKANNT = 'Export instructions status unavailable.'

/** 409 `gesperrt` mit einem Grund, den die Karte nicht kennt (`rechnung_unbekannt` …). */
const AUSFUHR_HINWEIS_GESPERRT_SATZ = 'Sending is not possible for this invoice right now. Nothing was sent.'

/**
 * R16-WV W-B (28.10.): die STATISCHE Grundzeile unter dem Stand bei einem
 * `knopf_grund`, den die Karte nicht kennt. Nicht der Dialogsatz oben: „right
 * now" waere bei einem endgueltigen Grund unwahr, und „Nothing was sent" gehoert
 * zu einem gescheiterten Klick, nicht zu einer Zustandszeile.
 */
export const AUSFUHR_HINWEIS_NICHT_MOEGLICH_SATZ = 'Sending is not possible for this invoice.'

/**
 * R16-WV W-A (28.10.): die endgueltigen Knopfgruende, bei denen die Anleitung
 * wirklich NICHT NOETIG ist — der Stand sagt dort „not needed". Bei den uebrigen
 * endgueltigen (`ohne_bestellung`, `kunde_geloescht`, `kein_ausfuhrvorgang`) fehlt
 * der Nachweis weiter, die Anleitung ist nur nicht MOEGLICH: „not available".
 * Ein Grund, der hier nicht steht (auch ein neuer, unbekannter), bekommt „not
 * available" — die Flaeche behauptet nie „not needed" fuer etwas, das sie nicht
 * kennt. Die Probe `test_ausfuhr_hinweis_gruende.py` haelt die Liste gegen
 * `KNOPF_GRUENDE_ENDGUELTIG` des Servers.
 */
export const AUSFUHR_HINWEIS_NICHT_NOETIG = ['firmenkunde', 'betrag_hoechstens_50', 'nachweis_liegt_vor', 'vorgang_entschieden', 'storniert']

/**
 * R16-WV2 W-D (28.10.): der naechste Schritt fuer den Mitarbeiter, wo die
 * Anleitung nicht moeglich ist, der Nachweis aber fehlt (Forge-Text, KEIN
 * Kundentext). Steht unter dem Grund, nur bei einem Grund aus dieser Karte.
 *
 * Bewusst NUR `ohne_bestellung` — die Rechnung hat einen offenen Vorgang, aber
 * keine Upload-Seite. NICHT `kunde_geloescht`: Art. 17 DSGVO, kein Aufruf zum
 * Kontakt. NICHT `kein_ausfuhrvorgang`: ohne Vorgang ist kein Nachweis faellig
 * (`export_proof.ist_kandidat`, die Upload-Wege antworten 409), und der Abschnitt
 * erscheint dann gar nicht (`daten.ausfuhr?.art`). Kein unbekannter Grund.
 */
export const AUSFUHR_HINWEIS_HANDLUNG = {
  ohne_bestellung: 'Contact the customer by hand and upload the proof here.',
}

/** Die Rechnungssprache als Wort (`ausfuhr_hinweis.sprache_von`: `de`, sonst `en`). */
const HINWEIS_SPRACHE = { de: 'German', en: 'English' }

/** Die zwei Wege eines Versands (`versand[].weg`). */
const HINWEIS_WEG = { auto: 'automatic', hand: 'manual' }

/**
 * R16 C2 (28.10.): die Ausgaenge, die einen BEWEGTEN Stand melden, an dem ein
 * zweiter Klick nichts aendert — der Dialog schliesst ab (nur „Close"), wie
 * `deuteEntscheidungsFehler`. `fehler` (SMTP), `adresse_noetig`, `an_ungueltig`,
 * `braucht_bestaetigung`, 401 und 429 bleiben scharf: dort ist ein neuer Versuch
 * sinnvoll.
 */
const HINWEIS_BEENDET = ['laeuft_schon', 'baufehler']

/**
 * Deutet einen gescheiterten Handversand: `{ satz, neuLaden, beendet, unbekannt }`.
 *
 * Reihenfolge wie `deuteEntscheidungsFehler`: 401/403/404/429 zuerst (ihr
 * Rumpf traegt keinen Ausgang der Route), dann 400 `an_ungueltig`, dann der
 * 409 des Schalters (`ausfuhr_hinweis_gesperrt`, ohne `zustand`), dann
 * `zustand` (+ `grund` bei `gesperrt`) aus der Karte. Alles andere —
 * Netzfehler, 5xx ohne bekannten Ausgang, `unbekannt` — ist unbekannt.
 *
 * `neuLaden`: der Stand hat sich womoeglich bewegt (ein anderer Versand, ein
 * neuer Nachweis, ein Fehler-Ereignis) — Liste und Detail laden neu.
 */
function deuteHinweisFehler(e) {
  const status = e?.response?.status
  const daten = e?.response?.data || {}
  if (status === 401) return { satz: 'Your session has expired — please sign in again. Nothing was sent.', neuLaden: false, beendet: false, unbekannt: false }
  if (status === 403) return { satz: 'Your account is not allowed to send emails. Nothing was sent.', neuLaden: false, beendet: false, unbekannt: false }
  if (status === 404) return { satz: 'This invoice does not exist. Nothing was sent.', neuLaden: false, beendet: false, unbekannt: false }
  if (status === 429) return { satz: 'Too many sends for this invoice — please wait a minute. Nothing was sent.', neuLaden: false, beendet: false, unbekannt: false }
  if (status === 400 && daten.error === 'an_ungueltig') {
    return { satz: AUSFUHR_HINWEIS_GRUND_TEXT.an_ungueltig, neuLaden: false, beendet: false, unbekannt: false }
  }
  if (status === 409 && daten.error === 'ausfuhr_hinweis_gesperrt') {
    return { satz: AUSFUHR_HINWEIS_GRUND_TEXT.modus_off, neuLaden: true, beendet: true, unbekannt: false }
  }
  if (status === 409 && daten.zustand === 'gesperrt') {
    return { satz: AUSFUHR_HINWEIS_GRUND_TEXT[daten.grund] || AUSFUHR_HINWEIS_GESPERRT_SATZ, neuLaden: true, beendet: true, unbekannt: false }
  }
  if ((status === 409 || status === 502) && daten.zustand !== 'unbekannt' && AUSFUHR_HINWEIS_GRUND_TEXT[daten.zustand]) {
    return {
      satz: AUSFUHR_HINWEIS_GRUND_TEXT[daten.zustand],
      neuLaden: true,
      beendet: HINWEIS_BEENDET.includes(daten.zustand),
      unbekannt: false,
    }
  }
  return { satz: AUSFUHR_HINWEIS_UNBEKANNT_SATZ, neuLaden: true, beendet: true, unbekannt: true }
}

/**
 * Der Stand der Hinweismail in einem Satz, aus `hinweis` (`HinweisLage.als_dict`):
 * `{ stand, knopf, warnung }`. `warnung` fuehrt zum Knopf — nur bei freiem Knopf
 * und nur, solange der Kunde nicht informiert ist.
 *
 * `knopf` (R16 W3, 28.10.): `frei`, `gesperrt` (ein VORLAEUFIGER Grund — Schalter
 * aus, Texte nicht frei: `disabled` + Satz, er wird wieder frei) oder `keiner`
 * (`knopf_endgueltig` des Servers — Firmenkunde, ≤ 50 €, Nachweis liegt vor,
 * entschieden …: kein dauerhaft toter Knopf, Hausform R16-W4 der
 * Nachweistabelle). Dann sagt der Stand „not needed" statt „not sent yet" (W4:
 * „yet" versprach einen Versand, der nie kommt) — oder „not available", wo die
 * Anleitung nicht unnoetig, sondern unmoeglich ist (R16-WV W-A,
 * `AUSFUHR_HINWEIS_NICHT_NOETIG`).
 *
 * 🔴 **Ohne Kundenadresse** (`adresse_noetig`, R16 C1) zaehlt ein Handversand
 * serverseitig NICHT als „informiert" (E8, Owner-Frage Q1 entschieden: so bleibt
 * es). Die Zeile sagt genau das, statt eine „customer address on file" zu
 * behaupten, die es nicht gibt.
 */
function hinweisStand(hinweis) {
  const versand = Array.isArray(hinweis?.versand) ? hinweis.versand : []
  const anKunde = versand.find((eintrag) => eintrag?.an_kunde)
  let knopf = 'frei'
  if (hinweis?.knopf_frei !== true) knopf = hinweis?.knopf_endgueltig === true ? 'keiner' : 'gesperrt'
  let stand = 'Export instructions not sent yet.'
  if (knopf === 'keiner') {
    stand = AUSFUHR_HINWEIS_NICHT_NOETIG.includes(hinweis?.knopf_grund)
      ? 'Export instructions not needed.'
      : 'Export instructions not available.'
  }
  if (hinweis?.kunde_informiert_am && anKunde) {
    stand = `Export instructions emailed to the customer on ${tag(anKunde.am)} (${HINWEIS_WEG[anKunde.weg] || 'unknown way'}).`
  } else if (versand.length > 0 && hinweis?.adresse_noetig) {
    stand = `Export instructions emailed on ${tag(versand[0].am)} to an address entered by hand `
      + '(no customer address on file) — not counted as informing the customer.'
  } else if (versand.length > 0) {
    stand = knopf === 'keiner'
      ? `Export instructions not sent to the customer address on file — last sent to another address on ${tag(versand[0].am)}.`
      : `Export instructions not yet sent to the customer address on file — last sent to another address on ${tag(versand[0].am)}.`
  }
  let warnung = null
  if (hinweis?.knopf_frei && !hinweis?.kunde_informiert_am) {
    if (hinweis.auto_zustand === 'fehler') {
      warnung = 'The automatic email failed — send it with “Email export instructions”.'
    } else if (hinweis.auto_zustand === 'ohne_ausgang') {
      warnung = 'The automatic email has no recorded outcome — it may or may not have gone out. '
        + 'Check the order timeline, then use “Email export instructions” if needed.'
    }
  }
  return { stand, knopf, warnung }
}

/**
 * Warum ein Kunde die Rechnung im Portal NICHT findet (`kundenbezug.grund`,
 * `admin_detail._kundenbezug`). Für den Support geschrieben.
 */
export const KUNDENBEZUG_TEXT = {
  gast: 'Guest order — there is no customer account in which the invoice could appear.',
  geloescht: 'Customer link deleted (Art. 17 GDPR). The invoice is retained but no longer appears in any customer account.',
  unterdrueckt: 'Customer link suppressed: the customer was already deleted at issuance, so no link was created.',
  altbeleg: 'No customer link — the correction refers to a Lexware invoice that is not in Forge and therefore appears in no customer account.',
}

// ── Formate ────────────────────────────────────────────────────────────────

const ZAHL = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Ein Betrag mit Währung; ohne Währung nur die Zahl. `null` bleibt „—", nie 0. */
const geld = (wert, waehrung) => {
  if (!Number.isFinite(wert)) return STRICH
  if (!waehrung) return ZAHL.format(wert)
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: waehrung }).format(wert)
  } catch (e) {
    return `${ZAHL.format(wert)} ${waehrung}`
  }
}
const zahl = (wert) => (Number.isFinite(wert) ? ZAHL.format(wert) : STRICH)
const menge = (wert) => (Number.isFinite(wert)
  ? new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(wert)
  : STRICH)
/** `vat_rate` ist PROZENT (19), nicht der Anteil (Migration 326). */
const prozent = (wert) => (Number.isFinite(wert)
  ? `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(wert)} %`
  : STRICH)
const tag = (iso) => (iso ? formatBerlinDate(iso, { dateOnly: true }) : STRICH)
const roh = (wert) => (wert == null || wert === '' ? STRICH : String(wert))
const fehlertext = (e) => e?.response?.data?.error || e?.message || 'Unknown error'

/** Der Name eines Dokuments für Titel und Links: Nummer, sonst die ID. */
const dokName = (nummer, id) => nummer || `without number (ID ${id})`

/**
 * Eine URL GLEICHEN Ursprungs wird zum Wurzelpfad — nur den probt
 * `PdfInlineFrame` (siehe Dateikopf). Eine fremde URL bleibt, wie sie ist.
 */
export function relativerPfad(url) {
  if (typeof url !== 'string' || url === '') return url
  try {
    const ziel = new URL(url, window.location.href)
    if (ziel.origin === window.location.origin) return `${ziel.pathname}${ziel.search}`
  } catch (e) {
    // Kein gültiger URL — unverändert weiterreichen, der Rahmen zeigt den Fehler.
  }
  return url
}

/**
 * Öffnet einen `anhang`-Link. Ein Objekt statt einer Funktion, damit der Test
 * den Aufruf beobachten kann (jsdom kennt keine Navigation).
 *
 * `location.assign`, nicht `window.open`: der Link kommt erst NACH einer Anfrage,
 * und ein Fenster, das nach einem `await` aufgeht, blockiert iOS Safari. Ein
 * Anhang (`Content-Disposition: attachment`) lädt herunter, ohne die Seite zu
 * verlassen.
 */
export const dokumentOeffner = {
  oeffne(url) {
    window.location.assign(url)
  },
}

/** Der Ursprung einer URL für einen Hinweis — nie der Pfad (er trägt den Token). */
const ursprungVon = (url) => {
  try {
    return new URL(url).origin
  } catch (e) {
    return null
  }
}

const NICHT_GEPRUEFT = 'The download link could not be checked — it is therefore not opened. Please try again.'

/**
 * Gate S5.5-F (P4): bevor die Seite auf einen Token-Link navigiert, probt sie
 * ihn per HEAD. Geöffnet wird nur bei **200 und `attachment`** — sonst stünde
 * eine Fehlerseite (404 abgelaufen, 409 Hash) oder das PDF selbst mit dem Token
 * in Adressleiste und Verlauf. Ein Link fremden Ursprungs wird gar nicht
 * angefasst. Liefert `{ pfad }` (öffnen) oder die Meldung `{ ton, text }`.
 */
export async function pruefeAnhang(url) {
  const pfad = relativerPfad(url)
  if (!isSameOriginPath(pfad)) {
    const ursprung = ursprungVon(pfad)
    return {
      ton: 'info',
      text: `Download available only via the main address: the link points to a different origin${ursprung ? ` (${ursprung})` : ''} and is not opened.`,
    }
  }
  if (typeof fetch !== 'function') return { ton: 'error', text: NICHT_GEPRUEFT }
  const steuerung = typeof AbortController === 'function' ? new AbortController() : null
  const uhr = setTimeout(() => { if (steuerung) steuerung.abort() }, PROBE_TIMEOUT_MS)
  let antwort
  try {
    antwort = await fetch(pfad, { method: 'HEAD', credentials: 'same-origin', signal: steuerung ? steuerung.signal : undefined })
  } catch (e) {
    return { ton: 'error', text: NICHT_GEPRUEFT }
  } finally {
    clearTimeout(uhr)
  }
  const status = antwort?.status
  if (status === 404 || status === 410) {
    return { ton: 'warning', text: 'Link expired or invalid — please tap “Download PDF” again.' }
  }
  if (status === 409) {
    return { ton: 'error', text: 'The PDF differs from the recorded hash and is not delivered.' }
  }
  if (status !== 200) return { ton: 'error', text: `The download is currently not possible (HTTP ${status ?? '?'}).` }
  const kopf = antwort.headers && typeof antwort.headers.get === 'function' ? antwort.headers.get('content-disposition') : null
  if (!String(kopf || '').trim().toLowerCase().startsWith('attachment')) {
    return { ton: 'error', text: 'The link delivers no download — it is therefore not opened.' }
  }
  return { pfad }
}

// ── Lesen ──────────────────────────────────────────────────────────────────

const detailPfad = (id) => `/api/invoicing/admin/invoices/${id}`

/**
 * Das Detail. `fehlt` = 404 (oder eine ID, die keine Zahl ist — dann geht gar
 * keine Anfrage hinaus). Ein Fehler löscht die Daten; ein Neuladen lässt sie
 * stehen, bis die Antwort da ist.
 *
 * `runde` zählt die Ladeaufträge, `fertig` sagt, welcher zuletzt ANKAM und ob
 * mit Daten — daran hängt „sind neu geladen" nach dem Neuversand (Gate P1).
 */
function useDetail(id) {
  const gueltig = /^\d+$/.test(id || '')
  const [stand, setStand] = useState({
    daten: null, laedt: gueltig, fehler: null, fehlt: !gueltig, fertig: { runde: -1, ok: false },
  })
  const [runde, setRunde] = useState(0)
  const zaehler = useRef(0)

  useEffect(() => {
    if (!gueltig) {
      setStand({ daten: null, laedt: false, fehler: null, fehlt: true, fertig: { runde, ok: false } })
      return undefined
    }
    let abgemeldet = false
    setStand((alt) => ({ ...alt, laedt: true, fehler: null, fehlt: false }))
    api.get(detailPfad(id))
      .then(({ data }) => {
        if (!abgemeldet) setStand({ daten: data, laedt: false, fehler: null, fehlt: false, fertig: { runde, ok: true } })
      })
      .catch((e) => {
        if (abgemeldet) return
        const fertig = { runde, ok: false }
        if (e?.response?.status === 404) setStand({ daten: null, laedt: false, fehler: null, fehlt: true, fertig })
        else setStand({ daten: null, laedt: false, fehler: fehlertext(e), fehlt: false, fertig })
      })
    return () => { abgemeldet = true }
  }, [id, gueltig, runde])

  /** Startet einen Ladeauftrag und gibt seine Nummer zurück (für `fertig.runde`). */
  const neuLaden = useCallback(() => {
    zaehler.current += 1
    setRunde(zaehler.current)
    return zaehler.current
  }, [])
  return { ...stand, neuLaden }
}

/**
 * Ein signierter Dokument-Link (`inline` oder `anhang`). `hole` liefert die URL
 * oder `null` zurück; der Fehler steht dann im Stand, mit Statuscode.
 */
function useDokumentLink(invoiceId) {
  const [stand, setStand] = useState({ url: null, gueltigMs: null, laedt: false, fehler: null })
  const aktiv = useRef(true)
  useEffect(() => {
    aktiv.current = true
    return () => { aktiv.current = false }
  }, [])

  const hole = useCallback((art) => {
    setStand((alt) => ({ ...alt, laedt: true, fehler: null }))
    return api.post(`/api/invoicing/admin/invoices/${invoiceId}/dokument-link`, null, { params: { art } })
      .then(({ data }) => {
        if (!data?.url) throw new Error('The response carried no link.')
        const sekunden = Number(data.gueltig_sekunden)
        const neu = {
          url: data.url,
          gueltigMs: (Number.isFinite(sekunden) && sekunden > 0 ? sekunden : 120) * 1000,
          laedt: false,
          fehler: null,
        }
        if (aktiv.current) setStand(neu)
        return neu.url
      })
      .catch((e) => {
        if (aktiv.current) {
          setStand({ url: null, gueltigMs: null, laedt: false, fehler: { code: e?.response?.status ?? null, text: fehlertext(e) } })
        }
        return null
      })
  }, [invoiceId])

  return [stand, hole]
}

/**
 * BA7 — die eingegangenen Nachweise einer Rechnung.
 *
 * `aktiv` ist die Bedingung der Flaeche: nur ein Kandidat (`ausfuhr.art` gesetzt)
 * laedt die Liste. Ein Fehler leert die Liste nicht still — er steht als Satz
 * da, denn „unbekannt" ist nicht „kein Nachweis" (Regel 22 §3).
 */
function useNachweise(invoiceId, aktiv) {
  // BA12: `hinweis` ist die Lage der Hinweismail aus demselben GET
  // (`export_proof_routes._hinweis_oder_none`). `null` heisst unbekannt — der
  // Server schirmt einen BA12-Fehler ab (B1), dann gibt es keinen Knopf.
  const [stand, setStand] = useState({ nachweise: [], hinweis: null, laedt: Boolean(aktiv), fehler: null })
  const [runde, setRunde] = useState(0)

  useEffect(() => {
    if (!aktiv) {
      setStand({ nachweise: [], hinweis: null, laedt: false, fehler: null })
      return undefined
    }
    let abgemeldet = false
    setStand((alt) => ({ ...alt, laedt: true, fehler: null }))
    api.get(`/api/invoicing/admin/invoices/${invoiceId}/export-proof`)
      .then(({ data }) => {
        if (!abgemeldet) {
          setStand({ nachweise: data?.nachweise || [], hinweis: data?.hinweis ?? null, laedt: false, fehler: null })
        }
      })
      .catch((e) => {
        if (!abgemeldet) {
          setStand({ nachweise: [], hinweis: null, laedt: false, fehler: ausfuhrFehlertext(e) })
        }
      })
    return () => { abgemeldet = true }
  }, [invoiceId, aktiv, runde])

  return { ...stand, neuLaden: () => setRunde((r) => r + 1) }
}

// ── Bausteine dieser Seite ─────────────────────────────────────────────────

/** Ein Abschnitt: benannte Landmarke (`region`) mit Überschrift, auf elevation 0. */
function Bereich({ titel, children, sx, tooltip }) {
  const id = useId()
  return (
    <Paper component="section" aria-labelledby={id} elevation={0} sx={{ ...PANEL_SX, ...sx }}>
      <Typography id={id} component="h2" variant="h6" title={tooltip} sx={{ mb: SP.sm }}>{titel}</Typography>
      {children}
    </Paper>
  )
}

/** Eine Untergruppe in einem Abschnitt. */
function Gruppe({ titel, children, tooltip }) {
  return (
    <Box>
      <Typography component="h3" title={tooltip} sx={{ fontSize: FS.base, fontWeight: 600, mb: SP.chip }}>{titel}</Typography>
      {children}
    </Box>
  )
}

/** Eine Beschreibungsliste (`dl`), zwei Spalten auf dem Handy, sonst so viele wie passen. */
function Angaben({ children }) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(10rem, 1fr))' },
        gap: SP.card,
      }}
    >
      {children}
    </Box>
  )
}

function Angabe({ label, children, betont = false, tooltip, testId }) {
  return (
    <Box data-testid={testId} sx={{ minWidth: 0 }}>
      <Typography component="dt" sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
        {tooltip ? <Fachbegriff label={label} begriff={tooltip} /> : label}
      </Typography>
      <Typography
        component="dd"
        sx={{
          m: 0,
          fontSize: betont ? FS.lg : FS.base,
          fontWeight: betont ? 700 : 400,
          fontVariantNumeric: 'tabular-nums',
          overflowWrap: 'anywhere',
        }}
      >
        {children}
      </Typography>
    </Box>
  )
}

const Gedaempft = ({ children }) => (
  <Typography component="span" sx={{ color: 'text.secondary', fontSize: 'inherit' }}>{children}</Typography>
)

const Herkunft = ({ children }) => (
  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: SP.sm }}>{children}</Typography>
)

/** Ein Link auf ein anderes Detail (Original, Korrektur). */
const DetailLink = ({ id, nummer }) => (
  <Link component={RouterLink} to={`/invoicing/${id}`}>{dokName(nummer, id)}</Link>
)

// ── Kopf ───────────────────────────────────────────────────────────────────

function Kopf({ kopf, mail, zahlung }) {
  const w = kopf.waehrung
  // 🔴 H2 (Regel-16-Bericht Runde 1, 2026-10-13): einen Schreiber für
  // `invoice.status = 'paid'` gibt es **nicht** — gemessen: `state.py` erlaubt den
  // Übergang (`:37`), aber kein Pfad setzt ihn (die Status-Schreiber sind
  // `issued`/`issuing`/`mailed`/`undeliverable`/`corrected`), und `paid_at` hat
  // ebenfalls keinen Schreiber. Der Status-Pill zeigt „Paid" deshalb **nie**.
  // Der Zustand „bezahlt" ist trotzdem da — als beim Intent eingefrorenes
  // Zahlungsziel `payment_terms_key === 'paid'` (`shopify_mapping.py:700`:
  // „Vorkasse, bei Ausstellung bezahlt"). Der Chip leitet sich daraus ab und
  // heisst **Prepaid**: er beschreibt das Zahlungsziel, nicht einen
  // Statuswechsel, den es nicht gibt.
  const vorkasseBezahlt = zahlung?.payment_terms_key === 'paid'
  return (
    <Paper component="section" aria-label="Invoice header" elevation={0} sx={PANEL_SX}>
      <Angaben>
        <Angabe label="Status" testId="angabe-status">
          <Stack direction="row" spacing={SP.tight} alignItems="center" flexWrap="wrap" useFlexGap>
            {kopf.status ? (
              <Pill tone={STATUS_TON[kopf.status] || 'plain'} bordered nowrap data-testid="status-pill">
                {STATUS_LABEL[kopf.status] || kopf.status}
              </Pill>
            ) : STRICH}
            {vorkasseBezahlt && (
              <Pill tone="ok" bordered nowrap data-testid="prepaid-pill">Prepaid</Pill>
            )}
          </Stack>
        </Angabe>
        {/* 🔴 R16 B1 (2026-11-01): „Test invoice" ist eine Kategorie, kein Status.
            Im Status-Feld trug sie `tone="warn"` — denselben Ton wie `issuing`, und
            genau dieser Zustand ist am Go-live-Morgen der häufigste. Eigene Angabe,
            neutraler Ton. */}
        {kopf.testbeleg && (
          <Angabe label="Category" testId="angabe-kategorie">
            <Pill tone="plain" bordered nowrap data-testid="testbeleg-pill">Test invoice</Pill>
          </Angabe>
        )}
        <Angabe label="Invoice date">{tag(kopf.rechnungsdatum)}</Angabe>
        <Angabe label="Delivery date">{tag(kopf.lieferdatum)}</Angabe>
        <Angabe label="Type">{kopf.art ? (ART_LABEL[kopf.art] || kopf.art) : STRICH}</Angabe>
        <Angabe label="Channel">{kopf.kanal ? (KANAL_LABEL[kopf.kanal] || kopf.kanal) : STRICH}</Angabe>
        <Angabe label="Tax case"
                tooltip={STEUERFALL_TOOLTIP[kopf.steuerfall] || 'Steuerfall'}>{kopf.steuerfall ? (STEUERFALL_LABEL[kopf.steuerfall] || kopf.steuerfall) : STRICH}</Angabe>
        <Angabe label="Legal entity" tooltip="Rechtsträger">
          {kopf.rechtstraeger ? (RECHTSTRAEGER_LABEL[kopf.rechtstraeger] || kopf.rechtstraeger) : STRICH}
        </Angabe>
        <Angabe label="Order">{roh(kopf.bestellung)}</Angabe>
        <Angabe label="Internal ID">{roh(kopf.id)}</Angabe>
        {mail?.mail_recipient && <Angabe label="Mailed to">{mail.mail_recipient}</Angabe>}
        <Angabe label="Net">{geld(kopf.netto, w)}</Angabe>
        <Angabe label="Tax">{geld(kopf.steuer, w)}</Angabe>
        <Angabe label="Gross" betont>{geld(kopf.brutto, w)}</Angabe>
      </Angaben>
      <Herkunft>
        Amounts: document (Forge, frozen){w ? '' : ', without currency'}.
      </Herkunft>
    </Paper>
  )
}

// ── Dokument ───────────────────────────────────────────────────────────────

/** Ein Linkfehler, ehrlich nach Statuscode. 403 und 404 bieten keinen zweiten Versuch an. */
function LinkFehler({ fehler, onNochmal }) {
  if (fehler.code === 403) {
    return (
      <Alert severity="warning">
        No write permission for this account: the document link for preview and download is issued only
        for released accounts.
      </Alert>
    )
  }
  if (fehler.code === 404) {
    return <Alert severity="info">There is no retrievable document for this entry.</Alert>
  }
  const text = fehler.code === 503
    ? 'The document link cannot be signed right now (the signer is switched off).'
    : `The preview link could not be fetched: ${fehler.text}`
  return (
    <Alert
      severity="error"
      action={<Button color="inherit" size="small" onClick={onNochmal}>Try again</Button>}
    >
      {text}
    </Alert>
  )
}

const RAHMEN_SX = { width: '100%', height: '70vh', minHeight: 480 }

/**
 * Der Rahmen samt Probe. Die Seite probt ZUERST und mountet `PdfInlineFrame`
 * erst, wenn ihre Probe durch ist (Gate P4): bei 404 (abgelaufen, verfälscht,
 * fremde Art — das Backend unterscheidet sie absichtlich nicht, E10) und bei
 * jedem anderen Fehler ersetzt sie den Rahmen — dessen „Open file" führte sonst
 * ungeprüft auf die Token-URL. Fremder Ursprung: weder Rahmen noch Probe.
 */
function Rahmen({ url, titel, abgelaufen, laedt, onNeu }) {
  const pfad = relativerPfad(url)
  const fremd = !isSameOriginPath(pfad)
  const probe = usePdfInlineProbe(fremd ? null : pfad)
  const neuKnopf = (
    <Button color="inherit" size="small" onClick={onNeu} disabled={laedt}>Reload preview</Button>
  )
  if (fremd) {
    const ursprung = ursprungVon(pfad)
    return (
      <Alert severity="info">
        Preview available only via the main address: the document link points to a different
        origin{ursprung ? ` (${ursprung})` : ''}. The page therefore does not embed or open it.
      </Alert>
    )
  }
  if (probe === PDF_PROBING) {
    return (
      <Skeleton
        variant="rectangular"
        data-testid="vorschau-laden"
        aria-busy="true"
        sx={{ ...RAHMEN_SX, borderRadius: PANEL_RADIUS }}
      />
    )
  }
  if (probe === PDF_MISSING) {
    return <Alert severity="warning" action={neuKnopf}>Link expired or invalid — reload preview</Alert>
  }
  if (probe === PDF_ERROR) {
    return (
      <Alert severity="warning" action={neuKnopf}>
        Preview not available: the document link does not respond with the PDF. “Reload preview” fetches
        a new link.
      </Alert>
    )
  }
  return (
    <Stack spacing={SP.tight}>
      {abgelaufen && (
        <Alert severity="info" role="status" action={neuKnopf}>
          The preview link has expired. The display stays; “Download PDF” and a reload
          need a new link.
        </Alert>
      )}
      <PdfInlineFrame url={pfad} title={titel} frameSx={RAHMEN_SX} />
    </Stack>
  )
}

function DesktopVorschau({ invoiceId, titel }) {
  const [link, hole] = useDokumentLink(invoiceId)
  const [abgelaufen, setAbgelaufen] = useState(false)

  // EIN Link je Aufbau. Ohne den Merker holte `React.StrictMode` (index.js) im
  // Entwicklungsmodus zwei — zwei signierte Links, zwei Zählungen am Limit.
  const angefragt = useRef(false)
  useEffect(() => {
    if (angefragt.current) return
    angefragt.current = true
    hole('inline')
  }, [hole])

  // Nach der Gültigkeit ist der Link tot — der Rahmen merkt es nicht.
  useEffect(() => {
    setAbgelaufen(false)
    if (!link.url || !link.gueltigMs) return undefined
    const uhr = setTimeout(() => setAbgelaufen(true), link.gueltigMs)
    return () => clearTimeout(uhr)
  }, [link.url, link.gueltigMs])

  const neu = useCallback(() => { hole('inline') }, [hole])

  if (link.fehler) return <LinkFehler fehler={link.fehler} onNochmal={neu} />
  if (!link.url) {
    return (
      <Skeleton
        variant="rectangular"
        data-testid="vorschau-laden"
        aria-busy="true"
        sx={{ ...RAHMEN_SX, borderRadius: PANEL_RADIUS }}
      />
    )
  }
  // Gate S5.5-F R2: `key` erzwingt einen frischen Probenstand je Link — sonst trüge
  // der Rahmen beim Neuladen einen Takt lang den Stand des ALTEN Links und hinge den
  // neuen ungeprüft ein.
  return <Rahmen key={link.url} url={link.url} titel={titel} abgelaufen={abgelaufen} laedt={link.laedt} onNeu={neu} />
}

function MobilDokument({ invoiceId }) {
  const [link, hole] = useDokumentLink(invoiceId)
  const [pruefung, setPruefung] = useState({ laeuft: false, meldung: null })
  const aktiv = useRef(true)
  useEffect(() => {
    aktiv.current = true
    return () => { aktiv.current = false }
  }, [])

  const lade = useCallback(async () => {
    setPruefung({ laeuft: true, meldung: null })
    const url = await hole('anhang')
    if (!url) {
      if (aktiv.current) setPruefung({ laeuft: false, meldung: null })
      return
    }
    // Gate P4: erst die Probe, dann — nur bei 200 und Anhang — die Navigation.
    const befund = await pruefeAnhang(url)
    if (!aktiv.current) return
    setPruefung({ laeuft: false, meldung: befund.pfad ? null : befund })
    if (befund.pfad) dokumentOeffner.oeffne(befund.pfad)
  }, [hole])

  const beschaeftigt = link.laedt || pruefung.laeuft
  return (
    <Stack spacing={SP.tight} alignItems="flex-start">
      <Button
        variant="outlined"
        onClick={lade}
        disabled={beschaeftigt}
        startIcon={beschaeftigt ? <CircularProgress size={16} aria-hidden="true" /> : <FileDownloadOutlinedIcon />}
      >
        Download PDF
      </Button>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        On mobile without an embedded preview — the button fetches a link that is valid for two minutes,
        checks it and downloads the file.
      </Typography>
      {pruefung.meldung && <Alert severity={pruefung.meldung.ton}>{pruefung.meldung.text}</Alert>}
      {link.fehler && <LinkFehler fehler={link.fehler} onNochmal={lade} />}
    </Stack>
  )
}

function DokumentBereich({ kopf, dokument, schmal }) {
  if (!dokument) {
    return (
      <Bereich titel="Document">
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No document stored.
          {!kopf.nummer && ' The entry has no number — it is not issued.'}
        </Typography>
      </Bereich>
    )
  }
  const hashOk = dokument.hash_ok === true
  const kurzHash = dokument.pdf_sha256 ? `${String(dokument.pdf_sha256).slice(0, 12)}…` : STRICH
  return (
    <Bereich titel="Document">
      <Stack spacing={SP.card}>
        <Stack direction="row" alignItems="center" gap={SP.tight} flexWrap="wrap" useFlexGap>
          <Pill tone={hashOk ? 'ok' : 'err'} bordered nowrap data-testid="pruefsumme-pill">
            {hashOk ? 'Checksum matches' : 'Checksum differs'}
          </Pill>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            SHA-256 {kurzHash} · Layout {roh(dokument.layout_version)} · freshly recomputed
          </Typography>
        </Stack>

        {!hashOk && (
          <Alert severity="error">
            The stored bytes do not match the recorded hash. The PDF is not delivered —
            preview and download respond with 409.
          </Alert>
        )}
        {hashOk && (schmal
          ? <MobilDokument invoiceId={kopf.id} />
          : <DesktopVorschau invoiceId={kopf.id} titel={`PDF ${dokName(kopf.nummer, kopf.id)}`} />)}

        <CollapsibleSection title="Snapshot (read-only)" defaultExpanded={false}>
          <Box
            component="pre"
            data-testid="snapshot-json"
            sx={{
              m: 0,
              p: SP.sm,
              bgcolor: 'action.hover',
              borderRadius: PANEL_RADIUS,
              fontFamily: 'monospace',
              fontSize: FS.sm,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {JSON.stringify(dokument.snapshot ?? {}, null, 2)}
          </Box>
        </CollapsibleSection>
      </Stack>
    </Bereich>
  )
}

// ── Hauptbuch und Zahlung ──────────────────────────────────────────────────

const POSITION_SPALTEN = [
  { key: 'pos', titel: 'Pos.', wert: (p) => roh(p.position) },
  { key: 'text', titel: 'Description', wert: (p) => roh(p.beschreibung) },
  { key: 'menge', titel: 'Quantity', rechts: true, wert: (p) => menge(p.menge) },
  { key: 'netto', titel: 'Net', rechts: true, wert: (p) => zahl(p.netto) },
  { key: 'steuer', titel: 'Tax', rechts: true, wert: (p) => zahl(p.steuer) },
  { key: 'brutto', titel: 'Gross', rechts: true, wert: (p) => zahl(p.brutto) },
  { key: 'satz', titel: 'Rate', rechts: true, wert: (p) => prozent(p.vat_rate) },
  { key: 'vat', titel: 'VAT code', wert: (p) => roh(p.bb_vat_code) },
  { key: 'konto', titel: 'Revenue account', wert: (p) => roh(p.erloeskonto) },
]

function Positionen({ positionen, schmal }) {
  if (!positionen.length) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No items stored.</Typography>
  }
  if (schmal) {
    return (
      <Stack spacing={SP.tight} role="list" aria-label="Items">
        {positionen.map((p, i) => (
          <Box
            key={`${p.position}-${i}`}
            role="listitem"
            sx={{ border: 1, borderColor: 'divider', borderRadius: PANEL_RADIUS, p: SP.sm }}
          >
            {POSITION_SPALTEN.map((s) => (
              <Stack key={s.key} direction="row" justifyContent="space-between" gap={SP.sm}>
                <Typography component="span" sx={{ fontSize: FS.sm, color: 'text.secondary', flexShrink: 0 }}>
                  {s.titel}
                </Typography>
                <Typography component="span" sx={{ fontSize: FS.sm, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>
                  {s.wert(p)}
                </Typography>
              </Stack>
            ))}
          </Box>
        ))}
      </Stack>
    )
  }
  return (
    // Breite Tabelle zwischen 600 und 960 px: sie scrollt in sich, nicht die Seite.
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Items">
        <TableHead>
          <TableRow>
            {POSITION_SPALTEN.map((s) => (
              <TableCell key={s.key} align={s.rechts ? 'right' : 'left'}>{s.titel}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {positionen.map((p, i) => (
            <TableRow key={`${p.position}-${i}`}>
              {POSITION_SPALTEN.map((s) => (
                <TableCell key={s.key} align={s.rechts ? 'right' : 'left'} sx={{ fontSize: FS.sm }}>
                  {s.wert(p)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

function HauptbuchBereich({ hauptbuch, zahlung, waehrung, schmal }) {
  const hb = hauptbuch || {}
  const zl = zahlung || {}
  const positionen = hb.positionen || []
  return (
    <Bereich titel="General ledger and payment">
      <Stack spacing={SP.md}>
        <Box>
          <Angaben>
            <Angabe label="BB state">
              {hb.bb_state ? (BB_LABEL[hb.bb_state] || hb.bb_state) : <Gedaempft>no BB state yet</Gedaempft>}
            </Angabe>
            <Angabe label="BB receipt ID">
              {hb.bb_receipt_id_by_customer ? (
                <>
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>{hb.bb_receipt_id_by_customer}</Box>
                  {/* Kopierknopf für die BB-Beleg-ID. KEIN Deep-Link: das URL-Format
                      der BB-Oberfläche ist nicht gemessen. `statusVisible` behält die
                      sichtbare Bestätigungszeile des früheren `KopierKnopf`, und
                      `failText` den nächsten Schritt des Vorbilds: die ID steht
                      unmittelbar links daneben und ist von Hand markierbar. Der
                      gemeinsame Vorgabetext kann den Schritt nicht tragen — an
                      MarketLab steht der kopierte Text nirgends auf dem Schirm.
                      `reserveStatusLine` reserviert die Zeile: hier sitzt die
                      Region in der Zelle einer `display: grid`-`<dl>`, und ohne
                      die Reservierung wüchse die Zelle beim Erscheinen der
                      Bestätigung um eine zweite Zeile und spränge nach 2 s
                      zurück. Reserviert wird mit dem geschützten Leerzeichen der
                      leeren Region, NICHT mit dem `display: block` — das Vorbild
                      `KopierKnopf` rendert denselben leeren Block und sprang
                      deshalb genauso (Hausmuster `components/common/StatTile.jsx`). */}
                  <CopyButton
                    value={String(hb.bb_receipt_id_by_customer)}
                    label="Copy BB receipt ID"
                    title="To the clipboard"
                    statusVisible
                    reserveStatusLine
                    failText="Copy failed — select the ID."
                    sx={{ ml: SP.chip }}
                  />
                </>
              ) : <Gedaempft>none yet</Gedaempft>}
            </Angabe>
            <Angabe label="Posted on">{hb.bb_posted_at ? formatBerlinDateTime(hb.bb_posted_at) : STRICH}</Angabe>
          </Angaben>
          <Herkunft>
            BB state: last written by the receipt run. The BB receipt ID is there to copy — a link into the
            BB interface follows only once its address format has been measured.
          </Herkunft>
        </Box>

        <Gruppe titel="Items">
          <Positionen positionen={positionen} schmal={schmal} />
          {positionen.length > 0 && (
            <Herkunft>
              Amounts {waehrung ? `in ${waehrung}` : 'without currency'}, from the document (Forge, frozen).
              VAT code and revenue account per item.
            </Herkunft>
          )}
        </Gruppe>

        <Gruppe titel="Payment">
          <Angaben>
            <Angabe label="Payment method">{roh(zl.zahlungsart)}</Angabe>
            <Angabe label="Payment terms">{roh(zl.payment_terms_key)}</Angabe>
            {/* „Nicht anwendbar" (Querschnitt, G10): die Zuordnung führt BB — der Satz
                der API steht da, keine Zahl, die wie „geprüft, nichts gefunden" aussähe. */}
            <Angabe label="Assigned payments">{roh(zl.zugeordnete_zahlungen)}</Angabe>
          </Angaben>
          <Herkunft>Payment method frozen as at the order, payment terms from the snapshot.</Herkunft>
        </Gruppe>
      </Stack>
    </Bereich>
  )
}

// ── Bezüge ─────────────────────────────────────────────────────────────────

function kundenbezugText(kb, kopf) {
  if (kb?.vorhanden) return null
  if (kb?.grund && KUNDENBEZUG_TEXT[kb.grund]) return KUNDENBEZUG_TEXT[kb.grund]
  if (kb?.grund) return `No customer link (reason: ${kb.grund}).`
  if (!kopf.nummer) return 'Not yet issued — the customer link is created at issuance.'
  return 'No customer link, and no event explains it. For an issued invoice this is a '
    + 'deviation — it appears under the issuance errors in the warning bar.'
}

function BezuegeBereich({ daten, onKorrekturFertig }) {
  const { kopf, original, korrekturen = [], refund_entscheidungen: refunds = [], kundenbezug, altbeleg } = daten
  const [korrektur, setKorrektur] = useState(null)
  const fehlt = kundenbezugText(kundenbezug, kopf)
  return (
    <Bereich titel="References">
      <Stack spacing={SP.md}>
        <Gruppe titel="Corrections">
          {/* R16 B1: eine Korrektur auf einen Lexware-Altbeleg hat kein Forge-Original
              (`original` NULL) — der Bezug steht in `altbeleg`. Ohne diesen Block läse
              die Bedienerin „Keine Korrektur zu diesem Dokument." und hielte die
              Korrektur für bezuglos. */}
          {altbeleg && (
            <Typography variant="body2">
              Correction to Lexware invoice {roh(altbeleg.nummer)} dated {tag(altbeleg.rechnungsdatum)}
            </Typography>
          )}
          {!altbeleg && !original && korrekturen.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>No correction for this document.</Typography>
          )}
          {original && (
            <Typography variant="body2">
              Corrected <DetailLink id={original.id} nummer={original.nummer} />
              {original.status && <Gedaempft> ({STATUS_LABEL[original.status] || original.status})</Gedaempft>}
            </Typography>
          )}
          {korrekturen.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: SP.card }}>
              {korrekturen.map((k) => (
                <Typography component="li" variant="body2" key={k.id}>
                  Corrected by <DetailLink id={k.id} nummer={k.nummer} />
                  {k.status && <Gedaempft> ({STATUS_LABEL[k.status] || k.status})</Gedaempft>}
                </Typography>
              ))}
            </Box>
          )}
        </Gruppe>

        <Gruppe titel="Refund decisions">
          {refunds.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>No refund decision for the order.</Typography>
          ) : (
            <Stack component="ul" spacing={SP.tight} sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {refunds.map((r) => (
                <Box
                  component="li"
                  key={r.refund_id}
                  aria-label={`Refund ${r.refund_id}`}
                  sx={{ borderTop: 1, borderColor: 'divider', pt: SP.tight }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={SP.tight}>
                    <Typography sx={{ fontSize: FS.base, fontWeight: 600 }}>Refund {r.refund_id}</Typography>
                    <Pill tone={REFUND_ZUSTAND_TON[r.zustand] || 'plain'} bordered nowrap data-testid="refund-pill">
                      {REFUND_ZUSTAND_LABEL[r.zustand] || r.zustand || STRICH}
                    </Pill>
                  </Stack>
                  <Typography variant="body2">{REFUND_ART_LABEL[r.art] || roh(r.art)}</Typography>
                  <Typography sx={{ fontSize: FS.sm, color: 'text.secondary', overflowWrap: 'anywhere' }}>
                    <Box component="code" sx={{ fontFamily: 'monospace' }}>{roh(r.grund)}</Box>
                    {r.angelegt_am && ` · ${formatBerlinDateTime(r.angelegt_am)}`}
                  </Typography>
                  {/* S5.6a: „Korrektur anlegen" nur, wenn es zur Bestellung eine
                      Entscheidung im Zustand `offen` gibt UND ihre Art hineinführt.
                      🔴 E2 (Handweg ohne Forge-Original, 2026-10-12): UND nur mit
                      gespeichertem Original — dieselbe Bedingung wie der Server
                      (`handweg_lage`: `invoice_id is None` ⇒ 409
                      `original_unbekannt`). */}
                  {r.zustand === 'offen' && ZULAESSIGE_ARTEN.includes(r.art) && (
                    r.invoice_id != null ? (
                      <Button size="small" sx={{ mt: SP.chip }} onClick={() => setKorrektur(r)}>
                        Create correction
                      </Button>
                    ) : (
                      /* 🔴 Ohne Original bietet DIESE Seite keine Aktion an (die
                         Schliessmaske lebt in der Liste) — der Hinweis nennt den
                         Weg, statt den Bediener erst in eine 409 laufen zu lassen. */
                      <Typography sx={{ fontSize: FS.sm, color: 'text.secondary', mt: SP.sm }}>
                        No Forge invoice stored — close the decision in{' '}
                        <Link component={RouterLink} to="/invoicing?tab=refunds">
                          Refund decisions
                        </Link>.
                      </Typography>
                    )
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Gruppe>

        <Gruppe titel="Customer link">
          {fehlt ? (
            <Typography variant="body2">{fehlt}</Typography>
          ) : (
            <Typography variant="body2">
              Linked to the customer account — Shopify customer {roh(kundenbezug.shopify_customer_id)}.
            </Typography>
          )}
          {original && (
            <Herkunft>For a correction, the customer link of the original applies.</Herkunft>
          )}
        </Gruppe>
      </Stack>

      <KorrekturDialog
        offen={korrektur !== null}
        refundId={korrektur?.refund_id}
        onSchliessen={() => setKorrektur(null)}
        onErgebnis={onKorrekturFertig}
      />
    </Bereich>
  )
}

// ── Verlauf ────────────────────────────────────────────────────────────────

/** Die Nutzlast eines Ereignisses, wertfrei: Schlüssel und Wert, wie gespeichert. */
function Nutzlast({ wert }) {
  const leer = wert == null
    || (typeof wert === 'object' && !Array.isArray(wert) && Object.keys(wert).length === 0)
  if (leer) {
    return <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>no payload</Typography>
  }
  if (typeof wert !== 'object' || Array.isArray(wert)) {
    return <Box component="code" sx={{ fontFamily: 'monospace', fontSize: FS.xs }}>{JSON.stringify(wert)}</Box>
  }
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        mt: SP.chip,
        display: 'grid',
        gridTemplateColumns: 'max-content minmax(0, 1fr)',
        columnGap: SP.tight,
        fontFamily: 'monospace',
        fontSize: FS.xs,
      }}
    >
      {Object.entries(wert).map(([schluessel, w]) => (
        <Fragment key={schluessel}>
          <Box component="dt" sx={{ color: 'text.secondary' }}>{schluessel}</Box>
          <Box component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {w !== null && typeof w === 'object' ? JSON.stringify(w) : String(w)}
          </Box>
        </Fragment>
      ))}
    </Box>
  )
}

/**
 * Aufsteigend nach der Backend-Reihenfolge. `vorgang` kommt schon stabil sortiert
 * (`(zeit, quelle_ordnung, roh_id)`, D3) — die Anzeige darf diese Ordnung NICHT
 * über eine zweite Regel (etwa die Roh-`id`) wieder umwerfen.
 */
const nachRang = (a, b) => a.rang - b.rang

/**
 * Ereignis-Arten mit NEGATIVEM Ausgang → Warn-/Fehlerton (Regel-16-Warnung W1,
 * 2026-10-13).
 *
 * 🔴 **Die Liste ist ABGELEITET, nicht erfunden:** sie nennt die `event_type`-Werte,
 * die das Backend tatsächlich schreibt (`grep` über `backend/invoicing/` —
 * `mailing.py`, `admin_detail.py`, `bb_beleglauf.py`, `bb_buchung.py`,
 * `issuance.py`). Ein Typ, der hier nicht steht, behält den neutralen Ton — und
 * die **Beschriftung trägt den Ausgang ohnehin**, die Farbe ist nie der alleinige
 * Kanal (Regel 22 §2).
 *
 * Die Grenze zwischen den zwei Tönen ist der Ausgang, nicht die Zuständigkeit:
 *   * `error`   — die Handlung ist **gescheitert** (abgelehnt, Fehler, Abweichung,
 *                 ohne Empfänger, abgewiesen).
 *   * `warning` — die Handlung ist **gehalten** oder ihr Ausgang ist **unbekannt**
 *                 (Sperre, gesperrt, unklar, unbekannt, unbrauchbar, fehlt).
 */
const VORGANG_TON = {
  mail_abgelehnt: 'error',
  mail_konfigurationsfehler: 'error',
  mail_pdf_abweichung: 'error',
  mail_ohne_empfaenger: 'error',
  mail_vorlaeufig_gescheitert: 'error',
  mail_neuversand_fehler: 'error',
  // Der Normalfall hat ein eigenes Fehler-Ereignis (Grill W2) — derselbe Ton.
  mail_erneut_fehler: 'error',
  bb_upload_abgewiesen: 'error',
  sperre: 'warning',
  bb_buchung_gesperrt: 'warning',
  bb_upload_unbekannt: 'warning',
  bb_buchung_unklar: 'warning',
  bb_uebernahme_unklar: 'warning',
  bb_uebernahme_mehrere: 'warning',
  bb_nutzlast_unbrauchbar: 'warning',
  bb_korrektur_original_fehlt: 'warning',
  bb_korrektur_betrag_nicht_negativ: 'warning',
  // BA12: der Versand der Ausfuhr-Hinweismail ist gescheitert.
  ausfuhr_hinweis_fehler: 'error',
}

/** Die Palette je Ereignis-Art — die geteilte `ActivityTimeline` bleibt unberuehrt. */
function paletteFuer(art) {
  if (art === 'refund_decision') return EVENT_TYPES.PAYMENT
  if (VORGANG_TON[art] === 'error') return EVENT_TYPES.ERROR
  if (VORGANG_TON[art] === 'warning') return EVENT_TYPES.WARNING
  if (typeof art === 'string' && art.startsWith('mail')) return EVENT_TYPES.EMAIL_SENT
  if (art === 'corrected') return EVENT_TYPES.CHANGE
  return EVENT_TYPES.INVOICE
}

/** Der Zeilenton des negativen Ausgangs — `null` laesst die Zeile neutral. */
const severityFor = (e) => VORGANG_TON[e.art] || null

/**
 * R16 W2 (2026-10-13): der geoeffnete Beleg wird ueber EINEN ruhigen Kanal
 * kenntlich gemacht — eine dezente Zeilentoennung — statt ueber einen
 * „Current"-Chip an JEDER Zeile. Ein Beleg mit den ueblichen Ereignissen
 * (issued + mail + bb_*) trug sonst drei bis fuenf identische Chips an der
 * Schiene, und das Signal verwaesserte.
 */
const EIGENE_ZEILE_SX = {
  bgcolor: 'action.hover',
  borderRadius: `${BUTTON_RADIUS}px`,
  px: SP.chip,
  py: SP.chip,
}

/** Was ein Beleg korrigiert — als kleiner Chip (`beleg_bezug`, D2). */
function BezugChip({ bezug }) {
  if (!bezug) return null
  const text = bezug.art === 'lexware'
    ? `Lexware ${bezug.nummer}`
    : `Correction of ${bezug.beleg_nummer || `#${bezug.beleg_id}`}`
  return <Pill tone="plain">{text}</Pill>
}

const zeigeVorgang = (e) => (
  <Box data-testid="vorgang-eintrag" data-eigener={e.eigener ? 'ja' : undefined}
    sx={{ minWidth: 0, ...(e.eigener ? EIGENE_ZEILE_SX : null) }}>
    <Stack direction="row" spacing={SP.sm} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography
        component="div"
        data-testid="vorgang-art"
        sx={{ fontFamily: 'monospace', fontSize: FS.sm, fontWeight: 600, overflowWrap: 'anywhere' }}
      >
        {e.label}
      </Typography>
      {e.belegId != null ? (
        <Link
          component={RouterLink}
          to={`/invoicing/${e.belegId}`}
          data-testid="vorgang-beleg"
          sx={{ fontSize: FS.sm, fontWeight: 600 }}
        >
          {e.belegNummer || `#${e.belegId}`}
        </Link>
      ) : (
        // Bestellweite Erstattung ohne Beleg (D4): kein Link, die Zeile nennt „Order".
        <Typography component="span" data-testid="vorgang-beleg"
          sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
          Order
        </Typography>
      )}
      <BezugChip bezug={e.belegBezug} />
    </Stack>
    <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
      {formatBerlinDateTime(e.date)}{e.akteur ? ` · ${e.akteur}` : ''}
    </Typography>
    {/* F11: die Kundenmitteilung als VERWEIS — die Bytes stehen nie im Verlauf
        (die Nutzlast trägt `{dateiname, mime, groesse, sha256}`). */}
    {e.dokument
      ? <DokumentVerweis invoiceId={e.invoiceId} verweis={e.dokument} />
      : <Nutzlast wert={e.nutzlast} />}
  </Box>
)

/**
 * B1 — der Vorgang der Bestellung. Liest `daten.vorgang` (alle Belege), hebt die
 * Eintraege des geoeffneten Belegs hervor, verlinkt jede Belegnummer.
 *
 * 🔴 **Nur die ANZEIGE liest `vorgang`.** Der IgL-Schreibpfad (`letztes`,
 * `iglSperre`, `iglWiederholung`) liest weiterhin `daten.verlauf` — das jüngste
 * Ereignis des EIGENEN Belegs (Grill-Blocker B1). Diese Trennung ist gepinnt.
 */
function VorgangBereich({ vorgang, kopfId }) {
  const eintraege = (vorgang || []).map((v, i) => ({
    id: `vorgang-${v.quelle}-${i}`,
    rang: i,
    date: v.zeit,
    art: v.art,
    type: paletteFuer(v.art),
    label: vorgangLabel(v.art, v.nutzlast),
    belegId: v.beleg_id,
    belegNummer: v.beleg_nummer,
    belegBezug: v.beleg_bezug,
    nutzlast: v.nutzlast,
    // F11: der Dokumentverweis der Kundenmitteilung (Plan B6) — die Bytes
    // liefert allein die GET-Route, die Zeitleiste zeigt Dateiname und Groesse.
    dokument: v.art === 'anschrift_dokument' ? (v.nutzlast?.dokument || null) : null,
    // 🔴 R15-W2: die Rechnung des BELEGS, nicht die des Kopfes. Ein Vorgang
    // traegt auch Ereignisse fremder Belege derselben Bestellung; mit `kopfId`
    // zeigte der Knopf auf `/invoices/<kopf>/anschrift-dokument/<sha des
    // anderen Belegs>` und liefe in 404.
    invoiceId: v.beleg_id != null ? v.beleg_id : kopfId,
    akteur: v.akteur,
    eigener: v.beleg_id != null && v.beleg_id === kopfId,
  }))
  return (
    <Bereich titel="Order timeline">
      {/* R16 W3 (2026-10-13): die Zaehlzeile nur bei mindestens EINEM Ereignis —
          sonst stuende „0 events · oldest first · complete" unmittelbar ueber
          „No event yet.", und die zwei Zeilen widerspraechen einander im Ton. */}
      {eintraege.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {eintraege.length === 1 ? '1 event' : `${eintraege.length} events`} · oldest first · complete
        </Typography>
      )}
      <ActivityTimeline
        entries={eintraege}
        renderEntry={zeigeVorgang}
        severityFor={severityFor}
        sortKey={nachRang}
        disablePaper
        emptyLabel="No event yet."
      />
    </Bereich>
  )
}

// ── Neuversand ─────────────────────────────────────────────────────────────

/**
 * Die Antwort des Neuversands. Drei Sorten:
 *
 *   * `feld` / `fehler` — die Antwort kam VOR dem Versuch (Riegel, Konto, Form,
 *     404): nichts hat sich geändert, der Satz steht in der Maske.
 *   * `seite` mit `art: 'erfolg' | 'nicht'` — der Server hat den Versuch
 *     erreicht (`zustand`): ein Ereignis steht im Verlauf, das Detail lädt neu,
 *     und der Satz lebt in der SEITE (Gate P1).
 *   * `seite` mit `art: 'unbekannt'` — keine Antwort, oder ein 5xx ohne
 *     `zustand` (etwa ein Proxy-Zeitlimit mitten im SMTP-Versand): der Ausgang
 *     ist unbekannt, NICHT „gescheitert" (Gate P3). Auch hier lädt das Detail neu.
 */
/** Die Kennung des Normalfalls fuer `deuteNeuversand` — sie schaltet den
 * uebersetzten Sperrgrund ein (W5) und sonst nichts. */
const ART_ERNEUT = 'erneut'

/**
 * Die Marken des Mailers in Bedienertext (Regel-16-W5).
 *
 * 🔴 **Die Marke darf nicht roh in die Flaeche.** `mailing.PLATZHALTER_MARKE` ist
 * `"TODO(owner)"`, und sie geht als `grund` mit — ein Bediener las bisher
 * „Reason: TODO(owner):de". Ebenso `mailing.FENSTER` (`"fenster"`).
 *
 * ⚠️ **Nur die bekannten Marken werden uebersetzt; alles andere bleibt roh.** Die
 * uebrigen Werte sind echte Diagnose (`status:mailed`, `document_type:correction`)
 * — sie zu ueberschreiben hiesse, dem Bediener die einzige Spur zu nehmen, die
 * den Fall erklaert.
 */
function grundSatz(grund) {
  if (!grund) return ''
  if (String(grund).startsWith('TODO(owner)')) {
    return 'the standard text for this language is not released yet'
  }
  if (grund === 'fenster') {
    return 'another delivery for this invoice was started just now'
  }
  return grund
}

/**
 * Die Antwort des Neuversands. Drei Sorten:
 * ...
 */
function deuteNeuversand(status, data, art) {
  if (data?.error === 'neuversand_gesperrt') {
    return { fehler: 'Resend is switched off. Nothing was sent.' }
  }
  if (status === 403) {
    return {
      fehler: 'No write permission for this account — resend is only possible '
        + 'for released accounts. Nothing was sent.',
    }
  }
  // 🔴 `feldArt` nennt das Feld, an dem der Satz stehen muss. Der Reparaturweg
  // hat nur eines (`an`) und liest es nicht — die Erneut-Maske hat drei.
  if (status === 400 && data?.error === 'an_ungueltig') {
    return { feldArt: 'an',
             feld: 'No valid address — exactly one address like name@example.com.' }
  }
  if (status === 400 && data?.error === 'nachricht_zu_lang') {
    return { feldArt: 'nachricht', feld: 'The message is too long — please shorten it.' }
  }
  if (status === 400 && data?.error === 'nachricht_ungueltig') {
    return { feldArt: 'nachricht', feld: 'The message must be plain text.' }
  }
  if (status === 400 && data?.error === 'betreff_zu_lang') {
    return { feldArt: 'betreff', feld: 'The subject is too long — please shorten it.' }
  }
  if (status === 400 && data?.error === 'betreff_ungueltig') {
    return { feldArt: 'betreff',
             feld: 'The subject must be a single line — line breaks are not allowed.' }
  }
  if (status === 400) return { feldArt: 'an', feld: 'Please enter an address.' }
  if (status === 404) return { fehler: 'The invoice was not found. Nothing was sent.' }
  // 🔴 R15 C1 (2026-10-18): die Sendegrenze der Route (`@limiter.limit` in
  // `admin_detail.py`) ist ein EIGENER Ausgang — sie antwortet VOR dem Versand,
  // also ist nichts gescheitert und nichts hinausgegangen. Ohne diesen Zweig
  // fiel der Fall durch bis `Resend failed: HTTP 429`: roher Jargon auf einer
  // Fläche, die ausdrücklich jargon-frei gehalten wird.
  if (status === 429) {
    return {
      fehler: 'Too many attempts for this invoice — please wait a minute. Nothing was sent.',
    }
  }
  if (data?.zustand === 'versandt') return { seite: { art: 'erfolg', ton: 'success', text: 'Mailed again.' } }
  if (data?.zustand === 'fehler') {
    return {
      seite: { art: 'nicht', ton: 'error', text: 'The delivery aborted with an error. The attempt is in the history.' },
    }
  }
  if (data?.zustand) {
    const label = NEUVERSAND_ZUSTAND_LABEL[data.zustand] || data.zustand
    // 🔴 Regel-16-W5: `data.grund` ist die ROHFORM des Servers. Fuer den neuen
    // Weg wird sie uebersetzt; der Reparaturweg behaelt sie unveraendert (er ist
    // Bestandsverhalten und nicht Gegenstand dieses Auftrags — als Karte
    // gemeldet).
    const grund = art === ART_ERNEUT ? grundSatz(data.grund) : data.grund
    return { seite: { art: 'nicht', ton: 'error', text: `Not mailed: ${label}.${grund ? ` Reason: ${grund}` : ''}` } }
  }
  if (status === undefined || status === null || status >= 500) {
    return { seite: { art: 'unbekannt', ton: 'warning', text: '' } }
  }
  return { fehler: `Resend failed: ${data?.error || `HTTP ${status}`}` }
}

/**
 * Der Satz eines Ergebnisses auf der Seite — samt dem, was das Neuladen gerade
 * tut. `laden`: 'laeuft' | 'neu' | 'gescheitert'.
 */
function ergebnisText(ergebnis, laden) {
  if (ergebnis.art === 'unbekannt') {
    if (laden === 'neu') return 'Outcome unknown — the state has been reloaded. Whether the mail went out is shown by status and history.'
    if (laden === 'gescheitert') return 'Outcome unknown — and the state could not be reloaded. Please “Try again”.'
    return 'Outcome unknown — the page is reloading the state.'
  }
  const nachsatz = {
    laeuft: 'Status and history are reloading …',
    neu: 'Status and history have been reloaded.',
    gescheitert: 'Status and history could not be reloaded.',
  }[laden]
  return `${ergebnis.text} ${nachsatz}`
}

function NeuversandMaske({ offen, invoiceId, mail, onSchliessen, onErgebnis }) {
  const titelId = useId()
  const eingeschraenkt = mail?.kunden_email_eingeschraenkt === true
  const vorbelegt = eingeschraenkt ? '' : (mail?.kunden_email || '')
  const [adresse, setAdresse] = useState(vorbelegt)
  const [sendet, setSendet] = useState(false)
  const [feldFehler, setFeldFehler] = useState(null)
  const [fehler, setFehler] = useState(null)

  useEffect(() => {
    if (!offen) return
    setAdresse(vorbelegt)
    setFeldFehler(null)
    setFehler(null)
  }, [offen, vorbelegt])

  let hinweis
  if (eingeschraenkt) {
    hinweis = 'After erasure under Art. 17 GDPR no prefill — the stored address is not proposed for sending again.'
  } else if (mail?.kunden_email) {
    hinweis = 'Prefilled with the address on the invoice.'
  } else {
    hinweis = 'No address on the invoice — please enter one.'
  }

  const an = adresse.trim()
  const sende = (e) => {
    e.preventDefault()
    if (!an || sendet) return
    setSendet(true)
    setFeldFehler(null)
    setFehler(null)
    api.post(`/api/invoicing/admin/invoices/${invoiceId}/neuversand`, { an })
      .then(({ status, data }) => deuteNeuversand(status, data))
      // Ohne `response` (Netzwerk, Abbruch) bleibt `status` undefiniert → „unbekannt".
      .catch((err) => deuteNeuversand(err?.response?.status, err?.response?.data))
      .then((ergebnis) => {
        setSendet(false)
        // Hat der Server den Versuch erreicht (oder ist es unbekannt), gehört der
        // Satz der SEITE — sie lädt neu und hält ihn über den Statuswechsel hinweg.
        if (ergebnis.seite) {
          onErgebnis(ergebnis.seite)
          return
        }
        if (ergebnis.feld) setFeldFehler(ergebnis.feld)
        if (ergebnis.fehler) setFehler(ergebnis.fehler)
      })
  }

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { component: 'form', onSubmit: sende, noValidate: true } }}
    >
      <DialogTitle id={titelId}>Send invoice again</DialogTitle>
      <DialogContent>
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          <Typography variant="body2">
            Forge resends the invoice mail with the stored PDF — to the address below. Text and
            attachment come unchanged from the backend.
          </Typography>
          <TextField
            label="Recipient address"
            type="email"
            size="small"
            fullWidth
            autoFocus
            autoComplete="off"
            value={adresse}
            onChange={(e) => { setAdresse(e.target.value); setFeldFehler(null) }}
            error={Boolean(feldFehler)}
            helperText={feldFehler || ' '}
            disabled={sendet}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{hinweis}</Typography>
          {fehler && <Alert severity="error">{fehler}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={sendet}>Cancel</Button>
        <DisabledReason
          reason={sendet ? null : !an ? 'Enter an email address first.' : null}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={!an || sendet}
            startIcon={sendet ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
          >
            Send
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}

/**
 * „Send the invoice again" — der Normalfall (Owner-Auftrag 2026-10-16).
 *
 * 🔴 **Nicht zu verwechseln mit `NeuversandMaske`.** Die repariert: die Mail kam
 * nicht an (`undeliverable`), die Adresse wird korrigiert. Hier ist die Rechnung
 * **angekommen** — derselbe Beleg geht noch einmal hinaus.
 *
 * 🔴 **Ein Formular mit DREI Feldern, jedes optional, jedes mit sichtbarer
 * Vorgabe** (Auftrag A4):
 *
 *   * **To** — vorbelegt mit der Bestelladresse (`mail.kunden_email`). Ein
 *     abweichender Empfänger kommt vor; leer heisst „die Bestelladresse".
 *   * **Subject** — leer heisst `mail.betreff_vorgabe` (der Betreff, der ohne
 *     Eingabe hinausgeht). 🔴 Er steht als **Hilfetext** unter dem Feld, nicht als
 *     Wert **und nicht als `placeholder`** (Regel-16-W2, 2026-10-16): ein
 *     `placeholder` stand hier nie im DOM, und vorbelegt wäre er ein eigener
 *     Betreff und damit etwas anderes.
 *   * **Message** — 🔴 **additiv, nicht ersetzend** (Regel-16-B2). Der
 *     Standard-Text ist die **Einleitung** und geht **immer** mit
 *     (`mailing.py`, Schritt (9): `[anrede, "", nachricht?, "", intro]`); was der
 *     Bediener schreibt, steht als **Zeile darüber**. Leer heisst: nur die
 *     Einleitung.
 *
 * 🔴 **Der leere Fall ist der Normalfall, kein Fehler** (Auftrag A4). Es gibt
 * genau EINEN Grund, in dem gar nichts geht: der Standard-Text dieser **Sprache**
 * ist noch nicht freigegeben (`mail.standard_nachricht_bereit === false`). Dann
 * sagt die Maske das VOR dem Absenden und sperrt den Versand — auch
 * mit geschriebenem Text, denn die Einleitung ginge trotzdem mit dem Platzhalter
 * hinaus. Fail-closed, wie das Backend (Muster Gate S5.5-F P7).
 *
 * Die zwei Obergrenzen und der Vorgabe-Betreff kommen vom Server; die Anzeige
 * führt keine zweite Zahl und keinen zweiten Text.
 *
 * `ergebnis.art === 'neuversand'` ist Absicht: Ausgang, Meldungsfläche und
 * Neuladen sind derselbe Weg wie beim Reparaturversand — nur die Route und die
 * Eingabe unterscheiden sich.
 */
function ErneutSendenMaske({ offen, invoiceId, mail, onSchliessen, onErgebnis }) {
  const titelId = useId()
  // 🔴 Die Kennung des SICHTBAREN Sperrgrundes (Regel-16-B4). Der Satz steht
  // genau EINMAL im DOM — im `Alert` —, und der gesperrte Knopf zeigt per
  // `aria-describedby` darauf. `DisabledReason` ist hier **verboten**: sein
  // `@dontUseFor` nennt genau diesen Fall („a cause that is already VISIBLE next
  // to the control") und die Hausform daneben.
  const sperrgrundId = useId()
  const bereit = mail?.standard_nachricht_bereit === true
  // 🔴 Nach einer Art.-17-Löschung liefert das Backend `kunden_email: null` UND
  // `kunden_email_eingeschraenkt: true` (`admin_detail.py`). Die zwei Fälle sind
  // NICHT derselbe: hier wird nichts vorbelegt, weil nichts mehr da ist — und der
  // Hilfetext sagt das (Regel-16-B3, dieselbe Form wie `NeuversandMaske`).
  const eingeschraenkt = mail?.kunden_email_eingeschraenkt === true
  const vorgabeAn = eingeschraenkt ? '' : (mail?.kunden_email || '')
  const vorgabeBetreff = mail?.betreff_vorgabe || ''
  const maxNachricht = Number(mail?.nachricht_max_zeichen) > 0
    ? Number(mail.nachricht_max_zeichen) : null
  const maxBetreff = Number(mail?.betreff_max_zeichen) > 0
    ? Number(mail.betreff_max_zeichen) : null

  // 🔴 **Grenze UND Zähler in den Hilfetext** (Regel-16-B1). Ohne die Zahl sieht
  // der Bediener nicht, dass `maxLength` ihm den Schwanz abschneidet — und genau
  // dieses Kürzen lehnt das Backend ab (`mailing.py`: „Kein Kürzen … ein halber
  // Satz beim Kunden wäre schlimmer als eine Fehlermeldung"), der gut gebaute
  // `nachricht_zu_lang`-Pfad wäre von dieser Fläche aus also unerreichbar.
  // Hausform ist die IgL-Maske dieser Datei: `… (${x.length}/500) …`.
  // Liefert der Server keine Zahl, bleibt der Satz ohne Klammer.
  const mitZaehler = (satz, wert, max) => (max ? `${satz} (${wert.length}/${max}).` : `${satz}.`)

  const [an, setAn] = useState('')
  const [betreff, setBetreff] = useState('')
  const [text, setText] = useState('')
  const [sendet, setSendet] = useState(false)
  const [feldFehler, setFeldFehler] = useState({})
  const [fehler, setFehler] = useState(null)

  useEffect(() => {
    if (!offen) return
    setAn(vorgabeAn)
    setBetreff('')
    setText('')
    setFeldFehler({})
    setFehler(null)
    // Nur beim Öffnen zurücksetzen: die Vorgabe kommt aus dem Detail und darf
    // eine laufende Eingabe nicht überschreiben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen])

  // Der einzige Sperrgrund, der den GANZEN Weg schliesst.
  const sperrgrund = !bereit
    ? 'The standard text for this invoice’s language is not released yet — nothing can be sent.'
    : null

  const sende = (e) => {
    e.preventDefault()
    if (sendet || sperrgrund) return
    setSendet(true)
    setFeldFehler({})
    setFehler(null)
    api.post(`/api/invoicing/admin/invoices/${invoiceId}/erneut-senden`,
      { an: an.trim(), betreff: betreff.trim(), nachricht: text.trim() })
      .then(({ status, data }) => deuteNeuversand(status, data, ART_ERNEUT))
      // Ohne `response` (Netzwerk, Abbruch) bleibt `status` undefiniert → „unbekannt".
      .catch((err) => deuteNeuversand(err?.response?.status, err?.response?.data, ART_ERNEUT))
      .then((ergebnis) => {
        setSendet(false)
        // Hat der Server den Versuch erreicht (oder ist es unbekannt), gehört der
        // Satz der SEITE — sie lädt neu und hält ihn über den Statuswechsel hinweg.
        if (ergebnis.seite) {
          onErgebnis(ergebnis.seite)
          return
        }
        if (ergebnis.feld) {
          setFeldFehler({ [ergebnis.feldArt || 'an']: ergebnis.feld })
        }
        if (ergebnis.fehler) setFehler(ergebnis.fehler)
      })
  }

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { component: 'form', onSubmit: sende, noValidate: true } }}
    >
      <DialogTitle id={titelId}>Send the invoice again</DialogTitle>
      <DialogContent>
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            The stored PDF goes out again — attachment and legal footer come
            unchanged from the backend. Everything below is optional: To and
            Subject fall back to the default shown under the field, and a
            message is added above the standard text.
          </Typography>

          {/* 🔴 Der Sperrgrund traegt die Kennung, auf die der gesperrte Knopf
              zeigt (B4) — der Satz steht genau EINMAL im DOM. */}
          {sperrgrund && <Alert severity="warning" id={sperrgrundId}>{sperrgrund}</Alert>}

          <TextField
            label="To (optional)"
            type="email"
            size="small"
            fullWidth
            autoFocus
            autoComplete="off"
            value={an}
            onChange={(e) => { setAn(e.target.value); setFeldFehler((f) => ({ ...f, an: null })) }}
            error={Boolean(feldFehler.an)}
            helperText={feldFehler.an || (eingeschraenkt
              ? 'After erasure under Art. 17 GDPR no prefill — the stored address is not '
                + 'proposed for sending again.'
              : vorgabeAn
                ? `Prefilled with the order address (${vorgabeAn}).`
                : 'The invoice carries no address — please enter one.')}
            disabled={sendet}
          />

          <TextField
            label="Subject (optional)"
            size="small"
            fullWidth
            autoComplete="off"
            value={betreff}
            onChange={(e) => { setBetreff(e.target.value); setFeldFehler((f) => ({ ...f, betreff: null })) }}
            error={Boolean(feldFehler.betreff)}
            helperText={feldFehler.betreff || mitZaehler(
              vorgabeBetreff ? `Empty sends: ${vorgabeBetreff}` : 'Empty sends the standard subject',
              betreff, maxBetreff)}
            disabled={sendet}
            slotProps={maxBetreff ? { htmlInput: { maxLength: maxBetreff } } : undefined}
          />

          <TextField
            label="Message to the customer (optional)"
            size="small"
            fullWidth
            multiline
            minRows={3}
            autoComplete="off"
            value={text}
            onChange={(e) => { setText(e.target.value); setFeldFehler((f) => ({ ...f, nachricht: null })) }}
            error={Boolean(feldFehler.nachricht)}
            helperText={feldFehler.nachricht || mitZaehler(
              'Empty sends the standard message alone; anything written is added above it',
              text, maxNachricht)}
            disabled={sendet}
            slotProps={maxNachricht ? { htmlInput: { maxLength: maxNachricht } } : undefined}
          />

          {fehler && <Alert severity="error">{fehler}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={sendet}>Cancel</Button>
        {/* 🔴 KEIN `DisabledReason` (Regel-16-B4). Der Grund steht schon sichtbar
            im `Alert` oben; der Wrapper legte eine ZWEITE, versteckte Kopie in
            den a11y-Baum (der Satz wuerde doppelt vorgelesen) und einen
            `tabIndex`-Fokuspunkt fuer einen Zustand, den keine Nutzeraktion
            aufloest. `disabled` bleibt, die Kennung zeigt auf den sichtbaren Satz
            — genau die Hausform aus `DisabledReason`s eigenem `@dontUseFor`. */}
        <Button
          type="submit"
          variant="contained"
          disabled={sendet || Boolean(sperrgrund)}
          aria-describedby={sperrgrund ? sperrgrundId : undefined}
          startIcon={sendet ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── BA7 — der Ausfuhrnachweis in Forge ─────────────────────────────────────

/**
 * Die Maske „Nachweis hochladen" — ein `<form>`, wie `ErneutSendenMaske`.
 *
 * 🔴 **Der Zuordnungs-Check ist ein Formular, kein Automatismus** (Gesamtplan
 * §5.1, Stufe 1): die `SHOP-<nr>` tippt der Mensch vom Scan ab, die drei
 * uebrigen Pruefungen sind Haken. Kein OCR — das gibt es im Repo nicht.
 *
 * 🔴 **Jede der vier Pruefungen hat ihren eigenen Satz**, und die Saetze kommen
 * vom Server: bei 422 traegt `data.fehler` je `feld` den Satz, der hier an
 * genau diesem Feld steht. Ein Fehlschlag schreibt nichts (der Server sammelt
 * alle Fehler und antwortet 422 vor der Ablage).
 *
 * 🔴 **Der Knopf heisst „Upload proof"** — nicht „Upload and refund". BA6 (der
 * Geldpfad) existiert noch nicht; der Knopf schreibt Bytes und die
 * Ereigniszeile `eingegangen` und erstattet nicht. Die Umbenennung ist Teil
 * von BA6 (Gesamtplan §3). Der Plan fuehrt ihn unter seinem deutschen Namen
 * „Nachweis hochladen"; diese Seite ist englisch (Hausform der
 * Rechnungsflaechen).
 */
/**
 * 🔴 **Die drei Pruefhaken starten AN** (R16 B1 / Q1, Lesart A). Der Plan sagt
 * es zweimal: §1 „Der Haken ‚geprueft und stimmt so' ist **voreingestellt an**
 * — in **beiden** Admin-Zugaengen" (Owner, woertlich: *„Denn dann haben das
 * entweder Nils oder ich schon gecheckt."*), und §3 fuer BA7 „Haken ‚geprueft'
 * **an**". Die drei Haken SIND dieser Haken; die Flaeche hat keinen anderen.
 *
 * Sie behaupten damit nichts Ungeprueftes: wer den Scan in Forge oder der
 * Order-View hochlaedt, hat ihn vorher angesehen. Der Server prueft sie
 * trotzdem und lehnt ab, solange einer falsch ist — die Voreinstellung ist eine
 * Bedienhilfe, kein Riegel. `PRUEFHAKEN_VORGABE` in der Extension (BA8) traegt
 * dieselbe Vorgabe; der Code wird nicht geteilt.
 */
const HAKEN_AN = { rechnungsnummer: true, stempel: true, empfaenger: true }

function NachweisMaske({ offen, invoiceId, bestellung, onSchliessen, onErfolg, onUnbekannt }) {
  const titelId = useId()
  const dateiHinweisId = useId()
  // Der Knopf oeffnet das versteckte Feld (Hausmuster, s. Kommentar unten).
  const dateiRef = useRef(null)
  const fehlerIds = {
    rechnungsnummer: useId(),
    stempel: useId(),
    empfaenger: useId(),
  }

  const [datei, setDatei] = useState(null)
  const [shop, setShop] = useState('')
  const [haken, setHaken] = useState({ ...HAKEN_AN })
  const [sendet, setSendet] = useState(false)
  const [feldFehler, setFeldFehler] = useState({})
  const [fehler, setFehler] = useState(null)
  // 🔴 `hover: none`, nicht `sm`: ein Tablet MIT Maus behaelt die Flaeche
  // (W11). Auf einem groben Zeiger gibt es kein Ziehen, also auch keine
  // gestrichelte Kante und keinen Satz, der eines verspricht.
  const ohneZiehen = useMediaQuery('(hover: none)')

  useEffect(() => {
    if (!offen) return
    setDatei(null)
    setShop('')
    // 🔴 Auch nach jedem Oeffnen wieder AN — die Vorgabe ist der Zustand der
    // Flaeche, nicht der Rest des letzten Versuchs (R16 B1).
    setHaken({ ...HAKEN_AN })
    setFeldFehler({})
    setFehler(null)
  }, [offen])

  const setzeHaken = (feld) => (ereignis) => {
    setHaken((alt) => ({ ...alt, [feld]: Boolean(ereignis.target.checked) }))
    setFeldFehler((alt) => ({ ...alt, [feld]: null }))
  }

  // 🔴 EIN Weg fuer Knopf und Ablage. Die LEERE Liste ist der Fall „zwei
  // Dateien auf einmal": react-dropzone schiebt bei `multiple: false` jede
  // Datei nach `fileRejections` und liefert hier `[]` (gemessen an 14.3.8).
  // Ohne diesen Schutz loeschte sie die vorige Wahl.
  // 🔴 Dieselbe LEERE Liste kommt auch vom Knopf, wenn der Nutzer den
  // Dateidialog abbricht (`files?.[0] || null`): der Abbruch behaelt die
  // vorige Wahl — Absicht, nicht Zufall (R15 W3, Plan-Nachtrag S7a-NB2).
  // Die sofortige Rueckmeldung zu Groesse und Leerzustand kommt in GENAU
  // diese Funktion, damit Knopf und Wurf sie teilen (Z8).
  // 🔴 **Sofort sichtbar, was der Browser schon weiss** (Owner 01.11.: „man
  // muss sehen, was für ein File hochgeladen werden soll"). Leere Datei und
  // Groesse ueber der Grenze fallen hier auf, nicht erst nach dem Absenden —
  // dieselbe Regel wie im Kundenkonto (`ausfuhrregeln.dateiPruefen`), nur ohne
  // zweite Zahl: `AUSFUHR_MAX_BYTES` ist die eine Quelle. Der `onChange` des
  // versteckten Feldes und der Wurf rufen deshalb BEIDE diese Funktion
  // (Merge mit master, 03.11.: die Pruefung stand dort als eigener Block).
  const waehleDatei = (gewaehlt) => {
    if (!gewaehlt) return
    setDatei(gewaehlt)
    setFeldFehler((alt) => ({ ...alt, datei: dateiBefund(gewaehlt) }))
  }

  const weiseAb = (abweisungen) => {
    const zuViele = abweisungen?.some(
      (eintrag) => eintrag?.errors?.some((e) => e?.code === 'too-many-files'),
    )
    if (!zuViele) return
    setFeldFehler((alt) => ({ ...alt, datei: AUSFUHR_FEHLER_TEXT.mehrere_dateien }))
  }

  const sende = (e) => {
    e.preventDefault()
    if (sendet) return
    // 🔴 **Der Sofortbefund haelt den Sendeweg auf** (R16 W-1, Nachbesserung 3):
    // vorher fragte nur `if (!datei)` — eine 21-MB-Datei zeigte „The file is
    // too large." und wurde auf Klick trotzdem geschickt (der Server antwortete
    // 413 mit demselben Satz), waehrend `setFeldFehler({})` die rote Zeile
    // waehrend des Sendens LOESCHTE. Jetzt steht hier dieselbe Pruefung wie in
    // `waehleDatei`, VOR dem Leeren: der Befund bleibt sichtbar, und kein
    // Abruf geht hinaus.
    // 🔴 **Zweite Grenze, nicht die einzige** (R16 C1, Nachbesserung 4): der
    // Absenden-Knopf ist bei Befund `disabled` + `aria-describedby` auf den
    // sichtbaren Satz — die Hausform dieser Datei, wie `ErneutSendenMaske` und
    // `IgLFreigabeMaske` sie leben (gesperrt, Grund sichtbar daneben). Diese
    // Pruefung bleibt trotzdem stehen: sie faengt den Weg, der den Knopf nicht
    // braucht (Enter im `<form>`), und den Zustand, in dem der Befund erst
    // zwischen Wahl und Klick entsteht. Der Server bleibt die dritte Grenze.
    const befund = dateiBefund(datei)
    if (befund) {
      setFeldFehler((alt) => ({ ...alt, datei: befund }))
      return
    }
    setSendet(true)
    setFeldFehler({})
    setFehler(null)
    const form = new FormData()
    form.append('datei', datei)
    form.append('shop_nummer', shop.trim())
    form.append('rechnungsnummer_geprueft', haken.rechnungsnummer ? '1' : '0')
    form.append('stempel_vorhanden', haken.stempel ? '1' : '0')
    form.append('empfaenger_stimmt', haken.empfaenger ? '1' : '0')
    // 🔴 Der Content-Type MUSS hier stehen: `api` traegt global
    // `Content-Type: application/json`; ohne diese Zeile serialisiert axios
    // die `FormData` als JSON und der Server findet `request.files` leer
    // (dieselbe Falle wie `BelegVorschlag.jsx`, 2026-10-19).
    api.post(`/api/invoicing/admin/invoices/${invoiceId}/export-proof`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(({ data }) => {
        setSendet(false)
        onErfolg(data)
      })
      .catch((err) => {
        setSendet(false)
        const status = err?.response?.status
        const daten = err?.response?.data
        if (daten?.error === 'zuordnung' && Array.isArray(daten.fehler)) {
          const jeFeld = {}
          for (const eintrag of daten.fehler) {
            if (eintrag?.feld) jeFeld[eintrag.feld] = eintrag.satz
          }
          setFeldFehler(jeFeld)
          setFehler('Nothing was uploaded — please correct the marked checks.')
          return
        }
        const code = daten?.error
        if (code === 'datei_leer' || code === 'datei_fehlt'
          || code === 'datei_zu_gross' || code === 'rumpf_zu_gross'
          || code === 'typ_nicht_erlaubt' || code === 'mime_passt_nicht') {
          setFeldFehler({ datei: AUSFUHR_FEHLER_TEXT[code] })
          return
        }
        if (status === 403) {
          setFehler(AUSFUHR_FEHLER_TEXT.forbidden)
          return
        }
        if (status === 404) {
          setFehler('This invoice does not exist.')
          return
        }
        // Ein **bekannter** Code ist eine definierte Antwort — der Server hat
        // vor jedem Schreibzugriff abgelehnt (`datei_*`, `typ_*`, `zuordnung`,
        // `forbidden`, `kein_ausfuhrvorgang`, `datei_fehler`). Er bekommt seinen
        // Satz und **laedt nicht neu** (R15 W1): die Liste hat sich nicht
        // geaendert, und ein Neuladen behauptete eine Bewegung, die es nicht gab.
        const satz = AUSFUHR_FEHLER_TEXT[code]
        if (satz) {
          setFehler(satz)
          return
        }
        // 🔴 **Unbekannt heisst unbekannt** (R16 W2). Die Route schreibt erst
        // nach `pruefe_zuordnung`; ein Timeout oder ein 5xx kann also NACH dem
        // Commit kommen. „Nothing was stored" waere eine Behauptung ueber einen
        // Ausgang, den dieser Client nicht kennt — die Seite hat fuer genau
        // diesen Fall ihre eigene Sprache („Outcome unknown", `ergebnisText`).
        // Deshalb der ehrliche Satz und beide Ladewege neu: die Liste, weil dort
        // die Zeile stuende, und das Detail, weil `ausfuhr.nachweis_vorhanden`
        // sonst weiter „fehlt" sagt.
        setFehler('The upload may or may not have gone through — the list is being reloaded.')
        onUnbekannt?.()
      })
  }

  const hakenFeld = (feld, beschriftung) => (
    <FormControl error={Boolean(feldFehler[feld])}>
      <FormControlLabel
        control={(
          <Checkbox
            checked={haken[feld]}
            onChange={setzeHaken(feld)}
            disabled={sendet}
            slotProps={{ input: feldFehler[feld]
              ? { 'aria-describedby': fehlerIds[feld] }
              : {} }}
          />
        )}
        label={beschriftung}
      />
      {feldFehler[feld]
        ? <FormHelperText id={fehlerIds[feld]}>{feldFehler[feld]}</FormHelperText>
        : null}
    </FormControl>
  )

  // 🔴 **Der Befund der GEWAEHLTEN Datei**, fuer den Absenden-Knopf (R16 C1).
  // `datei &&` ist Pflicht, nicht Kosmetik: `dateiBefund(null)` liefert
  // „Please choose a file." — ohne die Vorschaltung waere „Upload proof" schon
  // VOR jeder Wahl gesperrt, und der richtige Hinweis kaeme nie.
  // Bewusst NICHT `feldFehler.datei`: der traegt auch den Doppelwurf-Satz
  // („Drop one file at a time."), waehrend die vorige, gueltige Wahl weiter
  // gesendet werden darf — genau das sichert S7a (3) zu.
  const dateiSatz = datei ? dateiBefund(datei) : null

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { component: 'form', onSubmit: sende, noValidate: true } }}
    >
      <DialogTitle id={titelId}>Upload export proof</DialogTitle>
      <DialogContent>
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            The stamped invoice goes into the export case. Nothing is refunded and
            no state changes beyond “received”. Check the four points on the scan
            before uploading — the server rejects the upload if one does not match.
          </Typography>

          <Box>
            {/* 🔴 Hausmuster wie `BelegVorschlag.jsx`: verstecktes Feld plus
                `ref`, der Knopf oeffnet es per `onClick`. Ein
                `Button component="label"` war mit der MAUS bedienbar (native
                Label-Aktivierung), mit der TASTATUR aber nicht: MUI rendert
                dann ein `<label role="button" tabIndex={0}>`, dessen
                Enter-/Space-Zweig den `onClick`-Prop aufruft — und den gab es
                hier nicht. Enter und Space taten nichts (R15-Blocker B1,
                WCAG 2.1.1 „Keyboard"). Ein echtes `<button>` bringt die
                Tastaturaktivierung nativ mit. */}
            <input
              ref={dateiRef}
              hidden
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
              aria-label="Stamped proof file"
              onChange={(e) => waehleDatei(e.target.files?.[0] || null)}
            />
            {/* 🔴 Die Ablage umschliesst NUR den Knopf und den Ziehen-Satz.
                `noClick` + `noKeyboard`: die Box oeffnet keinen zweiten
                Dateidialog und traegt keinen zweiten Tabstopp — der Knopf ist
                der Tastaturweg (die Probe zaehlt genau einen Aufruf am
                beschrifteten Feld). Kein `accept` an der Zone: ein Typfilter
                verschluckte eine falsche Datei still, statt sie in dieselbe
                Pruefung zu geben wie der Knopf. */}
            <FileDropzone
              onFiles={(dateien) => waehleDatei(dateien?.[0] || null)}
              onRejected={weiseAb}
              noClick
              noKeyboard
              disabled={sendet}
              data-testid="nachweis-drop"
              sx={ohneZiehen ? { p: 0, borderStyle: 'none' } : undefined}
            >
              {({ isDragActive }) => (
                <>
                  <Button
                    variant="outlined"
                    disabled={sendet}
                    onClick={() => dateiRef.current?.click()}
                    aria-describedby={dateiHinweisId}
                  >
                    {datei ? 'Choose another file' : 'Choose file'}
                  </Button>
                  {ohneZiehen ? null : (
                    <Typography
                      variant="caption"
                      component="p"
                      sx={{ mt: SP.tight, color: 'text.secondary' }}
                    >
                      {isDragActive ? 'Drop it here' : 'or drop the file here'}
                    </Typography>
                  )}
                </>
              )}
            </FileDropzone>
            {/* 🔴 ZWEI Zeilen in EINEM dauerhaft gerenderten Live-Bereich
                (R16 C1/C2, Hausform `CopyButton.jsx:264`,
                `DiscontinuedDialog.jsx:173`): die Meldung oben, darunter
                IMMER Dateiname + Groesse bzw. der Formathinweis. Vorher
                ersetzte die Meldung die Dateizeile — nach einem abgewiesenen
                Wurf erfuhr der Nutzer nicht mehr, WELCHE Datei gewaehlt ist.
                Der Behaelter steht von Anfang an im DOM, sonst kaeme die
                Ansage beim ersten Wechsel nicht (WCAG 2.1 4.1.3). Genau EINE
                Region, kein zusaetzliches `role="alert"`. */}
            <Box id={dateiHinweisId} role="status" sx={{ mt: SP.tight }}>
              {feldFehler.datei && (
                <Typography variant="caption" component="p" sx={{ color: 'error.main' }}>
                  {feldFehler.datei}
                </Typography>
              )}
              <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
                {datei
                  ? `${datei.name} · ${formatiereGroesse(datei.size)}`
                  : `PDF, JPEG, PNG or HEIC — up to ${AUSFUHR_MAX_BYTES / (1024 * 1024)} MB.`}
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Order number on the invoice"
            size="small"
            fullWidth
            autoComplete="off"
            placeholder="SHOP-11956"
            value={shop}
            onChange={(e) => {
              setShop(e.target.value)
              setFeldFehler((alt) => ({ ...alt, shop_nummer: null }))
            }}
            error={Boolean(feldFehler.shop_nummer)}
            helperText={feldFehler.shop_nummer
              || (bestellung
                ? `The invoice belongs to ${bestellung}.`
                : 'Type the SHOP number printed on the invoice.')}
            disabled={sendet}
          />

          {hakenFeld('rechnungsnummer', 'Invoice number checked')}
          {hakenFeld('stempel', 'Customs stamp present')}
          {hakenFeld('empfaenger', 'Recipient matches')}

          {fehler && <Alert severity="error">{fehler}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={sendet}>Cancel</Button>
        {/* 🔴 Gesperrt, solange die GEWAEHLTE Datei einen Befund traegt, und
            ueber `aria-describedby` an den sichtbaren Satz gebunden — kein
            `DisabledReason` (R16 C1): der Grund steht schon im Live-Bereich
            darueber, eine zweite Kopie im a11y-Baum wuerde doppelt vorgelesen
            (dieselbe Begruendung wie in `ErneutSendenMaske`). Kein dauerhaft
            toter Knopf: „Choose another file" bleibt der Ausweg, und eine
            gueltige Wahl gibt ihn frei. Ohne Datei bleibt er aktiv — der Klick
            zeigt dann „Please choose a file.". */}
        <Button
          type="submit"
          variant="contained"
          disabled={sendet || Boolean(dateiSatz)}
          aria-describedby={dateiSatz ? dateiHinweisId : undefined}
          startIcon={sendet ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
        >
          {sendet ? 'Uploading …' : 'Upload proof'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Ein Haken des Uploaders als Wort — `null`/fehlend ist „—", nie ein geratenes „no". */
const hakenWort = (wert) => {
  if (wert === true) return 'yes'
  if (wert === false) return 'no'
  return STRICH
}
const HAKEN_FELDER = ['rechnungsnummer_geprueft', 'stempel_vorhanden', 'empfaenger_stimmt']

/**
 * BA3b — die Bestaetigung einer Entscheidung ueber einen eingegangenen Nachweis.
 *
 * 🔴 **Warum nicht `ConfirmDialog`/`PromptDialog`** (`components/common/confirm/`):
 * beide haben keinen Fehlerplatz und keinen Endzustand `beendet`, `PromptDialog`
 * keinen Folgesatz. Diese Maske braucht alle drei — ein Fehler bleibt in ihr
 * stehen, und nach einem bewegten Stand gibt es nur noch „Close". Sie ist
 * seiteneigen wie `IgLFreigabeMaske`, deren Bauform sie folgt.
 *
 * 🔴 **Die Vier-Augen-Seite.** Die Maske nennt, was der Uploader bestaetigt hat
 * (Quelle, SHOP-Nummer, die drei Haken) — aus der Listenzeile, die
 * `nachweis_liste` ohnehin liefert. Wer freigibt, sieht so, ob ein Mensch den
 * Scan schon angesehen hat oder ein Kunde ihn ohne Pruefung hochgeladen hat.
 *
 * 🔴 **Nach einem unbekannten Ausgang kein zweiter Versuch** (Grill W1): der
 * erste kann gespeichert sein. Die Maske steht dann auf `beendet`, Liste und
 * Detail laden neu, und ist die Zeile danach noch offen, stehen die Knoepfe
 * wieder in der Tabelle.
 *
 * 🔴 **Nichts wird beim Schliessen zurueckgesetzt** (Grill W3): `zeile` und
 * `entscheidung` haelt der Aufrufer bis `onExited` fest, Fehler und Grund
 * leert erst das naechste Oeffnen. So bleiben Titel und Satz waehrend der
 * Ausblendung stehen.
 */
function EntscheidungsMaske({ offen, invoiceId, zeile, entscheidung, onSchliessen, onErfolg,
  onNeuLaden, onAusgangUnbekannt, onNachSchliessen }) {
  const titelId = useId()
  const satzId = useId()
  const text = ENTSCHEIDUNG_TEXT[entscheidung] || ENTSCHEIDUNG_TEXT.geprueft
  const ablehnen = entscheidung === 'abgelehnt'
  const knopfRef = useRef(null)
  // R16b W-a: die Ref sperrt SYNCHRON — zwei Absendungen im selben Takt saehen
  // sonst beide `sendet === false` und schickten zwei Entscheidungen.
  const sendetRef = useRef(false)
  const [grund, setGrund] = useState('')
  const [sendet, setSendet] = useState(false)
  const [fehler, setFehler] = useState(null)
  const [beendet, setBeendet] = useState(false)
  // K-F1 Regel 3: ein gesperrter Knopf gibt den Fokus ab; nach einem
  // definierten Fehler (403/429/…) bekommt er ihn zurueck.
  useFokusNachLaden(knopfRef, sendet)
  // R16 W2 (WCAG 2.4.3): im Zustand `beendet` ist der Bestaetigen-Knopf
  // ausgehaengt, `useFokusNachLaden` hat nichts mehr, wohin es zurueckgeben
  // koennte, und der Fokus bliebe auf dem Dialog-Container. „Close" ist dann der
  // einzige Weg weiter — er bekommt den Fokus. Probe „BA3b T4c".
  const schliessenRef = useRef(null)
  useEffect(() => {
    if (beendet) schliessenRef.current?.focus()
  }, [beendet])

  // Erst das (Wieder-)Oeffnen gibt die Sperre frei — nach einem Erfolg bleibt sie
  // bis dahin stehen (R15-Gate C2, siehe `sende`).
  useEffect(() => {
    if (!offen) return
    sendetRef.current = false
    setSendet(false)
    setGrund('')
    setFehler(null)
    setBeendet(false)
  }, [offen])

  const sende = (e) => {
    e.preventDefault()
    if (sendetRef.current || beendet || !zeile) return
    sendetRef.current = true
    setSendet(true)
    setFehler(null)
    const rumpf = { entscheidung }
    const getrimmt = grund.trim()
    if (ablehnen && getrimmt) rumpf.grund = getrimmt
    api.post(`/api/invoicing/admin/invoices/${invoiceId}/export-proof/${zeile.id}/entscheidung`, rumpf)
      .then(
        ({ data }) => {
          // 🔴 R15-Gate C2: die Sperre bleibt nach dem Erfolg STEHEN. `onErfolg`
          // schliesst die Maske, aber sie blendet noch aus (bis `onExited`); gaebe
          // dieser Zweig die Sperre frei, waere der Bestaetigen-Knopf in diesem
          // Fenster wieder scharf, und ein zweiter Klick oder Enter schickte eine
          // zweite Entscheidung (gemessen: zwei Aufrufe). Der Server antwortete
          // 409 — in eine Maske, die niemand mehr sieht. Frei wird die Sperre erst
          // beim naechsten Oeffnen (Effekt oben). Probe „BA3b T12".
          onErfolg(data)
        },
        (err) => {
          sendetRef.current = false
          setSendet(false)
          const deutung = deuteEntscheidungsFehler(err)
          setFehler(deutung.satz)
          if (deutung.beendet) setBeendet(true)
          if (deutung.neuLaden) onNeuLaden?.()
          if (deutung.unbekannt) onAusgangUnbekannt?.()
        },
      )
  }

  const pruefung = zeile?.pruefung || {}
  const hatHaken = HAKEN_FELDER.some((feld) => pruefung[feld] != null)
  const quelle = NACHWEIS_QUELLE_LABEL[zeile?.quelle] || 'unknown source'

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      aria-describedby={satzId}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: { component: 'form', onSubmit: sende, noValidate: true },
        transition: { onExited: onNachSchliessen },
      }}
    >
      <DialogTitle id={titelId}>{text.titel}</DialogTitle>
      <DialogContent>
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          <Box>
            <Typography variant="body2">
              {`${zeile?.dateiname || `proof ${zeile?.id}`} · received ${tag(zeile?.ereignis_am)}`}
            </Typography>
            <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
              {`Source: ${quelle} · Order number entered: ${pruefung.shop_nummer || STRICH}`}
            </Typography>
            <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
              {hatHaken
                ? `Invoice number checked: ${hakenWort(pruefung.rechnungsnummer_geprueft)}`
                  + ` · Customs stamp present: ${hakenWort(pruefung.stempel_vorhanden)}`
                  + ` · Recipient matches: ${hakenWort(pruefung.empfaenger_stimmt)}`
                : 'No checks recorded.'}
            </Typography>
            {/* `v2.warn.main` wie in der Tabelle (R16 B2: `warning.main` misst
                als Schrift nur 3,11:1). */}
            {zeile?.doppelt_verwendet ? (
              <Typography variant="caption" component="p" sx={{ color: 'v2.warn.main' }}>
                Same file at another invoice
              </Typography>
            ) : null}
          </Box>
          <Typography id={satzId} variant="body2" sx={{ color: 'text.secondary' }}>
            {text.satz}
          </Typography>
          {ablehnen && (
            <TextField
              label="Reason (optional)"
              multiline
              minRows={2}
              size="small"
              fullWidth
              autoFocus
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
              helperText={`Internal note for the order timeline — not sent to the customer (${grund.length}/${ENTSCHEIDUNG_GRUND_MAX}).`}
              disabled={sendet || beendet}
              slotProps={{ htmlInput: { maxLength: ENTSCHEIDUNG_GRUND_MAX } }}
            />
          )}
          {fehler && <Alert severity="error">{fehler}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button ref={schliessenRef} onClick={onSchliessen} disabled={sendet}>
          {beendet ? 'Close' : 'Cancel'}
        </Button>
        {/* 🔴 Grill B2 / K-F1: der Knopf TRAEGT den Lauf (`loading` +
            `aria-busy`), die Beschriftung bleibt. `loadingPosition="start"`
            haelt sie lesbar — die Vorgabe `center` setzt `color: transparent`.
            Im Zustand `beendet` gibt es nichts mehr zu bestaetigen. */}
        {!beendet && (
          <Button
            ref={knopfRef}
            type="submit"
            variant="contained"
            color={ablehnen ? 'error' : 'primary'}
            loading={sendet}
            loadingPosition="start"
            aria-busy={sendet || undefined}
            disabled={sendet}
          >
            {text.knopf}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

/**
 * BA12 — der Dialog „Email export instructions" (Handversand der Hinweismail).
 *
 * Nach dem Vorbild `EntscheidungsMaske` (K-F1): der Sendeknopf TRAEGT den Lauf
 * (`loading`, `loadingPosition="start"`, `aria-busy`), die Beschriftung bleibt,
 * `useFokusNachLaden` gibt den Fokus nach einem definierten Fehler zurueck, und
 * eine Ref sperrt SYNCHRON gegen ein Doppelsenden im selben Takt.
 *
 * 🔴 **Der Rumpf ist `{bestaetigt: true}`** bei unveraenderter Vorbelegung und
 * `{bestaetigt: true, an}` sonst. Eine andere Adresse als die vorbelegte ist ein
 * Probeversand und zaehlt NICHT als „Kunde informiert" (Plan E8) — der
 * Hilfetext sagt es. Ohne bekannte Adresse (`adresse_noetig`, Art. 17 oder
 * Abpraller) ist das Feld Pflicht und nicht vorbelegt.
 *
 * Ausgaenge: 200 → `onErgebnis({ an, probe, pflicht })` (der Aufrufer schliesst
 * und meldet); definierter Fehler → Satz im Dialog, bei bewegtem Stand
 * `onNeuLaden`; unbekannter Ausgang → Satz im Dialog, nur noch „Close",
 * `onUnbekannt` — **kein** Erfolg (Review Focus 5). Ein zweiter Versuch nach
 * einem unbekannten Ausgang geht ueber ein neues Oeffnen, nachdem Stand und
 * Zeitleiste neu geladen sind.
 *
 * 🔴 **R16 C2 (28.10.): ein bewegter Stand schliesst ebenfalls ab** — 409
 * `gesperrt`, Schalter aus, `laeuft_schon` und 502 `baufehler` liefern
 * `beendet` (`deuteHinweisFehler`), danach nur „Close", wie `EntscheidungsMaske`.
 * Scharf bleiben `fehler` (SMTP), `adresse_noetig`, `an_ungueltig`, 401, 429.
 *
 * R16 W1: weicht die Adresse von der Vorbelegung ab, heissen Hilfetext und Knopf
 * „Test copy"/„Send test copy", sobald es eintritt.
 *
 * `hinweis` ist der Stand beim OEFFNEN (der Aufrufer haelt ihn bis zum
 * naechsten Oeffnen fest) — ein Neuladen waehrend der offenen Maske
 * ueberschreibt die Eingabe nicht.
 */
function AusfuhrHinweisMaske({ offen, invoiceId, hinweis, onSchliessen, onErgebnis, onNeuLaden,
  onUnbekannt }) {
  const titelId = useId()
  const satzId = useId()
  const knopfRef = useRef(null)
  const schliessenRef = useRef(null)
  // Synchrone Sperre: zwei Absendungen im selben Takt saehen sonst beide `sendet === false`.
  const sendetRef = useRef(false)
  const vorbelegt = hinweis?.kunden_adresse || ''
  const pflicht = hinweis?.adresse_noetig === true
  const [an, setAn] = useState(vorbelegt)
  const [sendet, setSendet] = useState(false)
  const [fehler, setFehler] = useState(null)
  const [beendet, setBeendet] = useState(false)
  useFokusNachLaden(knopfRef, sendet)
  // Im Zustand `beendet` ist der Sendeknopf ausgehaengt — „Close" bekommt den
  // Fokus (dasselbe wie EntscheidungsMaske, R16 W2).
  useEffect(() => {
    if (beendet) schliessenRef.current?.focus()
  }, [beendet])

  // Erst das (Wieder-)Oeffnen setzt zurueck. Nach einem Erfolg bleibt die
  // Sperre bis dahin stehen (die ausblendende Maske sendet kein zweites Mal).
  useEffect(() => {
    if (!offen) return
    sendetRef.current = false
    setSendet(false)
    setAn(vorbelegt)
    setFehler(null)
    setBeendet(false)
    // `vorbelegt` bewusst nicht in den Abhaengigkeiten: ein Neuladen der Lage
    // waehrend der offenen Maske soll die Eingabe nicht ueberschreiben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen])

  const getrimmt = an.trim()
  const sprache = HINWEIS_SPRACHE[hinweis?.sprache] || HINWEIS_SPRACHE.en
  const versand = Array.isArray(hinweis?.versand) ? hinweis.versand : []
  let frueher = null
  if (hinweis?.kunde_informiert_am) {
    frueher = `The customer was already emailed on ${tag(hinweis.kunde_informiert_am)}.`
  } else if (versand.length > 0 && pflicht) {
    // R16 C1: ohne Kundenadresse gibt es keine „andere" Adresse — und der
    // Versand zaehlt nicht als informiert (E8, Q1 entschieden).
    frueher = `Last sent on ${tag(versand[0].am)} to an address entered by hand — not counted as informing the customer.`
  } else if (versand.length > 0) {
    frueher = `Last sent on ${tag(versand[0].am)} to another address — the customer has not been emailed yet.`
  }
  // R16 W1: eine von der Vorbelegung abweichende Adresse ist eine Testkopie
  // (E8) — Hilfetext und Knopf sagen es, SOBALD es eintritt, nicht erst das
  // Erfolgsband. Vergleich wie der Server (`strip().lower()`).
  const probe = !pflicht && getrimmt !== ''
    && getrimmt.toLowerCase() !== (hinweis?.kunden_adresse || '').trim().toLowerCase()
  let hilfe = 'Another address sends a test copy. It does not count as informing the customer.'
  if (pflicht) hilfe = 'There is no customer address on file — enter one. Sending to it is not counted as informing the customer.'
  else if (probe) hilfe = 'Test copy — the customer will not be marked as informed.'

  const sende = (e) => {
    e.preventDefault()
    if (sendetRef.current || beendet || !getrimmt) return
    sendetRef.current = true
    setSendet(true)
    setFehler(null)
    const geaendert = pflicht
      || getrimmt.toLowerCase() !== (hinweis?.kunden_adresse || '').trim().toLowerCase()
    const rumpf = geaendert ? { bestaetigt: true, an: getrimmt } : { bestaetigt: true }
    api.post(`/api/invoicing/admin/invoices/${invoiceId}/ausfuhr-hinweis`, rumpf)
      .then(
        () => {
          // Die Sperre bleibt STEHEN (siehe Effekt oben).
          onErgebnis({ an: getrimmt, probe: geaendert && !pflicht, pflicht })
        },
        (err) => {
          sendetRef.current = false
          setSendet(false)
          const deutung = deuteHinweisFehler(err)
          setFehler(deutung)
          // R16 C2: bei bewegtem Stand schliesst der Dialog ab (nur „Close"),
          // wie `EntscheidungsMaske` — ein zweiter Klick bekaeme dieselbe Antwort.
          if (deutung.beendet) setBeendet(true)
          if (deutung.unbekannt) {
            onUnbekannt?.()
          } else if (deutung.neuLaden) {
            onNeuLaden?.()
          }
        },
      )
  }

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      aria-describedby={satzId}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { component: 'form', onSubmit: sende, noValidate: true } }}
    >
      <DialogTitle id={titelId}>Email export instructions</DialogTitle>
      <DialogContent>
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          <Typography id={satzId} variant="body2" sx={{ color: 'text.secondary' }}>
            {`Sends the approved ${sprache} email that explains how to get the customs stamp and upload the proof.`}
          </Typography>
          {frueher && <Typography variant="body2">{frueher}</Typography>}
          <TextField
            label="To"
            type="email"
            size="small"
            fullWidth
            required={pflicht}
            autoFocus={pflicht}
            value={an}
            onChange={(e) => setAn(e.target.value)}
            disabled={sendet || beendet}
            helperText={hilfe}
          />
          {fehler && (
            <Alert severity={fehler.unbekannt ? 'warning' : 'error'}>{fehler.satz}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button ref={schliessenRef} onClick={onSchliessen} disabled={sendet}>
          {beendet ? 'Close' : 'Cancel'}
        </Button>
        {!beendet && (
          <DisabledReason reason={sendet ? null : !getrimmt ? 'Enter an email address first.' : null}>
            <Button
              ref={knopfRef}
              type="submit"
              variant="contained"
              loading={sendet}
              loadingPosition="start"
              aria-busy={sendet || undefined}
              disabled={sendet || !getrimmt}
            >
              {probe ? 'Send test copy' : 'Send'}
            </Button>
          </DisabledReason>
        )}
      </DialogActions>
    </Dialog>
  )
}

/**
 * BA7 — der Abschnitt „Export proof" der Rechnungsdetailseite.
 *
 * 🔴 **Er erscheint nur bei einem Kandidaten**, und die Bedingung kommt vom
 * Server: `admin_detail.detail` liefert `ausfuhr` aus
 * `export_proof.ausfuhr_sicht` (dieselbe Funktion wie die Kundenflaeche).
 * Diese Seite prueft **kein Land und keinen Status** (Gesamtplan §6, „eine
 * Quelle").
 *
 * 🔴 **Freigeben und Ablehnen seit BA3b, weiterhin kein Geld und keine Mail.**
 * An der EINEN entscheidbaren Zeile (`entscheidbareId`) stehen „Approve" und
 * „Reject"; beide oeffnen `EntscheidungsMaske` und schreiben ueber die
 * BA3-Route eine neue Zeile `geprueft`/`abgelehnt`. Erstattet wird nichts (das
 * ist BA6), und der Kunde erfaehrt von einer Ablehnung nichts automatisch. Ein
 * neuer Nachweis aus dem Upload loest serverseitig genau einmal die
 * BA4-Eingangsmail aus.
 *
 * 🔴 **BA12: „Email export instructions" neben „Upload proof".** Knopf, Stand
 * und Sperrgrund kommen allein aus `hinweis` (GET `…/export-proof`, Lage des
 * Servers). `hinweis: null` — unbekannt, auch beim ersten Laden und nach einem
 * Ladefehler — zeigt WEDER Knopf noch Stand (Review Focus 5); „Upload proof"
 * bleibt davon unberuehrt.
 */
function AusfuhrBereich({ invoiceId, ausfuhr, bestellung, onErfolg, onNeuLaden,
  onAusgangUnbekannt, onEntschieden, onHinweis }) {
  const [maskeOffen, setMaskeOffen] = useState(false)
  const { nachweise, hinweis, laedt, fehler, neuLaden } = useNachweise(invoiceId, true)
  // BA12: `hinweisOffen` steuert die Maske, `hinweisMaske` ist die Lage beim
  // Oeffnen — sie lebt bis zum naechsten Oeffnen (Muster `auswahl`, Grill W3).
  const [hinweisOffen, setHinweisOffen] = useState(false)
  const [hinweisMaske, setHinweisMaske] = useState(null)
  const hinweisGrundId = useId()
  const [oeffnend, setOeffnend] = useState(null)
  const [oeffneFehler, setOeffneFehler] = useState(null)
  // BA3b (Grill W3): `entscheidOffen` steuert die Maske, `auswahl` traegt ihren
  // Inhalt und lebt bis `onExited` — sonst rendert die Ausblendung ohne Zeile.
  const [entscheidOffen, setEntscheidOffen] = useState(false)
  const [auswahl, setAuswahl] = useState(null)
  const tabelleRef = useRef(null)
  const fokusNachSchliessen = useRef(false)

  const oeffne = (proofId) => {
    setOeffnend(proofId)
    setOeffneFehler(null)
    // 🔴 **Das Fenster wird SYNCHRON geholt, vor dem `await`** (R16 W1). Dieselbe
    // Hausregel wie bei `dokumentOeffner` oben: „ein Fenster, das nach einem
    // `await` aufgeht, blockiert iOS Safari." Die URL gibt es hier erst nach der
    // Anfrage, also oeffnet der Klick ein leeres Fenster und bekommt sie danach
    // gesetzt — ein Nutzergesten-Fenster, kein Popup.
    //
    // 🔴 **Ohne `noopener` im Feature-String, und das ist Absicht:** mit
    // `noopener` liefert `window.open` laut Spezifikation `null` zurueck, dann
    // gaebe es kein Handle. Der Schutz kommt stattdessen einen Schritt spaeter
    // ueber `fenster.opener = null` — gesetzt, BEVOR die fremde Seite geladen
    // wird.
    //
    // Der Token steht nie im Referer: er steht im Ziel selbst, nicht in der
    // Adresse dieser Seite. Zusaetzlich traegt die **Erfolgsantwort** von
    // `/nachweis/<token>` `Referrer-Policy: no-referrer` (`dokument_routes.py`,
    // der Kopf-Block der Route). 🔴 Die zwei 404-Zweige derselben Route tragen
    // ihn NICHT (R15 W3) — sie liefern JSON ohne Unterressourcen, von dort geht
    // nichts weiter. Wer den Kopf dort erwartet, sucht ihn an der falschen
    // Stelle; die Aussage gilt fuer die Seite, die der Bediener zu sehen bekommt.
    const fenster = window.open('', '_blank')
    if (!fenster) {
      setOeffnend(null)
      setOeffneFehler('The browser blocked the new tab. Please allow pop-ups for this page.')
      return
    }
    fenster.opener = null
    api.post(`/api/invoicing/admin/invoices/${invoiceId}/export-proof/${proofId}/link`)
      .then(({ data }) => {
        if (!data?.url) throw new Error('The response carried no link.')
        // Der Token lebt 120 s — das Fenster steht schon, es bekommt jetzt sein Ziel.
        fenster.location.assign(data.url)
      })
      .catch((e) => {
        // Ein leeres Fenster stehen zu lassen waere ein toter Tab.
        fenster.close()
        setOeffneFehler(`The link could not be created. ${ausfuhrFehlertext(e)}`)
      })
      .then(() => setOeffnend(null))
  }

  const meldeErfolg = (daten) => {
    setMaskeOffen(false)
    neuLaden()
    onErfolg(daten)
  }

  // 🔴 Unbekannter Ausgang (R16 W2): die Maske BLEIBT offen — der Satz gehoert
  // dorthin, wo der Mensch ihn sucht. Neu geladen wird trotzdem beides, sonst
  // behauptete die Seite weiter „Proof missing", waehrend der Nachweis vielleicht
  // schon liegt. Kein Erfolgsbanner: ein Ausgang, den niemand kennt, ist kein
  // Erfolg.
  //
  // 🔴 Und der ALTE Erfolgsbanner geht weg (R16 W-3): er stammt von einem
  // frueheren, gelungenen Upload und stuende jetzt neben „may or may not have
  // gone through" — zwei Baender, zwei Ereignisse, als Widerspruch lesbar.
  const meldeUnbekannt = () => {
    neuLaden()
    onNeuLaden?.()
    onAusgangUnbekannt?.()
  }

  // ── BA12: die Hinweismail von Hand ──────────────────────────────────────
  const oeffneHinweis = () => {
    setHinweisMaske(hinweis)
    setHinweisOffen(true)
  }
  // Erfolg: Maske zu, Liste neu (die Lage traegt den neuen Versand); das
  // Detail laedt der Aufrufer neu, er meldet mit `abRunde`.
  const meldeHinweis = (ergebnis) => {
    setHinweisOffen(false)
    neuLaden()
    onHinweis?.(ergebnis)
  }
  // Ein definierter Ausgang mit bewegtem Stand (409, 502 `fehler`): Liste und
  // Detail neu, die Maske bleibt mit ihrem Satz offen.
  const meldeHinweisBewegt = () => {
    neuLaden()
    onNeuLaden?.()
  }
  // Der Fokus geht beim Schliessen an den Ausloeser zurueck (MUI-Fokusfalle,
  // `disableRestoreFocus` ist aus) — der Knopf bleibt beim Neuladen stehen.
  const hinweisAnzeige = hinweis ? hinweisStand(hinweis) : null
  const hinweisGesperrt = hinweisAnzeige?.knopf === 'gesperrt'
  // R16 Q2: der GET gelang, die Lage fehlt — eine gedaempfte Zeile statt Schweigen.
  const hinweisUnbekannt = hinweis == null && !laedt && !fehler

  // ── BA3b: Freigeben/Ablehnen ────────────────────────────────────────────
  // 🔴 Waehrend eines Ladeauftrags gibt es KEINE Knoepfe (Review Focus 1): die
  // Liste, auf der die Regel rechnet, ist dann womoeglich schon ueberholt.
  const offeneId = laedt ? null : entscheidbareId(nachweise)

  const oeffneEntscheidung = (zeile, entscheidung) => {
    setAuswahl({ zeile, entscheidung })
    setEntscheidOffen(true)
  }

  // Erfolg: Maske zu, Liste neu; das Detail laedt der Aufrufer neu (er
  // meldet das Ergebnis mit `abRunde`). Der Fokus geht nach der Ausblendung auf
  // die Tabelle — der Ausloeser ist mit dem Neuladen verschwunden.
  const meldeEntschieden = (daten) => {
    setEntscheidOffen(false)
    fokusNachSchliessen.current = true
    neuLaden()
    onEntschieden?.(daten)
  }

  // Ein bekannter Code, der einen bewegten Stand meldet (`nicht_juengste` …),
  // oder ein unbekannter Ausgang: Liste UND Detail neu.
  const meldeEntscheidungBewegt = () => {
    neuLaden()
    onNeuLaden?.()
  }

  // Nach der Ausblendung (Grill W8a): faellt der Fokus auf `body`, weil der
  // Ausloeser beim Neuladen verschwunden ist, gehoert er auf die Tabelle.
  const nachEntscheidungZu = () => {
    const aktiv = document.activeElement
    if (fokusNachSchliessen.current || aktiv == null || aktiv === document.body) {
      tabelleRef.current?.focus()
    }
    fokusNachSchliessen.current = false
    setAuswahl(null)
  }

  const tage = typeof ausfuhr?.seit_tagen === 'number' ? ausfuhr.seit_tagen : null
  // 🔴 **Das Alterswort haengt am Zustand** (R15-W-1 der vierten Runde): „open"
  // ist nur fuer die zwei OFFENEN Arten wahr. `geprueft`/`abgelehnt` sind nach
  // BA3 beantwortete Ausgaenge, `abgelaufen` ist zu — an ihnen stuende „open"
  // als Behauptung ueber einen Vorgang, der nicht mehr offen ist. Dort nennt
  // der Kopf nur das Alter („N days old"); die Menge ist bewusst die der
  // offenen Arten und nicht `NACHWEIS_ARTEN` des Servers (die traegt auch die
  // beiden entschiedenen).
  //
  // 🔴 **Das dritte Segment haengt ebenfalls an der Art** (R16 C2 zu BA3b): nach
  // `abgelehnt` „No approved proof", obwohl `nachweis_vorhanden` dort wahr ist
  // (siehe `AUSFUHR_NACHWEIS_LABEL`). Ein neuer Upload macht die juengste Zeile
  // wieder zu `eingegangen` — dann steht wieder „Proof on file" (Probe „C2 W4").
  // ⚠️ Wer kuenftig den Uebergang `abgelaufen` schreibt (heute schreibt ihn
  // niemand, RA-030), entscheidet dieses Segment mit: `abgelehnt → abgelaufen`
  // ergaebe sonst wieder „Proof on file" fuer einen nicht freigegebenen Nachweis.
  const alter = tage === null
    ? 'Age unknown'
    : `${tage} ${tage === 1 ? 'day' : 'days'} ${OFFENE_ARTEN.includes(ausfuhr?.art) ? 'open' : 'old'}`
  let nachweisSegment = AUSFUHR_NACHWEIS_LABEL.nein
  if (ausfuhr?.art === 'abgelehnt') nachweisSegment = AUSFUHR_NACHWEIS_LABEL.abgelehnt
  else if (ausfuhr?.nachweis_vorhanden) nachweisSegment = AUSFUHR_NACHWEIS_LABEL.ja
  const stand = [
    AUSFUHR_ART_LABEL[ausfuhr?.art] || 'Unknown',
    alter,
    nachweisSegment,
  ].join(' · ')

  return (
    <Bereich titel="Export proof">
      <Stack spacing={SP.card}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: SP.md,
          flexWrap: 'wrap',
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{stand}</Typography>
            {hinweisAnzeige && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="ausfuhr-hinweis-stand">
                {hinweisAnzeige.stand}
              </Typography>
            )}
            {/* R16 W5: der Grund steht direkt unter der Standzeile — Stand und
                Grund lesen sich als ein Block, auch auf dem Desktop, wo der
                Knopf rechts steht. Bei einem endgueltigen Grund (W3) ist er die
                einzige Erklaerung, warum es keinen Knopf gibt. */}
            {hinweisAnzeige && hinweisAnzeige.knopf !== 'frei' && (
              <Typography id={hinweisGrundId} variant="body2" sx={{ color: 'text.secondary' }} data-testid="ausfuhr-hinweis-grund">
                {AUSFUHR_HINWEIS_GRUND_TEXT[hinweis.knopf_grund] || AUSFUHR_HINWEIS_NICHT_MOEGLICH_SATZ}
              </Typography>
            )}
            {/* R16-WV2 W-D: was der Mitarbeiter jetzt tun kann — nur bei einem
                Grund aus `AUSFUHR_HINWEIS_HANDLUNG` (ohne_bestellung). */}
            {hinweisAnzeige && hinweisAnzeige.knopf !== 'frei' && AUSFUHR_HINWEIS_HANDLUNG[hinweis.knopf_grund] && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="ausfuhr-hinweis-handlung">
                {AUSFUHR_HINWEIS_HANDLUNG[hinweis.knopf_grund]}
              </Typography>
            )}
            {hinweisUnbekannt && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="ausfuhr-hinweis-stand">
                {AUSFUHR_HINWEIS_STAND_UNBEKANNT}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: SP.tight, flexWrap: 'wrap' }}>
            {/* BA12: nur bei bekannter Lage. Vorlaeufig gesperrt heisst
                `disabled` UND der sichtbare Satz links, ueber `aria-describedby`
                verknuepft — kein Tooltip (DisabledReason @dontUseFor: Grund schon
                sichtbar). Endgueltig gesperrt (R16 W3): KEIN Knopf — ein
                dauerhaft toter Knopf ist keine Hausform (R16-W4 der
                Nachweistabelle). */}
            {hinweisAnzeige && hinweisAnzeige.knopf !== 'keiner' && (
              <Button
                variant="outlined"
                size="small"
                disabled={hinweisGesperrt}
                aria-describedby={hinweisGesperrt ? hinweisGrundId : undefined}
                onClick={oeffneHinweis}
              >
                Email export instructions
              </Button>
            )}
            <Button variant="outlined" size="small" onClick={() => setMaskeOffen(true)}>
              Upload proof
            </Button>
          </Box>
        </Box>

        {hinweisAnzeige?.warnung && (
          <Alert severity="warning">{hinweisAnzeige.warnung}</Alert>
        )}

        {/* 🔴 Hausregel Dateikopf: „erstes Laden = Skeleton, nie Spinner;
            Neuladen laesst den Stand stehen (`LinearProgress`)." Der Abschnitt
            erscheint nach dem Seitenaufbau, sein Listenladen ist also ein ERSTES
            Laden (R16 W5). Beim Neuladen bleibt die Tabelle stehen und der Balken
            zeigt die Bewegung. */}
        {laedt && nachweise.length === 0 && (
          <Box data-testid="nachweise-laden" aria-busy="true">
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="55%" />
          </Box>
        )}
        {laedt && nachweise.length > 0 && (
          <LinearProgress aria-label="Loading export proofs" />
        )}

        {fehler && (
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={neuLaden}>Try again</Button>}
          >
            <AlertTitle>The export proofs could not be loaded.</AlertTitle>
            <Box component="span">{fehler}</Box>
          </Alert>
        )}

        {oeffneFehler && (
          <Alert severity="error" onClose={() => setOeffneFehler(null)} closeText="Close">
            {oeffneFehler}
          </Alert>
        )}

        {!laedt && !fehler && nachweise.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No proof received yet.
          </Typography>
        )}

        {nachweise.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            {/* R16 W3: seit BA3b traegt die Tabelle auch Entscheidungszeilen
                („Approved"/„Rejected", by …). Ihr Datum ist das der Entscheidung,
                nicht eines Eingangs — deshalb „Date" und „Export proofs", nicht
                „Received". „Actions": bis zu drei Knoepfe je Zeile. */}
            <Table size="small" aria-label="Export proofs" ref={tabelleRef} tabIndex={-1}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nachweise.map((zeile) => (
                  <TableRow key={zeile.id}>
                    <TableCell>{tag(zeile.ereignis_am)}</TableCell>
                    <TableCell>
                      <Pill tone={AUSFUHR_ART_TON[zeile.art] || 'plain'} bordered nowrap>
                        {AUSFUHR_ART_LABEL[zeile.art] || 'Unknown'}
                      </Pill>
                      {/* BA3b: wer entschieden hat — `geprueft_von` setzt der
                          Server fuer BEIDE Arten aus dem Token. */}
                      {zeile.geprueft_von ? (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ display: 'block', color: 'text.secondary' }}
                        >
                          {`by ${zeile.geprueft_von}`}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {zeile.dateiname || STRICH}
                      {/* 🔴 `v2.warn.main`, nicht `warning.main` (R16 B2):
                          `warning.main` (#ed6c02) als Schrift auf Papier misst
                          3,11:1 und liegt unter WCAG 1.4.3 (4,5:1 bei 12,5 px).
                          `v2.warn.main` (#b45309) misst 5,02:1 — die Hausform
                          fuer getoente Schrift (theme.js:1328). */}
                      {zeile.doppelt_verwendet ? (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ display: 'block', color: 'v2.warn.main' }}
                        >
                          Same file at another invoice
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      {Number.isFinite(zeile.groesse)
                        ? `${Math.max(1, Math.round(zeile.groesse / 1024))} KB`
                        : STRICH}
                    </TableCell>
                    <TableCell align="right">
                      {/* 🔴 Eine Zeile ohne Bytes bekommt GAR KEINEN Knopf (R16 W4).
                          `disabled` waere hier ein dauerhaft toter Knopf ohne
                          erklaerenden Satz — `DisabledReason` ist dafuer
                          ausdruecklich das falsche Werkzeug („permanent disabled
                          state that no user action can clear"), und „Expected"
                          plus Datei „—" sagt den Grund schon. */}
                      {/* BA3b: an der EINEN entscheidbaren Zeile stehen dazu
                          „Approve" und „Reject" — beide oeffnen erst die Maske,
                          kein Klick schreibt direkt. Der Name traegt die Datei,
                          sonst hiessen zwei Knoepfe in der Sprachausgabe gleich. */}
                      {zeile.hat_bytes || zeile.id === offeneId ? (
                        <Box sx={{
                          display: 'inline-flex',
                          gap: SP.tight,
                          flexWrap: 'wrap',
                          justifyContent: 'flex-end',
                        }}>
                          {zeile.hat_bytes ? (
                            <Button
                              size="small"
                              onClick={() => oeffne(zeile.id)}
                              aria-label={`Open ${zeile.dateiname || `proof ${zeile.id}`}`}
                              disabled={oeffnend === zeile.id}
                              startIcon={oeffnend === zeile.id
                                ? <CircularProgress size={16} color="inherit" aria-hidden="true" />
                                : null}
                            >
                              Open
                            </Button>
                          ) : null}
                          {zeile.id === offeneId ? (
                            <>
                              <Button
                                size="small"
                                onClick={() => oeffneEntscheidung(zeile, 'geprueft')}
                                aria-label={`Approve ${zeile.dateiname || `proof ${zeile.id}`}`}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                onClick={() => oeffneEntscheidung(zeile, 'abgelehnt')}
                                aria-label={`Reject ${zeile.dateiname || `proof ${zeile.id}`}`}
                              >
                                Reject
                              </Button>
                            </>
                          ) : null}
                        </Box>
                      ) : STRICH}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Stack>

      <NachweisMaske
        offen={maskeOffen}
        invoiceId={invoiceId}
        bestellung={bestellung}
        onSchliessen={() => setMaskeOffen(false)}
        onErfolg={meldeErfolg}
        onUnbekannt={meldeUnbekannt}
      />

      {auswahl && (
        <EntscheidungsMaske
          offen={entscheidOffen}
          invoiceId={invoiceId}
          zeile={auswahl.zeile}
          entscheidung={auswahl.entscheidung}
          onSchliessen={() => setEntscheidOffen(false)}
          onErfolg={meldeEntschieden}
          onNeuLaden={meldeEntscheidungBewegt}
          onAusgangUnbekannt={onAusgangUnbekannt}
          onNachSchliessen={nachEntscheidungZu}
        />
      )}

      <AusfuhrHinweisMaske
        offen={hinweisOffen}
        invoiceId={invoiceId}
        hinweis={hinweisMaske}
        onSchliessen={() => setHinweisOffen(false)}
        onErgebnis={meldeHinweis}
        onNeuLaden={meldeHinweisBewegt}
        onUnbekannt={meldeUnbekannt}
      />
    </Bereich>
  )
}

// ── Die IgL-Handfreigabe (#7, S6.1g) ────────────────────────────────────────

/** Die Antwortzustände der Freigabe (P6-W22 + 2.5a A3). */
const IGL_TON = {
  ausgestellt: 'success',
  gesperrt: 'warning',
  uebersprungen: 'info',
  schon_erledigt: 'info',
  fehler: 'error',
  nicht_versucht: 'warning',
}

/**
 * Die Sperrgründe einer IgL-Lage als Satz — `sperre.nutzlast.gruende`.
 *
 * 🔴 **Hausform wie `KUNDENBEZUG_TEXT`** (R16 B1): der Mensch liest einen Satz, nie
 * den rohen Code. Ein unbekannter Grund fällt auf einen allgemeinen Satz zurück,
 * der den Code nur klein daneben nennt.
 */
export const IGL_GRUND_TEXT = {
  'ustid:valid_no_match': 'The customer’s VAT ID does not match the BZSt record (no match).',
  'ustid:valid_ohne_abgleich': 'The BZSt record confirms the VAT ID only in simple form (without address check).',
  'ustid:abweichung_anmeldung': 'The invoice address differs from the address on file with the BZSt.',
  'ustid:ausserhalb_fenster': 'The VAT ID was confirmed outside the check window.',
  b2b_ustid_besteuert: 'The order of an exempt EU business carries tax (§ 6a UStG) — it must be refunded before the intra-Community supply.',
  b2b_igl_ohne_ustid_pruefung: 'The intra-Community supply has not yet undergone a VAT ID check.',
  'steuer:nullsatz_steuerpflichtig': 'An item with a price carries 0 % VAT although it would be taxable.',
  'steuer:steuer_bei_steuerfreier_lieferung': 'An item carries tax under a tax-exempt intra-Community supply (§ 14c UStG).',
  vor_ausstellung_veraendert: 'The order has changed since the invoice was created.',
  nicht_vollstaendig_erfuellt: 'The order is not fully fulfilled.',
}

/** Ein Sperrgrund als Satz — der Code nur klein daneben, wenn kein Satz hinterlegt ist. */
export const iglGrundText = (grund) =>
  IGL_GRUND_TEXT[grund] || `A block reason without a stored sentence (${grund}).`

/**
 * Die Fehlerantworten der Route in einem Satz — **jeder** Code aus `admin_igl.py`
 * hat hier einen Eintrag. Die Jest-Probe liest die Menge aus dem Python-Quelltext
 * und erzwingt die Deckung (R16 B1); ein unbekannter Code fällt auf einen
 * allgemeinen Satz zurück, der den Code klein daneben nennt.
 */
export const IGL_FEHLER_TEXT = {
  kein_schreibkonto: 'No write account — this release is only possible with the write account.',
  'not found': 'The invoice was not found.',
  freigabe_gesperrt: 'The release is blocked: the switch is not set to “live”, or a latch is closed.',
  veraltet: 'The situation has changed — the release no longer matches the current state.',
  nicht_freigabefaehig: 'This block cannot be released by hand (a reason lies outside the releasable set).',
  pruefzeile_passt_nicht: 'The VAT ID check line no longer matches the block.',
  shopify_unlesbar: 'Shopify is not readable — please try again later. Nothing was released.',
  steuer_nicht_erstattet: 'The tax of the order is not yet fully refunded.',
  'sperre_ereignis_id und pruef_id sind Pflicht': 'The call is incomplete (block or check line missing).',
  'begruendung muss 20 bis 500 Zeichen haben': 'The reason must be 20 to 500 characters.',
  ereignis_nicht_gespeichert: 'The release could not be saved — nothing was released.',
}

/** Der verletzte Teil der Steuererstattung in einem Satz (R16 B2). */
const IGL_TEIL_TEXT = {
  refund_mit_positionen: 'a refund carries items',
  summe: 'the refunded total differs from the tax',
  totalrefunded: 'the total refund of the order differs',
  menge: 'an item was changed in quantity',
}

/** `steuer_nicht_erstattet` mit **beiden Summen** und dem verletzten Teil (R16 B2). */
export const iglSteuerSatz = (data) => {
  const betrag = (wert) => {
    const zahl = Number(wert)
    return Number.isFinite(zahl) ? geld(zahl, 'EUR') : STRICH
  }
  const teil = IGL_TEIL_TEXT[data?.teil] || data?.teil
  return 'The tax of the order is not yet fully refunded: '
    + `refunded ${betrag(data?.erstattet)}, tax ${betrag(data?.steuer)}`
    + `${teil ? ` — ${teil}` : ''}.`
}

/** Den Ausstellungszustand der Antwort in einen Satz übersetzen. */
function iglErgebnisText(ausstellung) {
  const nummer = ausstellung?.invoice_number
  const gruende = (ausstellung?.gruende || []).map(iglGrundText).join(' ')
  switch (ausstellung?.zustand) {
    case 'ausgestellt':
      return `Released and issued${nummer ? `: ${nummer}` : ''}.`
    case 'gesperrt':
      return `Released — issuance stays blocked${gruende ? `: ${gruende}` : '.'}`
    case 'uebersprungen':
      return 'Released — issuance was skipped (cut-off or mode).'
    case 'schon_erledigt':
      return 'The invoice was already issued — no second document.'
    case 'nicht_versucht':
      return 'Released. Issuance was not attempted (Shopify not readable) — the release stands, please try again.'
    case 'fehler':
      return 'Released, but issuance failed — the release stands, the case is logged.'
    default:
      return 'Release saved.'
  }
}

/**
 * Die Antwort der Route deuten — ab dem Ereignis immer 200 (P6-W22).
 *
 * Rückgabe `{seite}` (Ergebnis auf der Seite), `{feld}` (Feldfehler, 400 — das
 * Begründungsfeld wird rot) oder `{fehler}` (allgemeiner Fehler — als `<Alert>`,
 * R16 W1). Der Code kommt **nie** roh vor den Menschen (R16 B1).
 */
function deuteIgL(status, data) {
  if (data && data.ausstellung) {
    return {
      seite: {
        art: 'igl',
        ton: IGL_TON[data.ausstellung.zustand] || 'info',
        text: iglErgebnisText(data.ausstellung),
      },
    }
  }
  const code = data?.error
  const satz = code === 'steuer_nicht_erstattet' ? iglSteuerSatz(data) : IGL_FEHLER_TEXT[code]
  if (status === 400) {
    return { feld: satz || IGL_FEHLER_TEXT['begruendung muss 20 bis 500 Zeichen haben'] }
  }
  return { fehler: satz || `The release failed (${code || `HTTP ${status}`}).` }
}

function IgLFreigabeMaske({ offen, invoiceId, sperre, onSchliessen, onErgebnis }) {
  const titelId = useId()
  const [begruendung, setBegruendung] = useState('')
  const [sendet, setSendet] = useState(false)
  // R16 W1: zwei Kanäle wie `NeuversandMaske` — `feldFehler` rötet das Feld,
  // `fehler` ist ein allgemeiner Fehler (403/409/502/500) und steht als `<Alert>`.
  const [feldFehler, setFeldFehler] = useState(null)
  const [fehler, setFehler] = useState(null)

  useEffect(() => {
    if (!offen) return
    setBegruendung('')
    setFeldFehler(null)
    setFehler(null)
  }, [offen])

  const zuKurz = begruendung.trim().length < 20
  const zuLang = begruendung.length > 500
  const sende = (e) => {
    e.preventDefault()
    if (zuKurz || zuLang || sendet) return
    setSendet(true)
    setFeldFehler(null)
    setFehler(null)
    api.post(`/api/invoicing/admin/invoices/${invoiceId}/igl-freigabe`, {
      sperre_ereignis_id: sperre?.id,
      pruef_id: sperre?.pruef_id,
      begruendung: begruendung.trim(),
    })
      .then(({ status, data }) => deuteIgL(status, data))
      .catch((err) => deuteIgL(err?.response?.status, err?.response?.data))
      .then((ergebnis) => {
        setSendet(false)
        // Ab dem gespeicherten Ereignis gehört der Satz der SEITE (P6-W22).
        if (ergebnis.seite) { onErgebnis(ergebnis.seite); return }
        if (ergebnis.feld) setFeldFehler(ergebnis.feld)
        if (ergebnis.fehler) setFehler(ergebnis.fehler)
      })
  }

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { component: 'form', onSubmit: sende, noValidate: true } }}
    >
      <DialogTitle id={titelId}>Release intra-Community supply</DialogTitle>
      <DialogContent>
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          <Typography variant="body2">
            Forge has blocked this invoice. The release is a tax decision: it is
            logged indelibly under your account and triggers issuance immediately.
            {(sperre?.gruende || []).length
              ? <> Block reasons: {sperre.gruende.map(iglGrundText).join(' ')}</>
              : null}
          </Typography>
          <TextField
            label="Reason"
            multiline
            minRows={2}
            size="small"
            fullWidth
            autoFocus
            value={begruendung}
            onChange={(e) => { setBegruendung(e.target.value); setFeldFehler(null) }}
            error={Boolean(feldFehler)}
            helperText={feldFehler
              || `Required, 20–500 characters (${begruendung.length}/500), internal log (not customer text).`}
            disabled={sendet}
            inputProps={{ maxLength: 500 }}
          />
          {fehler && <Alert severity="error">{fehler}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={sendet}>Cancel</Button>
        <DisabledReason
          reason={sendet
            ? null
            : zuKurz ? 'The reason needs at least 20 characters.'
              : zuLang ? 'The reason must be 500 characters at most.' : null}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={zuKurz || zuLang || sendet}
            startIcon={sendet ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
          >
            Release
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}

// ── Die Seite ──────────────────────────────────────────────────────────────

const BROTKRUME = { label: 'Customer Invoices', href: '/invoicing' }

function Laden() {
  return (
    <Stack spacing={SP.section} data-testid="detail-laden" aria-busy="true">
      <Paper elevation={0} sx={PANEL_SX}>
        <Skeleton variant="text" width="35%" />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="50%" />
      </Paper>
      <Paper elevation={0} sx={PANEL_SX}>
        <Skeleton variant="rounded" height={240} />
      </Paper>
    </Stack>
  )
}

/** Der Satz des Statusstreifens „Unzustellbar" je Riegelstellung (Gate P7). */
function unzustellbarText(modus) {
  if (modus === 'live') return 'The invoice mail was rejected. A resend to a corrected address is possible.'
  if (modus === 'off') {
    return 'The invoice mail was rejected. Resend is switched off — from here it currently cannot '
      + 'be sent again.'
  }
  return 'The invoice mail was rejected. The server does not report whether a resend is possible — it is '
    + 'therefore not offered.'
}


// ── F11 — Rechnungsanschrift und „Ausstellen jetzt" ────────────────────────

/** Die Felder des Dialogs, in der Reihenfolge der Postanschrift. */
const ANSCHRIFT_FELDER = [
  { feld: 'company', label: 'Company', autoComplete: 'organization' },
  { feld: 'name', label: 'Recipient name', autoComplete: 'name' },
  { feld: 'street', label: 'Street', autoComplete: 'address-line1' },
  { feld: 'street_2', label: 'Address line 2', autoComplete: 'address-line2' },
  { feld: 'postal_code', label: 'Postal code', autoComplete: 'postal-code' },
  { feld: 'city', label: 'City', autoComplete: 'address-level2' },
]

const LEERE_ANSCHRIFT = Object.fromEntries(ANSCHRIFT_FELDER.map((f) => [f.feld, '']))

/**
 * F11 — der Verweis auf die Kundenmitteilung in der Zeitleiste.
 *
 * 🔴 **Bytes nie im Verlauf** (Plan B6): die Nutzlast des Ereignisses traegt nur
 * `{dateiname, mime, groesse, sha256}`; die Bytes holt allein die GET-Route.
 * Und zwar **durch `api`**: die Route haengt an `@require_auth`, ein nacktes
 * `<a href>` haette keinen Token und liefe in ein 403. Der Blob geht als
 * Download heraus, damit die Datei nicht im Tab des Admins gerendert wird
 * (`attachment`, dieselbe Entscheidung wie im Backend).
 */
function DokumentVerweis({ invoiceId, verweis }) {
  const [laedt, setLaedt] = useState(false)
  const [fehler, setFehler] = useState(null)
  const dateiname = verweis?.dateiname || 'document'
  const groesse = verweis?.groesse != null ? formatiereGroesse(verweis.groesse) : null
  const lade = () => {
    if (!verweis?.sha256 || laedt) return
    setLaedt(true)
    setFehler(null)
    api.get(`/api/invoicing/admin/invoices/${invoiceId}/anschrift-dokument/${verweis.sha256}`,
      { responseType: 'blob' })
      .then(({ data }) => {
        const url = URL.createObjectURL(data)
        const anker = document.createElement('a')
        anker.href = url
        anker.download = dateiname
        document.body.appendChild(anker)
        anker.click()
        anker.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      })
      .catch(() => setFehler('The document could not be loaded — please try again.'))
      .then(() => setLaedt(false))
  }
  return (
    <Box sx={{ mt: SP.chip }}>
      <Button
        size="small"
        variant="outlined"
        onClick={lade}
        disabled={laedt}
        aria-busy={laedt || undefined}
        startIcon={laedt
          ? <CircularProgress size={16} aria-hidden="true" />
          : <FileDownloadOutlinedIcon />}
      >
        {`${dateiname}${groesse ? ` · ${groesse}` : ''}`}
      </Button>
      {fehler && <Typography variant="caption" component="p" sx={{ color: 'error.main' }}>{fehler}</Typography>}
    </Box>
  )
}

/** Ein nicht änderbarer Wert im Dialog — Land, USt-IdNr., E-Mail. */
function FesterWert({ label, wert, testId }) {
  return (
    <Box data-testid={testId}>
      <Typography component="div" variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography component="div" variant="body2" sx={{ overflowWrap: 'anywhere' }}>
        {wert == null || wert === '' ? STRICH : String(wert)}
      </Typography>
    </Box>
  )
}

/**
 * F11 — „Change billing address …": die Rechnungsanschrift **vor** der
 * Ausstellung überschreiben (Plan B1b).
 *
 * 🔴 **Der Server entscheidet, was gilt** — Firmen- und Namensregel, gesperrte
 * Felder, Drossel, Dokumentgrenzen. Diese Maske prüft nur, was der Browser
 * schon weiß (leere Felder, fehlendes Dokument, Größe, Endung), und gibt jedem
 * Fehlercode des Vertrags seinen Satz (`beschriftungen.ANSCHRIFT_FEHLER_TEXT`).
 *
 * 🔴 **Leer heißt unverändert.** Die Vorbelegung kommt aus der letzten
 * Überschreibung dieses Belegs, sonst ist sie leer — die Bestellanschrift
 * liefert das Detail nicht (gemessen: `snapshot_json` entsteht erst beim
 * Ausstellen). Die B2B-Registrierung steht als **Hinweis** daneben, nicht als
 * Vorbelegung: sie zu übernehmen wäre eine Änderung, die niemand angeklickt hat.
 */
/**
 * 🔴 R16-Bed. 1 (Runde 1): ein **5xx ohne bekannten Vertragscode** ist ein
 * unbekannter Ausgang, kein „nichts gespeichert".
 *
 * Ein Gateway-502 (Render, Deploy-Fenster) heisst: die Anfrage kann die
 * Anwendung erreicht und geschrieben haben. Der Server nennt dann keinen
 * Code aus der Karte; nur ein 5xx MIT bekanntem Code (etwa `shopify_unlesbar`,
 * 502) traegt eine Aussage ueber die Folge.
 */
function ausgangUnbekannt(status, code, karte) {
  if (status == null) return true
  return status >= 500 && !(code && karte[code])
}

function AnschriftMaske({ offen, invoiceId, verlauf, snapshot, mail, onSchliessen,
  onErgebnis, onNeuLaden }) {
  const titelId = useId()
  const satzId = useId()
  const dateiHinweisId = useId()
  const pflichtId = useId()
  const dateiRef = useRef(null)
  const knopfRef = useRef(null)
  // Synchron sperren: zwei Absendungen im selben Takt sähen sonst beide `false`.
  const sendetRef = useRef(false)
  const [werte, setWerte] = useState(LEERE_ANSCHRIFT)
  const [grund, setGrund] = useState('')
  const [quelle, setQuelle] = useState('')
  const [datei, setDatei] = useState(null)
  const [sendet, setSendet] = useState(false)
  const [feldFehler, setFeldFehler] = useState({})
  const [fehler, setFehler] = useState(null)
  // F11: die Vorbelegung kommt aus einem EIGENEN, lesenden Abruf — das Detail
  // traegt die Bestellanschrift nicht, und ein Shopify-Aufruf dort hinge an
  // jeder Detailansicht (Plan B4, Orchestrator-Entscheid zu Luecke 1).
  const [laedt, setLaedt] = useState(false)
  const [ladefehler, setLadefehler] = useState(null)
  const [herkunft, setHerkunft] = useState(null)
  // 🔴 R16-W4: die Vorbelegung mitschreiben — nur so ist „geleert" von
  // „war nie da" zu unterscheiden.
  const [vorbelegung, setVorbelegung] = useState({})
  const [gesperrt, setGesperrt] = useState(null)
  useFokusNachLaden(knopfRef, sendet)
  const ohneZiehen = useMediaQuery('(hover: none)')

  // Erst das (Wieder-)Öffnen setzt zurück — nach einem Erfolg bleibt die Sperre
  // stehen, bis die ausblendende Maske weg ist.
  useEffect(() => {
    if (!offen) return
    sendetRef.current = false
    setSendet(false)
    setWerte(LEERE_ANSCHRIFT)
    setGrund('')
    // 🔴 R16-W3: „Source" wird NICHT vorbelegt. Ein Datum zu setzen erfindet
    // eine Herkunft — liegt die Kundenmail vom 28.10., protokollierte die
    // Zeitleiste den heutigen Tag, vom Bediener nie bestätigt. Das Feld bleibt
    // leer, der Hilfetext nennt das Beispiel.
    setQuelle('')
    setDatei(null)
    // 🔴 R16-neu-2: die Vorbelegung gehoert zum OEFFNEN. Bleibt sie stehen,
    // meldet ein gescheitertes Laden an jedem Feld „Cleared — the order’s value
    // stays.", obwohl der Bediener nichts geleert hat.
    setVorbelegung({})
    setFeldFehler({})
    setFehler(null)
    setLadefehler(null)
    setHerkunft(null)
    setGesperrt(null)
    setLaedt(true)
    let abgebrochen = false
    // 🔴 Der Abruf laeuft NUR beim Oeffnen des Dialogs — nicht beim Laden der
    // Seite. Ein Fehler (502 `shopify_unlesbar`, Netz) laesst den Dialog offen
    // und LEER: geraten wird nichts, und leere Felder heissen beim Server
    // „unveraendert".
    api.get(`/api/invoicing/admin/invoices/${invoiceId}/anschrift`)
      .then(({ data }) => {
        if (abgebrochen) return
        const felder = data?.felder || {}
        const vorbelegt = Object.fromEntries(ANSCHRIFT_FELDER.map(
          ({ feld }) => [feld, felder[feld] == null ? '' : String(felder[feld])]))
        setWerte(vorbelegt)
        setVorbelegung(vorbelegt)
        setGesperrt(data?.gesperrt || null)
        setHerkunft(data?.quelle || null)
      })
      .catch(() => {
        if (abgebrochen) return
        setLadefehler('Could not load the order address. Empty fields stay as they are on the order.')
      })
      .then(() => { if (!abgebrochen) setLaedt(false) })
    return () => { abgebrochen = true }
  }, [offen, invoiceId])

  const registrierung = registrierungAus(verlauf || [])
  const empfaenger = snapshot?.empfaenger || {}
  // Die drei nicht aenderbaren Werte: der Abruf ist die naehere Quelle (er liest
  // die Bestellung); der eingefrorene Snapshot bleibt Rueckfall fuer den Fall,
  // dass der Abruf scheitert.
  const land = gesperrt?.country ?? empfaenger.country
  const ustid = gesperrt?.vat_id ?? empfaenger.vat_id
  const email = gesperrt?.email ?? mail?.kunden_email
  const HERKUNFT_TEXT = {
    billing: 'From the order’s billing address.',
    shipping: 'From the order’s shipping address (no billing address on the order).',
    ueberschreibung: 'From the last correction of this invoice.',
    keine: 'The order carries no billing or shipping address — please enter the address.',
  }
  // 🔴 F11/R16: ein Fehler gehoert an sein FELD (UX-Hinweis 3a). Der Server nennt
  // den Grund als Code; hier steht, welches Feld ihn traegt. Alles ohne Eintrag
  // bleibt Sammelmeldung.
  const FELD_JE_CODE = {
    firma_abweichend_von_registrierung: 'company',
    name_abweichend_von_bestellung: 'name',
    dokument_fehlt: 'dokument',
    dokument_typ_nicht_erlaubt: 'dokument',
    dokument_zu_gross: 'dokument',
    grund_fehlt: 'grund',
    quelle_fehlt: 'quelle',
  }
  const feldRefs = useRef({})
  const dateiKnopfRef = useRef(null)
  // 🔴 Der Fokus laeuft ueber einen Effekt, nicht direkt im Handler: MUI setzt
  // beim Dialog-Update selbst den Fokus und ueberschriebe einen Aufruf mitten
  // im Render (gemessen: `document.activeElement` blieb der Dialog-Container).
  const [fokusZiel, setFokusZiel] = useState(null)
  const fokussiere = (feld) => setFokusZiel(feld)
  useEffect(() => {
    if (!fokusZiel) return
    const ziel = fokusZiel === 'dokument' ? dateiKnopfRef.current : feldRefs.current[fokusZiel]
    ziel?.focus?.()
    setFokusZiel(null)
  }, [fokusZiel])
  // Die Reihenfolge, in der geprueft wird — und damit auch die Fokusreihenfolge.
  const FELD_REIHENFOLGE = ['company', 'name', 'street', 'street_2', 'postal_code',
    'city', 'grund', 'quelle', 'dokument']
  const erstesFehlerfeld = (fehler) => FELD_REIHENFOLGE.find((feld) => fehler[feld]) || null
  const feldAendern = (feld) => (ereignis) => {
    setWerte((alt) => ({ ...alt, [feld]: ereignis.target.value }))
    setFeldFehler((alt) => ({ ...alt, [feld]: null }))
  }
  const waehleDatei = (gewaehlt) => {
    if (!gewaehlt) return
    setDatei(gewaehlt)
    setFeldFehler((alt) => ({ ...alt, dokument: anschriftDateiBefund(gewaehlt) }))
  }
  const dateiSatz = datei ? anschriftDateiBefund(datei) : null

  const sende = (e) => {
    e.preventDefault()
    if (sendetRef.current || laedt) return
    const gesetzt = Object.entries(werte)
      .map(([feld, wert]) => [feld, String(wert || '').trim()])
      .filter(([, wert]) => wert)
    const befund = anschriftDateiBefund(datei)
    const neueFehler = {}
    if (!gesetzt.length) neueFehler.kein_feld = ANSCHRIFT_FEHLER_TEXT.kein_feld
    if (!grund.trim()) neueFehler.grund = ANSCHRIFT_FEHLER_TEXT.grund_fehlt
    if (!quelle.trim()) neueFehler.quelle = ANSCHRIFT_FEHLER_TEXT.quelle_fehlt
    if (befund) neueFehler.dokument = befund
    setFeldFehler(neueFehler)
    if (Object.keys(neueFehler).length) {
      // 🔴 Alles, was ein Feld hat, steht AM FELD — die Sammelmeldung bleibt für
      // Fehler ohne Feldbezug (R16-Hinweis 3a: „Fehler beim Feld"). `kein_feld`
      // hat kein einzelnes Feld; es zeigt auf das erste Adressfeld.
      const feld = erstesFehlerfeld(neueFehler)
        || (neueFehler.kein_feld ? 'company' : null)
      setFehler(feld ? null : Object.values(neueFehler)[0])
      if (feld) fokussiere(feld)
      return
    }
    sendetRef.current = true
    setSendet(true)
    setFehler(null)
    const form = new FormData()
    for (const [feld, wert] of gesetzt) form.append(feld, wert)
    form.append('grund', grund.trim())
    form.append('quelle', quelle.trim())
    form.append('dokument', datei)
    // 🔴 Der Content-Type MUSS hier stehen: `api` trägt global
    // `Content-Type: application/json`; ohne diese Zeile serialisiert axios die
    // `FormData` als JSON und der Server findet `request.files` leer (dieselbe
    // Falle wie `NachweisMaske`, 2026-10-19).
    api.post(`/api/invoicing/admin/invoices/${invoiceId}/anschrift`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(({ data }) => {
        // Die Sperre bleibt stehen (die Maske blendet aus); der Aufrufer
        // schließt und meldet.
        onErgebnis(data)
      })
      .catch((err) => {
        sendetRef.current = false
        setSendet(false)
        const status = err?.response?.status
        const daten = err?.response?.data
        const code = daten?.fehler
        // 🔴 Unbekannter Ausgang heißt unbekannt: ohne Antwort — und bei einem
        // 5xx OHNE bekannten Vertragscode — kann der Server geschrieben haben.
        // Ehrlicher Satz, neu laden, keine Behauptung (R16-Bed. 1).
        if (ausgangUnbekannt(status, code, ANSCHRIFT_FEHLER_TEXT)) {
          setFehler('Outcome unknown — the change may or may not have been saved. The page is being reloaded.')
          onNeuLaden?.()
          return
        }
        const satz = ANSCHRIFT_FEHLER_TEXT[code] || ANSCHRIFT_UNBEKANNT(code || `HTTP ${status}`)
        // 🔴 Ein Fehler mit Feldbezug steht AM FELD — und NICHT zusätzlich unten
        // (einmal, nicht doppelt). Der Fokus springt dorthin.
        const feld = FELD_JE_CODE[code]
        if (feld) {
          setFeldFehler((alt) => ({ ...alt, [feld]: satz }))
          setFehler(null)
          fokussiere(feld)
        } else {
          setFehler(satz)
        }
        // Ein bewegter Stand: die Seite lädt neu, damit die Knöpfe verschwinden.
        if (status === 409 && (code === 'nicht_intent' || code === 'nicht_gefunden')) onNeuLaden?.()
      })
  }

  // 🔴 R16-Hinweis 3b/3: bei 390 liegen `Reason`, `Source` und die Datei unter der
  // Falz. Der Sende-Knopf ist deshalb gesperrt, solange Pflichtteile fehlen —
  // und der GRUND steht sichtbar in der Aktionszeile (die liegt ausserhalb des
  // scrollenden Inhalts, also ohne Scrollen lesbar).
  const fehlendePflicht = []
  if (!grund.trim()) fehlendePflicht.push('Reason')
  if (!quelle.trim()) fehlendePflicht.push('Source')
  if (!datei) fehlendePflicht.push('the customer’s message')
  const aufzaehlung = (teile) => (teile.length <= 1
    ? (teile[0] || '')
    : `${teile.slice(0, -1).join(', ')} and ${teile[teile.length - 1]}`)
  const pflichtSatz = fehlendePflicht.length
    ? `Required: ${aufzaehlung(fehlendePflicht)}.`
    : null

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      aria-describedby={satzId}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { component: 'form', onSubmit: sende, noValidate: true } }}
    >
      <DialogTitle id={titelId}>Change billing address</DialogTitle>
      <DialogContent>
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          <Typography id={satzId} variant="body2" sx={{ color: 'text.secondary' }}>
            A cleared field keeps the order’s value. Removing a value is a manual case.
            The change is stored in the order timeline together with the customer’s message.
          </Typography>

          {/* F11: der Ladezustand der Vorbelegung. Der Dialog steht dabei schon,
              die Felder sind gesperrt — ein zweiter Klick kann nichts senden. */}
          {laedt && (
            <Box data-testid="anschrift-laedt" aria-busy="true">
              <LinearProgress aria-label="Loading the order address" />
            </Box>
          )}
          {ladefehler && <Alert severity="warning">{ladefehler}</Alert>}
          {!laedt && !ladefehler && herkunft && HERKUNFT_TEXT[herkunft] && (
            <Typography variant="caption" component="p" data-testid="anschrift-herkunft"
              sx={{ color: 'text.secondary' }}>
              {HERKUNFT_TEXT[herkunft]}
            </Typography>
          )}

          {/* `kein_feld` hat kein einzelnes Feld — der Satz steht ueber der
              Feldgruppe, der Fokus geht auf das erste Adressfeld. */}
          {feldFehler.kein_feld && (
            <Typography variant="caption" component="p" sx={{ color: 'error.main' }}>
              {feldFehler.kein_feld}
            </Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: SP.card }}>
            {ANSCHRIFT_FELDER.map(({ feld, label, autoComplete }) => (
              <TextField
                key={feld}
                label={label}
                size="small"
                fullWidth
                autoComplete={autoComplete}
                value={werte[feld]}
                onChange={feldAendern(feld)}
                inputRef={(el) => { feldRefs.current[feld] = el }}
                error={Boolean(feldFehler[feld])}
                helperText={feldFehler[feld]
                  || (vorbelegung[feld] && !werte[feld] ? 'Cleared — the order’s value stays.' : undefined)}
                disabled={sendet || laedt}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
            ))}
          </Box>

          <Box>
            <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
              Country, VAT ID and email cannot be changed here — a change of country or VAT ID is a manual case.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: SP.card, mt: SP.tight }}>
              <FesterWert label="Country" wert={land} testId="anschrift-land" />
              <FesterWert label="VAT ID" wert={ustid} testId="anschrift-ustid" />
              <FesterWert label="Email" wert={email} testId="anschrift-email" />
            </Box>
            {registrierung && (
              <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: SP.tight }}>
                {`Registration on file: ${[registrierung.company_name, registrierung.street,
                  [registrierung.postal_code, registrierung.city].filter(Boolean).join(' ')]
                  .filter(Boolean).join(', ') || STRICH}`}
              </Typography>
            )}
          </Box>

          <TextField
            label="Reason"
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            value={grund}
            inputRef={(el) => { feldRefs.current.grund = el }}
            onChange={(e) => { setGrund(e.target.value); setFeldFehler((alt) => ({ ...alt, grund: null })) }}
            error={Boolean(feldFehler.grund)}
            helperText={feldFehler.grund || 'Internal note for the order timeline — not sent to the customer.'}
            disabled={sendet || laedt}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <TextField
            label="Source"
            size="small"
            fullWidth
            required
            value={quelle}
            inputRef={(el) => { feldRefs.current.quelle = el }}
            onChange={(e) => { setQuelle(e.target.value); setFeldFehler((alt) => ({ ...alt, quelle: null })) }}
            error={Boolean(feldFehler.quelle)}
            helperText={feldFehler.quelle || 'Where the change comes from — e.g. Customer email of 28.10.2026.'}
            disabled={sendet || laedt}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />

          <Box>
            <input
              ref={dateiRef}
              hidden
              type="file"
              accept=".eml,.pdf,.png,.jpg,.jpeg"
              aria-label="Customer message file"
              onChange={(e) => waehleDatei(e.target.files?.[0] || null)}
            />
            <FileDropzone
              onFiles={(dateien) => waehleDatei(dateien?.[0] || null)}
              noClick
              noKeyboard
              disabled={sendet}
              data-testid="anschrift-drop"
              sx={ohneZiehen ? { p: 0, borderStyle: 'none' } : undefined}
            >
              {({ isDragActive }) => (
                <>
                  <Button
                    ref={dateiKnopfRef}
                    variant="outlined"
                    disabled={sendet || laedt}
                    onClick={() => dateiRef.current?.click()}
                    aria-describedby={dateiHinweisId}
                    aria-invalid={feldFehler.dokument ? true : undefined}
                  >
                    {datei ? 'Choose another file' : 'Choose file'}
                  </Button>
                  {ohneZiehen ? null : (
                    <Typography variant="caption" component="p" sx={{ mt: SP.tight, color: 'text.secondary' }}>
                      {isDragActive ? 'Drop it here' : 'or drop the file here'}
                    </Typography>
                  )}
                </>
              )}
            </FileDropzone>
            <Box id={dateiHinweisId} role="status" sx={{ mt: SP.tight }}>
              {feldFehler.dokument && (
                <Typography variant="caption" component="p" sx={{ color: 'error.main' }}>
                  {feldFehler.dokument}
                </Typography>
              )}
              <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
                {datei
                  ? `${datei.name} · ${formatiereGroesse(datei.size)}`
                  : `The customer’s message — .eml, .pdf, .png or .jpg, up to ${ANSCHRIFT_MAX_BYTES / (1024 * 1024)} MB.`}
              </Typography>
            </Box>
          </Box>

          {fehler && <Alert severity="error">{fehler}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* Der Grund steht VOR den Knoepfen und bleibt sichtbar, auch wenn der
            Inhalt scrollt. `aria-describedby` am gesperrten Knopf nennt ihn. */}
        {pflichtSatz && (
          <Typography id={pflichtId} variant="caption" component="p"
            data-testid="anschrift-pflicht"
            sx={{ color: 'text.secondary', mr: 'auto', alignSelf: 'center' }}>
            {pflichtSatz}
          </Typography>
        )}
        <Button onClick={onSchliessen} disabled={sendet}>Cancel</Button>
        <Button
          ref={knopfRef}
          type="submit"
          variant="contained"
          loading={sendet}
          loadingPosition="start"
          aria-busy={sendet || undefined}
          disabled={sendet || laedt || Boolean(dateiSatz) || fehlendePflicht.length > 0}
          aria-describedby={dateiSatz ? dateiHinweisId : (pflichtSatz ? pflichtId : undefined)}
        >
          {sendet ? 'Saving …' : 'Save address'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * F11 — „Issue now": stößt die Ausstellung eines Entwurfs an (Plan B1a).
 *
 * 🔴 **Eine Rückfrage, weil die Handlung nicht zurücknehmbar ist.** Die
 * Ausstellung vergibt eine Rechnungsnummer und startet die Rechnungsmail; ein
 * Fehlklick wäre nicht rückgängig zu machen. Der Dialog nennt beides, bevor er
 * sendet — die Drossel des Servers (5 min) ist ausdrücklich **kein** Riegel
 * gegen einen zweiten Menschen, der Schutz ist der Status-Riegel.
 *
 * 🔴 **`uebersprungen` ist sichtbar** (Grill-W5): der Server antwortet 409 mit
 * `grund`, und der Grund wird als Satz gezeigt — ein still wirkungsloser Knopf
 * wäre schlimmer als keiner.
 */
function AusstellenMaske({ offen, invoiceId, onSchliessen, onErgebnis, onNeuLaden }) {
  const titelId = useId()
  const satzId = useId()
  const knopfRef = useRef(null)
  const sendetRef = useRef(false)
  const [sendet, setSendet] = useState(false)
  const [fehler, setFehler] = useState(null)
  // 🔴 R16-W2: der Ton kommt aus dem ZUSTAND, nicht aus der Stelle. Ein
  // „übersprungen" ist kein Fehler des Bedieners — dieselbe Lage meldet die
  // Seite sonst als `warning` (`KorrekturDialog`, `AusfuhrKorrekturDialog`).
  const [ton, setTon] = useState('error')
  useFokusNachLaden(knopfRef, sendet)

  useEffect(() => {
    if (!offen) return
    sendetRef.current = false
    setSendet(false)
    setFehler(null)
    setTon('error')
  }, [offen])

  const sende = (e) => {
    e.preventDefault()
    if (sendetRef.current) return
    sendetRef.current = true
    setSendet(true)
    setFehler(null)
    // 🔴 R15-N4: der Ton gehoert zum VERSUCH, nicht zum Dialog. Ohne diese
    // Zeile erschiene eine echte Ablehnung nach einer vorangegangenen Warnung
    // (`uebersprungen`) weiter gelb.
    setTon('error')
    api.post(`/api/invoicing/admin/invoices/${invoiceId}/neu-ausstellen`)
      .then(({ data }) => {
        const gruende = (data?.gruende || []).map(iglGrundText).join(' ')
        let text = 'The invoice was issued.'
        let ton = 'success'
        if (data?.zustand === 'ausgestellt') {
          text = `Issued${data?.invoice_number ? `: ${data.invoice_number}` : ''}. The invoice email follows.`
        } else if (data?.zustand === 'gesperrt') {
          ton = 'warning'
          text = `Issuing stays blocked${gruende ? `: ${gruende}` : '.'}`
        } else if (data?.zustand === 'schon_erledigt') {
          ton = 'info'
          text = 'The invoice was already issued — no second document.'
        }
        onErgebnis({ art: 'issue-now', ton, text })
      })
      .catch((err) => {
        sendetRef.current = false
        setSendet(false)
        const status = err?.response?.status
        const daten = err?.response?.data
        const code = daten?.fehler
        // 🔴 R16-Bed. 1: auch ein 5xx ohne bekannten Code ist ein unbekannter
        // Ausgang — die Ausstellung kann gelaufen sein.
        if (ausgangUnbekannt(status, code, AUSSTELLEN_FEHLER_TEXT)) {
          setFehler('Outcome unknown — the invoice may or may not have been issued. The page is being reloaded.')
          onNeuLaden?.()
          return
        }
        if (code === 'uebersprungen') {
          const grund = String(daten?.grund || '')
          const satz = AUSSTELLEN_UEBERSPRUNGEN_TEXT[grund]
            || `Issuing was skipped (${grund || 'no reason given'}) — nothing was issued.`
          // 🔴 R16-W2: ein `modus:*`-Grund ist eine Einstellung, kein
          // Fehlschlag — die Seite sperrt den Knopf für diese Ansicht und
          // nennt den Grund sichtbar. Alles andere bleibt im Dialog stehen.
          if (grund.startsWith('modus:')) {
            onErgebnis({ art: 'issue-now', ton: 'warning', text: satz, sperrtAusstellen: true })
            return
          }
          setTon('warning')
          setFehler(satz)
          return
        }
        setFehler(AUSSTELLEN_FEHLER_TEXT[code] || `Issuing was rejected (${code || `HTTP ${status}`}). Nothing was issued.`)
      })
  }

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      aria-describedby={satzId}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { component: 'form', onSubmit: sende, noValidate: true } }}
    >
      <DialogTitle id={titelId}>Issue invoice now?</DialogTitle>
      <DialogContent>
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          <Typography id={satzId} variant="body2" sx={{ color: 'text.secondary' }}>
            The draft is issued now: it receives an invoice number and the invoice email is started.
            This cannot be undone — a later change is a correction document.
          </Typography>
          {fehler && (
            <Alert severity={ton} data-testid="anschrift-sammelfehler">{fehler}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={sendet}>Cancel</Button>
        <Button
          ref={knopfRef}
          type="submit"
          variant="contained"
          loading={sendet}
          loadingPosition="start"
          aria-busy={sendet || undefined}
          disabled={sendet}
        >
          {sendet ? 'Issuing …' : 'Issue now'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function DetailSeite({ id }) {
  const theme = useTheme()
  const schmal = useMediaQuery(theme.breakpoints.down('sm'))
  const { daten, laedt, fehler, fehlt, fertig, neuLaden } = useDetail(id)
  const [maskeOffen, setMaskeOffen] = useState(false)
  // Der zweite Weg („erneut senden") — eigener Dialog, eigene Bedeutung.
  const [erneutOffen, setErneutOffen] = useState(false)
  // #7 (S6.1g): die IgL-Handfreigabe — eigener Dialog, eigene Herkunft.
  const [iglOffen, setIglOffen] = useState(false)
  // F11: die zwei Handlungen einer `intent`-Rechnung — Anschrift und Ausstellen.
  const [anschriftOffen, setAnschriftOffen] = useState(false)
  const [ausstellenOffen, setAusstellenOffen] = useState(false)
  // 🔴 R16-W2: nach einem `modus:*`-Sprung ist der Knopf für DIESE Ansicht
  // gesperrt, mit sichtbarem Grund — sonst wiederholt der Bediener denselben
  // wirkungslosen Klick beliebig oft.
  const [ausstellenSperre, setAusstellenSperre] = useState(null)
  // Gate P1: das Ergebnis des Neuversands lebt HIER, nicht in der Maske — es
  // überlebt das Neuladen, den Statuswechsel (die Maske fällt dann weg) und ein
  // gescheitertes Laden (dann ist `daten` null). `abRunde`: der Ladeauftrag,
  // nach dem „sind neu geladen" stimmt.
  const [ergebnis, setErgebnis] = useState(null)

  const kopf = daten?.kopf
  const kopfId = kopf?.id
  const titel = kopf ? `${ART_LABEL[kopf.art] || 'Document'} ${kopf.nummer || 'without number'}` : 'Customer invoice'
  const unzustellbar = kopf?.status === 'undeliverable' && kopf?.art === 'invoice'
  const modus = daten?.neuversand_modus
  // Gate P7: angeboten nur bei ausdrücklich `live` — fehlt das Feld, gilt die
  // Fassung, die nichts bewegt.
  const neuversandMoeglich = unzustellbar && modus === 'live'
  // „Rechnung erneut senden" (Owner-Auftrag 2026-10-16): der Normalfall — eine
  // **zugestellte** Rechnung noch einmal. Dieselbe konservative Fassung wie oben:
  // nur bei ausdrücklich `live`, und nur für die Rechnung selbst (eine Korrektur
  // hat diesen Weg nicht).
  // 🔴 Grill B1: `paid` gehört dazu — eine bezahlte Rechnung ist der häufigste
  // Anlass, und `lagen.py` zählt sie als **zugestellt**. Dieselbe Menge wie
  // `mailing.ERNEUT_SENDBARE_STATI`; die Probe
  // „„Erneut senden" gibt es NUR bei einer zugestellten Rechnung" ist die
  // Kalibrierung dazu (sie muss für `issued` weiterhin NICHTS anbieten).
  const erneutMoeglich = kopf?.art === 'invoice'
    && ERNEUT_SENDBARE_STATI.includes(kopf?.status)
    && modus === 'live'

  // Fällt die Maske weg, darf sie beim nächsten Angebot nicht von selbst aufgehen.
  useEffect(() => {
    if (!neuversandMoeglich) setMaskeOffen(false)
  }, [neuversandMoeglich])

  // Fällt der Anlass weg (Statuswechsel nach dem Versand), darf die Maske beim
  // nächsten Mal nicht von selbst aufgehen.
  useEffect(() => {
    if (!erneutMoeglich) setErneutOffen(false)
  }, [erneutMoeglich])

  // 🔴 F11: beide Knöpfe nur für einen **Entwurf einer Rechnung** mit
  // Shopify-Bestellung — dieselben drei Schranken wie die Routen (`status`,
  // `document_type`, `channel`). Ein Knopf, der nur 409 zurückbringt, wäre ein
  // toter Knopf; die Kanalfrage (Grill-W6) entscheidet die Seite, nicht erst der
  // Server. `kanal` fehlt nie im Detail — die konservative Fassung bleibt trotzdem.
  const intentHandelbar = kopf?.status === 'intent' && kopf?.art === 'invoice'
    && kopf?.kanal === 'shopify'
  useEffect(() => {
    if (!intentHandelbar) setAnschriftOffen(false)
    if (!intentHandelbar) setAusstellenOffen(false)
  }, [intentHandelbar])

  const meldeErgebnis = useCallback((neu) => {
    setMaskeOffen(false)
    setErgebnis({ ...neu, abRunde: neuLaden() })
  }, [neuLaden])

  // S5.6a: der Ausgang der Handkorrektur — dieselbe Meldungsfläche wie der
  // Neuversand, damit der Verlauf danach neu geladen wird.
  const meldeKorrektur = useCallback((text, ausgang) => {
    setErgebnis({
      art: 'korrektur',
      ton: ausgang?.zustand === 'ausgestellt' ? 'success' : 'warning',
      text,
      abRunde: neuLaden(),
    })
  }, [neuLaden])

  // #7: eine gesperrte IgL-Lage — **das Backend entscheidet**, ob sie freigabefähig
  // ist (`igl_freigabe_moeglich`, dieselbe Prüfung wie Schritt 4 der Route, R15-A W1);
  // das Frontend führt keine zweite Gründeliste. Die Freigabe braucht die `id` des
  // Sperr-Ereignisses und die Prüfzeilen-ID aus dessen Nutzlast.
  // 🔴 B1 (Grill-Blocker): `letztes`/IgL lesen `verlauf` — das jüngste Ereignis des
  // EIGENEN Belegs —, NICHT `vorgang` (bestellweit). Läse die Seite hier `vorgang`,
  // könnte ein fremdes `sperre`/`igl_handfreigabe` den IgL-Knopf mit fremder
  // `pruef_id` speisen (Fehlbuchung über die Handlung). Gepinnt in
  // `__tests__/InvoiceDetail.test.jsx` und `test_admin_detail.py`.
  const verlauf = daten?.verlauf || []
  // 🔴 F11: die letzte **zustandstragende** Zeile, nicht `verlauf[-1]`. Seit dem
  // 04.11. schreiben die Anschriften-Überschreibung, das Dokument und der Druck
  // „Issue now" Ereignisse ohne Zustand; mit `[-1]` wäre der IgL-Knopf nach
  // einer Überschreibung weg, obwohl die Sperre steht. Dieselbe Menge wie im
  // Backend (`anschrift.EREIGNIS_TYPEN_OHNE_ZUSTAND`, `admin_detail`).
  const letztes = [...verlauf].reverse().find((e) => !NICHT_ZUSTANDS_TYPEN.includes(e?.typ)) || null
  const iglSperre = (daten?.igl_freigabe_moeglich === true && letztes?.typ === 'sperre')
    ? { id: letztes.id, pruef_id: letztes.nutzlast?.pruef_id, gruende: letztes.nutzlast?.gruende || [] }
    : null
  // #7 (R15-A B2): nach `nicht_versucht`/`fehler` ist das jüngste Ereignis das
  // `igl_handfreigabe` — die Freigabe steht, die Ausstellung ist offen. Der Knopf
  // geht denselben Wiederholungsweg der Route (`admin_igl.py`, Schritt 3).
  const kopfStatus = kopf?.status
  const iglWiederholung = useMemo(() => (
    (kopfStatus === 'intent' && letztes?.typ === 'igl_handfreigabe'
      && letztes.nutzlast?.sperre_ereignis_id != null && letztes.nutzlast?.pruef_id != null)
      ? { sperre_ereignis_id: letztes.nutzlast.sperre_ereignis_id, pruef_id: letztes.nutzlast.pruef_id }
      : null
  ), [kopfStatus, letztes])
  const meldeIgL = useCallback((neu) => {
    setIglOffen(false)
    setErgebnis({ ...neu, abRunde: neuLaden() })
  }, [neuLaden])

  // F11: die Überschreibung ist gespeichert — der Verlauf und die Knöpfe laden
  // neu (die Drossel des Servers kann den nächsten Klick ohnehin ablehnen).
  const meldeAnschrift = useCallback((daten) => {
    setAnschriftOffen(false)
    setErgebnis({
      art: 'anschrift',
      ton: 'success',
      text: `Billing address saved${daten?.dokument_dateiname ? ` together with ${daten.dokument_dateiname}` : ''}. `
        + 'The next issue uses it.',
      abRunde: neuLaden(),
    })
  }, [neuLaden])
  const meldeAusstellen = useCallback((neu) => {
    setAusstellenOffen(false)
    // 🔴 R16-neu-3a: ein `modus:*`-Sprung meldet NUR den Dauer-Alert. Zwei
    // Alerts mit demselben Satz nebeneinander liest niemand zweimal.
    if (neu?.sperrtAusstellen) {
      setAusstellenSperre(neu.text || true)
      setErgebnis(null)
      neuLaden()
      return
    }
    setErgebnis({ ...neu, abRunde: neuLaden() })
  }, [neuLaden])

  // BA7: der Ausgang des Nachweis-Uploads — dieselbe Meldungsflaeche wie die
  // anderen Wege, damit Status und Liste danach neu geladen werden.
  const meldeAusfuhr = useCallback((daten) => {
    setErgebnis({
      art: 'ausfuhr',
      ton: 'success',
      text: daten?.neu === false
        ? 'The proof was already on file — nothing was added.'
        : 'The export proof has been received.',
      abRunde: neuLaden(),
    })
  }, [neuLaden])
  // BA12: der Handversand der Hinweismail ist gelungen (200). Ein Versand an
  // eine andere als die vorbelegte Adresse ist ein Probeversand (E8) und sagt
  // das; ohne bekannte Kundenadresse nennt der Satz die eingegebene.
  const meldeHinweis = useCallback(({ an, probe, pflicht }) => {
    let text = 'The export instructions have been emailed to the customer.'
    if (probe) {
      text = `A test copy of the export instructions has been emailed to ${an} — this does not count as informing the customer.`
    } else if (pflicht) {
      // R16 C1: ohne Kundenadresse zaehlt der Versand nicht als informiert (E8).
      text = `The export instructions have been emailed to ${an}. With no customer address on file, this is not counted as informing the customer.`
    }
    setErgebnis({ art: 'ausfuhr', ton: 'success', text, abRunde: neuLaden() })
  }, [neuLaden])
  // BA3b: der Ausgang einer Entscheidung — dieselbe Meldungsflaeche, damit
  // Kopf („Approved"/„Rejected") und Zeitleiste danach neu geladen werden. Der
  // Satz haengt an der Art, die der SERVER zurueckmeldet, nicht an der Maske.
  const meldeEntscheidung = useCallback((daten) => {
    setErgebnis({
      art: 'ausfuhr',
      ton: 'success',
      text: ENTSCHEIDUNG_TEXT[daten?.art]?.ergebnis || 'The decision has been saved.',
      abRunde: neuLaden(),
    })
  }, [neuLaden])
  // R16b W-a: solange der Versuch läuft, ist der Knopf gesperrt. Die Ref sperrt
  // synchron (zwei Klicks im selben Takt sähen sonst beide `false`), der Zustand
  // zeigt es an.
  const wiederholtRef = useRef(false)
  const [wiederholt, setWiederholt] = useState(false)
  const wiederholeIgL = useCallback(() => {
    if (!iglWiederholung || wiederholtRef.current) return
    wiederholtRef.current = true
    setWiederholt(true)
    setErgebnis(null)
    api.post(`/api/invoicing/admin/invoices/${kopfId}/igl-freigabe`, {
      sperre_ereignis_id: iglWiederholung.sperre_ereignis_id,
      pruef_id: iglWiederholung.pruef_id,
      begruendung: '',
    })
      .then(({ status, data }) => deuteIgL(status, data))
      .catch((err) => deuteIgL(err?.response?.status, err?.response?.data))
      .then((neu) => {
        wiederholtRef.current = false
        setWiederholt(false)
        if (neu.seite) { meldeIgL(neu.seite); return }
        setErgebnis({ art: 'igl', ton: 'error', text: neu.fehler || neu.feld, abRunde: neuLaden() })
      })
  }, [iglWiederholung, kopfId, meldeIgL, neuLaden])

  let ladenNachVersand = 'laeuft'
  if (ergebnis && fertig.runde >= ergebnis.abRunde) ladenNachVersand = fertig.ok ? 'neu' : 'gescheitert'

  return (
    <PageContainer>
      <ViewHeader
        breadcrumbs={[BROTKRUME, { label: kopf ? dokName(kopf.nummer, kopf.id) : 'Detail' }]}
        backLink={BROTKRUME}
        title={titel}
        // 🔴 `variant: 'outlined'` steht ausdrücklich da: der Kopf rendert eine
        // EINZELNE Aktion sonst `contained` (E2). „Erneut senden" ist nicht DIE
        // Handlung dieser Seite — sie ist lesen.
        actions={intentHandelbar ? [{
          // F11: die Anschrift ist der Anlass des Owners (SHOP-14425) und
          // darum die gefüllte Aktion (E2) — am Telefon bleibt genau sie inline
          // (die übrigen wandern in den ⋯). „Issue now" steht daneben, weil die
          // Handlung nicht zurücknehmbar ist und nicht der erste Klick sein soll.
          key: 'anschrift',
          label: 'Change billing address …',
          variant: 'contained',
          onClick: () => { setErgebnis(null); setAnschriftOffen(true) },
        }, {
          key: 'issue-now',
          label: 'Issue now',
          variant: 'outlined',
          disabled: Boolean(ausstellenSperre),
          onClick: () => { setErgebnis(null); setAusstellenOffen(true) },
        }] : (erneutMoeglich ? [{
          key: 'erneut-senden',
          // 🔴 `label`, nicht `children`: `ViewHeader.renderActionButton`
          // rendert `{label}` — ein `children` erreichte das DOM nie.
          label: 'Send invoice again …',
          variant: 'outlined',
          onClick: () => { setErgebnis(null); setErneutOffen(true) },
        }] : [])}
      />

      {/* 🔴 W13 (2026-10-07): der Refresh stand als `actions`-Eintrag im Kopf.
          Dort rendert die EINZIGE Aktion ohne `variant` `contained` (E2) — ein
          Refresh ist nie primaer. Er steht jetzt dort, wo die Daten stehen.
          `firstLoad`: im Aufbau rendert `<Laden/>` Skelette; der Knopf dreht
          sich dann nicht mit (Anti-Pattern 11). `disabled={fehlt}` haelt den
          alten Riegel: ohne gueltigen Dokumentlink gibt es nichts zu laden. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: SP.chip }}>
        <RefreshButton
          ariaLabel="Refresh invoice detail"
          loading={laedt}
          firstLoad={laedt && !daten && !fehler && !fehlt}
          disabled={fehlt}
          onClick={neuLaden}
        />
      </Box>

      <Stack spacing={SP.section}>
        {ergebnis && (
          <Alert
            severity={ergebnis.ton}
            onClose={() => setErgebnis(null)}
            closeText="Close"
            data-testid={ergebnis.art === 'igl'
              ? 'igl-ergebnis'
              : (ergebnis.art === 'ausfuhr'
                ? 'ausfuhr-ergebnis'
                : (ergebnis.art === 'anschrift'
                  ? 'anschrift-ergebnis'
                  : (ergebnis.art === 'issue-now' ? 'ausstellen-ergebnis' : 'neuversand-ergebnis')))}
          >
            {ergebnisText(ergebnis, ladenNachVersand)}
          </Alert>
        )}

        {/* 🔴 R16-W2: der sichtbare Grund zur gesperrten Aktion. Er bleibt
            stehen, auch wenn die Ergebnis-Meldung weggeklickt wird — ein
            gesperrter Knopf ohne Grund waere die schlechtere Hälfte. */}
        {intentHandelbar && ausstellenSperre && (
          <Alert severity="warning" data-testid="ausstellen-gesperrt">
            <AlertTitle>Issue now is not available</AlertTitle>
            {typeof ausstellenSperre === 'string'
              ? ausstellenSperre
              : 'Issuing is switched off for this environment — nothing was issued.'}
          </Alert>
        )}

        {fehlt && (
          <Alert severity="info">
            <AlertTitle>Invoice not found</AlertTitle>
            There is no customer invoice at this address.
            <Box sx={{ mt: SP.tight }}>
              <Button component={RouterLink} to="/invoicing" size="small" variant="outlined">Back to the list</Button>
            </Box>
          </Alert>
        )}

        {fehler && (
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={neuLaden}>Try again</Button>}
          >
            <AlertTitle>The invoice could not be loaded.</AlertTitle>
            <Box component="span">{fehler}</Box>
          </Alert>
        )}

        {laedt && !daten && !fehler && !fehlt && <Laden />}

        {daten && (
          <>
            {laedt && <LinearProgress aria-label="Updating" />}

            {/* Der Statusstreifen des Musters: nur, wenn der Zustand eine Handlung verlangt.
                Die Handlung selbst nur bei `neuversand_modus = live` (Gate P7). */}
            {unzustellbar && (
              <Alert
                severity="warning"
                action={neuversandMoeglich ? (
                  <Button color="inherit" size="small" onClick={() => { setErgebnis(null); setMaskeOffen(true) }}>
                    Send again …
                  </Button>
                ) : undefined}
              >
                <AlertTitle>Undeliverable</AlertTitle>
                {unzustellbarText(modus)}
              </Alert>
            )}

            {/* #7: eine gesperrte IgL-Lage wartet auf eine Handfreigabe. */}
            {iglSperre && (
              <Alert
                severity="warning"
                action={(
                  <Button color="inherit" size="small" onClick={() => { setErgebnis(null); setIglOffen(true) }}>
                    Release IgL …
                  </Button>
                )}
              >
                <AlertTitle>Intra-Community supply — blocked</AlertTitle>
                The invoice is waiting for a manual release.
                {iglSperre.gruende.length
                  ? <> Block reasons: {iglSperre.gruende.map(iglGrundText).join(' ')}</>
                  : null}
              </Alert>
            )}

            {/* #7 (R15-A B2): die Freigabe steht, die Ausstellung ist offen. */}
            {iglWiederholung && (
              <Alert
                severity="warning"
                action={(
                  <Button
                    color="inherit"
                    size="small"
                    onClick={wiederholeIgL}
                    disabled={wiederholt}
                    aria-busy={wiederholt}
                    startIcon={wiederholt ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : null}
                  >
                    Retry issuance
                  </Button>
                )}
              >
                <AlertTitle>Released — issuance is still pending</AlertTitle>
                The release is saved. Issuance was not completed (Shopify was not
                readable or failed) — please try again.
              </Alert>
            )}

            {kopf.testbeleg && (
              <Alert severity="info" data-testid="testbeleg-hinweis">
                <AlertTitle>Test invoice</AlertTitle>
                Created before the productive cut-off. It is hidden from the invoice overview by
                default and stays fully usable here.
              </Alert>
            )}

            <Kopf kopf={kopf} mail={daten.mail} zahlung={daten.zahlung} />

            <Box sx={{ display: 'flex', gap: SP.md, flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'flex-start' } }}>
              <Stack spacing={SP.section} sx={{ flex: '1 1 0', minWidth: 0 }}>
                <DokumentBereich key={`dok-${kopf.id}`} kopf={kopf} dokument={daten.dokument} schmal={schmal} />
                <HauptbuchBereich
                  hauptbuch={daten.hauptbuch}
                  zahlung={daten.zahlung}
                  waehrung={kopf.waehrung}
                  schmal={schmal}
                />
              </Stack>
              <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
                <BezuegeBereich daten={daten} onKorrekturFertig={meldeKorrektur} />
              </Box>
            </Box>

            <VorgangBereich vorgang={daten.vorgang} kopfId={kopf.id} />

            {/* BA7: nur bei einem Kandidaten — die Bedingung kommt vom Server. */}
            {daten.ausfuhr?.art && (
              <AusfuhrBereich
                invoiceId={kopf.id}
                ausfuhr={daten.ausfuhr}
                bestellung={kopf.bestellung}
                onErfolg={meldeAusfuhr}
                onNeuLaden={neuLaden}
                onAusgangUnbekannt={() => setErgebnis(null)}
                onEntschieden={meldeEntscheidung}
                onHinweis={meldeHinweis}
              />
            )}

            {neuversandMoeglich && (
              <NeuversandMaske
                offen={maskeOffen}
                invoiceId={kopf.id}
                mail={daten.mail}
                onSchliessen={() => setMaskeOffen(false)}
                onErgebnis={meldeErgebnis}
              />
            )}

            {erneutMoeglich && (
              <ErneutSendenMaske
                offen={erneutOffen}
                invoiceId={kopf.id}
                mail={daten.mail}
                onSchliessen={() => setErneutOffen(false)}
                onErgebnis={meldeErgebnis}
              />
            )}

            {iglSperre && (
              <IgLFreigabeMaske
                offen={iglOffen}
                invoiceId={kopf.id}
                sperre={iglSperre}
                onSchliessen={() => setIglOffen(false)}
                onErgebnis={meldeIgL}
              />
            )}

            {intentHandelbar && (
              <AnschriftMaske
                offen={anschriftOffen}
                invoiceId={kopf.id}
                verlauf={verlauf}
                snapshot={daten.dokument?.snapshot}
                mail={daten.mail}
                onSchliessen={() => setAnschriftOffen(false)}
                onErgebnis={meldeAnschrift}
                onNeuLaden={neuLaden}
              />
            )}

            {intentHandelbar && (
              <AusstellenMaske
                offen={ausstellenOffen}
                invoiceId={kopf.id}
                onSchliessen={() => setAusstellenOffen(false)}
                onErgebnis={meldeAusstellen}
                onNeuLaden={neuLaden}
              />
            )}
          </>
        )}
      </Stack>
    </PageContainer>
  )
}

/**
 * Die Route `/invoicing/:id`. Je ID ein eigener Baum (`key`): wer von einer
 * Korrektur auf ihr Original springt, sieht keinen Zustand der vorigen Seite
 * (Vorschau-Link, Meldungen, offene Maske).
 */
export default function InvoiceDetail() {
  const { id } = useParams()
  return <DetailSeite key={id} id={id} />
}
